import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  private currentLanguage = 'en';
  private translations: any = {};
  private translationsLoaded = false;

  // Translation data for login and registration pages - fallback if JSON fails
  private fallbackTranslations: any = {
    en: {
      // Common
      'common.instructions': 'Instructions',
      'common.contactUs': 'Contact Us',
      'common.indianApplications': 'Indian Applications',
      'common.email': 'Email',
      'common.phone': 'Phone',
      'common.mobile': 'Mobile',

      // Instructions
      'instructions.registration.title': 'Registration Instructions',
      'instructions.registration.text': 'Ensure your email and mobile number are valid for account recovery.',
      'instructions.registration.clickHere': 'Click here',
      'instructions.registration.createAccount': 'to create your account.',
      'instructions.registration.passwordInfo': 'Choose a secure password (minimum 8 characters, including uppercase, lowercase, and special characters).',
      'instructions.registration.mandatoryFields': 'All fields are mandatory to complete registration.',
      'instructions.login.title': 'Login Instructions',
      'instructions.login.text': 'Enter your email address or mobile number to receive an OTP (One Time Password).',
      'instructions.login.otpText': 'After recieving OTP, Enter the OTP to log in.',
      'instructions.login.warning': 'Do not share your OTP with anyone.',

      // Login Page
      'login.title': 'Login',
      'login.welcome': 'Welcome to Gurudev Siddha Peeth Applications Portal',

      // Registration Page
      'registration.title': 'Registration',
      'registration.welcome': 'Welcome to Gurudev Siddha Peeth Applications Portal',

      // Form Fields
      'form.loginMethod': 'Login Method',
      'form.email': 'Email',
      'form.mobile': 'Mobile Number',
      'form.emailAddress': 'Email Address',
      'form.emailPlaceholder': 'Enter your email address',
      'form.mobileNumber': 'Mobile Number',
      'form.mobilePlaceholder': 'Enter your mobile number',
      'form.otp': 'OTP',
      'form.sequenceNo': 'Sequence No',
      'form.otpPlaceholder': 'Enter OTP',
      'form.resendOtp': 'Resend OTP',
      'form.firstName': 'First Name',
      'form.middleName': 'Middle Name',
      'form.lastName': 'Last Name',
      'form.firstNamePlaceholder': 'Enter your first name',
      'form.middleNamePlaceholder': 'Enter your middle name',
      'form.lastNamePlaceholder': 'Enter your last name',
      'form.dateOfBirth': 'Date of Birth',
      'form.dateOfBirthPlaceholder': 'dd/mm/yyyy',
      'form.gender': 'Gender',
      'form.selectGender': 'Select Gender',
      'form.male': 'Male',
      'form.female': 'Female',
      'form.other': 'Other',
      'form.specifyGender': 'Please specify your gender',
      'form.genderPlaceholder': 'Enter your gender',
      'form.password': 'Password',
      'form.confirmPassword': 'Confirm Password',
      'form.passwordPlaceholder': 'Enter your password',
      'form.confirmPasswordPlaceholder': 'Confirm your password',
      'form.countryCode': 'Country Code',
      'form.selectCountry': 'Select Country',

      // Validation Messages
      'validation.emailRequired': 'Email is required.',
      'validation.emailInvalid': 'Please enter a valid email address.',
      'validation.countryCodeRequired': 'Country code is required.',
      'validation.mobileRequired': 'Mobile Number is required.',
      'validation.mobileInvalid': 'Invalid mobile number format.',
      'validation.mobileMaxLength': 'Mobile Number cannot exceed 10 digits.',
      'validation.mobileMustDiffer': 'Emergency contact number must differ from current address number.',
      'validation.otpRequired': 'OTP is required.',
      'validation.firstNameRequired': 'First Name is required.',
      'validation.firstNameMaxLength': 'First Name cannot exceed 50 characters.',
      'validation.middleNameMaxLength': 'Middle Name cannot exceed 50 characters.',
      'validation.lastNameRequired': 'Last Name is required.',
      'validation.lastNameMaxLength': 'Last Name cannot exceed 50 characters.',
      'validation.dateOfBirthRequired': 'Date of Birth is required.',
      'validation.genderRequired': 'Gender is required.',
      'validation.genderMaxLength': 'Gender cannot exceed 20 characters.',
      'validation.emailMaxLength': 'Email cannot exceed 100 characters.',
      'validation.passwordRequired': 'Password is required.',
      'validation.passwordMinLength': 'Password must be at least 6 characters.',
      'validation.confirmPasswordRequired': 'Confirm Password is required.',
      'validation.passwordsMismatch': 'Passwords do not match.',

      // Buttons
      'button.login': 'Login',
      'button.sendOtp': 'Send OTP',
      'button.createAccount': 'Create a new account',
      'button.register': 'Register',
      'button.registering': 'Registering...',
      'button.backToLogin': 'Back to Login Page',
      'button.submit': 'Submit',
      'button.cancel': 'Cancel',
      'button.back': 'Back',
      'button.next': 'Next',
      'button.previous': 'Previous',
      'button.proceed': 'Proceed',
      'button.saveAsDraft': 'Save as Draft',

      // Success Messages
      'success.registrationComplete': 'Registration completed successfully!',
      'success.otpSent': 'OTP sent successfully!',
      'success.loginSuccess': 'Login successful!',
      'success.informationSaved': 'Information saved successfully',
      'success.documentUploaded': 'Document uploaded successfully',
      'success.documentDeleted': 'Document deleted successfully',
      'success.title': 'Success',

      // Error Messages
      'error.registrationFailed': 'Registration failed. Please try again.',
      'error.loginFailed': 'Login failed. Please check your credentials.',
      'error.otpInvalid': 'Invalid OTP. Please try again.',
      'error.networkError': 'Network error. Please check your connection.',
      'error.uploadFailed': 'Failed to upload document. Please try again.',
      'error.deleteFailed': 'Failed to delete document. Please try again.',
      'error.title': 'Error',

      // Profile Summary Page
      'profile.title': 'Seva Profile',
      'profile.importantInfo': 'Important Information',
      'profile.infoText': 'For applying for seva or retreat in Gurudev Siddha Peeth, completely fill the Seva Profile and then the corresponding Contemplation Form.',
      'profile.mustComplete': 'You must complete your Seva Profile to unlock the Contemplation Forms.',
      'profile.progress': 'Seva Profile Progress',
      'profile.progressInstructions': 'Progress Instructions',
      'profile.progressText': 'Complete your seva profile form to unlock the contemplation forms.',
      'profile.updateProfile': 'Update Your Seva Profile',
      'profile.complete': 'Complete',
      'profile.pending': 'Pending',
      'profile.myApplications': 'My Applications',
      'profile.validTill': 'Valid Till',
      'profile.expiredOn': 'Expired On',
      'profile.contemplationForms':'Application Section',
      'profile.completeProfileToUnlock': 'Completely fill your seva profile to unlock below contemplation forms.',
      'profile.fillContemplationForm': 'Fill and submit the contemplation form for the seva or retreat stay you wish to apply.',
      'profile.desc.visitingSeva': 'For Seva duration less than 6 months',
      'profile.desc.gurukulaSeva': 'Join as a student in the spiritual learning program',
      'profile.desc.kypr': 'Spiritual retreat designed for young adults',
      'profile.desc.sypr': 'Family-oriented spiritual retreat experience',
      'profile.desc.familyRetreat': 'Family-oriented spiritual retreat experience',
      'profile.desc.pth': 'Deep spiritual pilgrimage and retreat',
      'profile.desc.homeSeva': 'Offer seva from home for specific projects',
      'profile.childrenNote': 'Children between 5 and 12 years of age are not required to fill contemplation forms.',
      'profile.needHelp': 'Need Help?',
      'profile.sevaProfile': 'Seva Profile',
      'profile.visitingSeva': 'Visiting Seva',
      'profile.gurukulaSeva': 'Gurukula Seva',
      'profile.homeSeva': 'Home Seva',
      'profile.familyRetreat': 'Family Retreat',
      'profile.pth': 'Pilgrimage to the Heart Retreat',
      'profile.sypr': 'SYPR',
      'profile.kypr': 'KYPR',
      'profile.downloadTemplateInfo': 'Download contemplation form templates to fill and upload',

      // Help Page
      'help.title': 'Help Instructions',
      'help.documentTitle': 'Help Document',
      'help.introText': 'The steps below will help you understand how to navigate the portal and apply for seva or retreat in Gurudev Siddha Peeth. You can also watch the video to fully understand the application process.',
      'help.instructions': 'Instructions',
      'help.instructionsSubtitle': 'Complete your journey for applying for seva or retreat by following the steps below.',
      'help.step1.title': 'Step 1: Fill Your Seva Profile',
      'help.step1.desc1': 'Fill in your details and complete the Seva profile',
      'help.step1.desc2': 'You can save your progress anytime by clicking \'Save as draft\'.',
      'help.step1.desc3': 'The contemplation form for seva and retreats in Gurudev Siddha Peeth will be enabled only after the seva profile is completely filled i.e. the profile progress reaches 100%. Your application will be incomplete without filling the contemplations.',
      'help.step2.title': 'Step 2: Fill and Submit Application',
      'help.step2.desc1': 'After completing your seva profile, fill the corresponding contemplation form.',
      'help.step2.desc2': 'E.g. If you wish to apply for Visiting Seva stay in Gurudev Siddha Peeth, click on the ‘Visiting Seva’ button and fill the contemplation form. ',
      'help.step2.desc3': 'After you have completely filled both seva profile and the corresponding contemplation form, click on “submit” for final submission.',
      'help.downloadTitle': 'Downloadable Help Document',
      'help.downloadText': 'For a comprehensive guide, download the help document:',
      'help.downloadButton': 'Download Help Document',
      'help.videoTitle': 'Video Tutorial',
      'help.videoText': 'For a visual guide, watch the video tutorial below:',

      // Seva Profile Component
      'sevaProfile.formTitle': 'SEVA PROFILE FORM',
      'sevaProfile.applicantLabel': 'Applicant:',
      'sevaProfile.goBackHome': 'Go back to home',

      // Personal Details Page (additional)
      'personalDetails.title': 'Personal Details',

      // Common (additional)
      'common.select': 'Select',
      'common.yes': 'Yes',
      'common.no': 'No',

      // Form Fields (additional)
      'form.age': 'Age',
      'form.maritalStatus': 'Marital Status',
      'form.beforeMarriageFullName': 'Full Name Before Marriage',
      'form.isFirstStayQuestion': 'Will this be your first stay in Gurudev Siddha Peeth?',
      'form.lastStayQuestion': 'If no, when was your last stay? (Specify month and year)',
      'form.middleNameVariants': "Middle Name / Father's Name / Husband's Name",

      // Placeholders (additional)
      'placeholder.firstName': 'Enter First Name',
      'placeholder.middleName': 'Enter Middle Name',
      'placeholder.lastName': 'Enter Last Name',
      'placeholder.dateOfBirth': 'Select Date of Birth',
      'placeholder.ageAuto': 'Age will be calculated automatically',
      'placeholder.selectMaritalStatus': 'Select Marital Status',
      'placeholder.beforeMarriageFullName': 'Full Name Before Marriage',
      'placeholder.selectGender': 'Select Gender',
      'placeholder.enterGender': 'Enter Gender',
      'placeholder.lastStayMonthYear': 'Select Last Stay Month & Year',

      // Options (additional)
      'options.single': 'Single',
      'options.married': 'Married',
      'options.other': 'Other',
      'options.male': 'Male',
      'options.female': 'Female',

      // Validation (additional)
      'validation.requiredField': 'This field is required.',
      'validation.middleNameRequired': 'Middle Name is required.',
      'validation.beforeMarriageFullNameRequired': 'Full Name Before Marriage is required.',
      'validation.otherGenderRequired': 'Other Gender is required.',
      'validation.lastStayDateRequired': 'Month and Year of Last Stay is required.',

      // Health Insurance Page
      'healthInsurance.title': 'Health Insurance',
      'healthInsurance.question.hasInsurance': 'Do you have a medical insurance that will cover your stay in GSP?',
      'healthInsurance.companyName': 'Name of company',
      'healthInsurance.companyNamePlaceholder': 'Enter Company Name',
      'healthInsurance.policyNumber': 'Policy Number',
      'healthInsurance.policyNumberPlaceholder': 'Enter Policy Number',
      'healthInsurance.validUntil': 'Valid until',
      'healthInsurance.validUntilPlaceholder': 'Select Valid Until Date',
      'healthInsurance.question.financialSupport': 'If No, can you support yourself financially in case of any medical emergency?',
      'healthInsurance.declaration': 'Declaration: I hereby certify that the information given above are true and correct.',
      'healthInsurance.declarationRequired': 'Please accept the declaration to proceed.',

      // Hospital & Allergies Page
      'hospital.title': 'Hospital Admissions & Allergies',
      'hospital.question.pastHospitalized': 'Are you currently impacted by any past hospitalization?',
      'hospital.pleaseDescribe': 'Please describe',
      'hospital.enterDetails': 'Enter details',
      'hospital.question.surgeryDone': 'Have you had any surgeries?',
      'hospital.immunizations.title': 'Immunizations: When is the last approximate year that you had the following immunizations?',
      'hospital.immunizations.tetanus': 'Tetanus immunization:',
      'hospital.immunizations.tb': 'TB (BCG):',
      'hospital.immunizations.polio': 'Polio immunization:',
      'hospital.immunizations.other': 'Other:',
      'hospital.pickYear': 'Pick Year',
      'hospital.allergies.title': 'Allergies – Are you allergic (or sensitive) to any of the following?',
      'hospital.allergies.mould': 'IsAllergicToMould:',
      'hospital.allergies.pollen': 'IsAllergicToPollen:',
      'hospital.allergies.beeSting': 'Bee Sting:',
      'hospital.medicineAllergies': "What medicines are you allergic to? (If none, write 'none')",
      'hospital.enterMedicineAllergies': "Enter medicine allergies or 'none'",
      'hospital.foodAllergies': "What foods are you sensitive or allergic to? (If none, write 'none')",
      'hospital.enterFoodAllergies': "Enter food allergies or 'none'",
      'hospital.otherAllergies': 'Any other allergies? Please List:',
      'hospital.enterOtherAllergies': 'Enter other allergies',

      // Language Proficiency Page
      'language.title': 'Language Proficiency',
      'language.languageName': 'Language Name',
      'language.speaking': 'Speaking',
      'language.reading': 'Reading',
      'language.writing': 'Writing',
      'language.selectLanguage': 'Select Language',
      'language.select': 'Select Proficiency',
      'language.addNewLanguage': 'Add New Language',
      'language.delete': 'Delete',
      'language.proficiencyA': 'A: 75 – 100%',
      'language.proficiencyB': 'B: 50 – 75%',
      'language.proficiencyC': 'C: 0 - 50%',
      'language.proficiencyD': 'D: None',
      'validation.languageNameRequired': 'Language name is required.',
      'validation.speakingRequired': 'Speaking proficiency is required.',
      'validation.readingRequired': 'Reading proficiency is required.',
      'validation.writingRequired': 'Writing proficiency is required.',
      'validation.completeLastRow': 'Please complete the last language row before adding a new one.',
      'validation.hindiEnglishRequired': 'Hindi and English languages are required and cannot be removed.',
      'validation.minimumLanguagesRequired': 'At least 2 language entries (Hindi and English) are required.',
      'validation.completeAllFields': 'Please complete all required fields before submitting.',

      // Current Address Page
      'address.title': 'Current Residential Address',
      'address.streetAddress': 'Enter House Name, No. and Street Address:',
      'address.townCity': 'Town/City:',
      'address.district': 'District:',
      'address.state': 'State:',
      'address.pinCode': 'Pin Code:',
      'address.mobilePersonal': 'Mobile No. (Personal)',
      'address.mobileAlternate': 'Mobile No. (Alternate)',
      'address.email': 'Email:',
      'address.selectState': 'Select State',
      'address.selectCountry': 'Select',
      'address.enterStreetAddress': 'Enter Street Address',
      'address.enterTownCity': 'Enter Town/City',
      'address.enterDistrict': 'Enter District',
      'address.enterPinCode': 'Enter Pin Code',
      'address.enterHomePhone': 'Enter Home Phone',
      'address.enterWorkPhone': 'Enter Work Phone',
      'address.enterEmail': 'Enter Email',
      'validation.streetAddressRequired': 'Street Address is required.',
      'validation.townCityRequired': 'Town/City is required.',
      'validation.districtRequired': 'District is required.',
      'validation.stateRequired': 'State is required.',
      'validation.pinCodeRequired': 'Pin Code is required and must be 6 digits.',

      // Permanent Address Page
      'permanentAddress.title': 'Permanent Residential Address',
      'permanentAddress.sameAsCurrent': 'Permanent Residential Address',
      'permanentAddress.isSameAsCurrent': 'is the same as Current Residential Address',
      'permanentAddress.checkboxTooltip': 'Check this box if your permanent address matches your current residential address',

      // Emergency Contact Page
      'emergencyContact.title': 'Emergency Contact Information',
      'emergencyContact.contactName': 'Name:',
      'emergencyContact.relationship': 'Relationship:',
      'emergencyContact.selectRelationship': '-- Select Relationship --',
      'emergencyContact.enterContactName': 'Emergency Contact Name',
      'emergencyContact.enterEmergencyMobile': 'Enter Emergency Mobile',
      'emergencyContact.enterWorkPhone': 'Enter Work Phone',
      'emergencyContact.enterEmergencyEmail': 'Enter Emergency Email',
      'validation.contactNameRequired': 'Emergency Contact Name is required.',
      'validation.relationshipRequired': 'Relationship is required.',

      // Study Participation Information Page
      'studyParticipation.title': 'Siddha Yoga Participation',
      'studyParticipation.beginPracticeYear': 'Which year did you begin practicing Siddha Yoga?',
      'studyParticipation.selectPracticeYear': 'Select Practice Year',
      'studyParticipation.whereBeginPractice': 'Where did you begin your practice?',
      'studyParticipation.selectOption': 'Select an option',
      'studyParticipation.siddhaYogaCentre': 'Siddha Yoga Centre',
      'studyParticipation.home': 'Home',
      'studyParticipation.other': 'Other',
      'studyParticipation.ifOtherMention': 'If other, mention the name of the Siddha Yoga centre and city',
      'studyParticipation.enterCentreName': 'Enter Centre Name and City',
      'studyParticipation.howOftenVisit': 'How often do you visit your Siddha Yoga centre?',
      'studyParticipation.selectFrequency': 'Select Frequency',
      'studyParticipation.weekly': 'Weekly',
      'studyParticipation.monthly': 'Monthly',
      'studyParticipation.occasionally': 'Occasionally',
      'studyParticipation.never': 'Never',
      'studyParticipation.whichCentreVisit': 'Which Siddha Yoga centre do you visit?',
      'studyParticipation.state': 'State',
      'studyParticipation.district': 'District',
      'studyParticipation.centre': 'Centre',
      'studyParticipation.selectState': 'Select State',
      'studyParticipation.selectDistrict': 'Select District',
      'studyParticipation.selectCentre': 'Select Centre(s)',
      'studyParticipation.enterCentrePlaceholder': 'Please enter center',
      'studyParticipation.whichPracticeEngage': 'Which Siddha Yoga practice do you engage with regularly?',
      'studyParticipation.selectPractice': 'Select Practice',
      'studyParticipation.none': 'None',
      'studyParticipation.meditation': 'Meditation',
      'studyParticipation.chanting': 'Chanting',
      'studyParticipation.yogaAsanas': 'Yoga Asanas',
      'studyParticipation.studyScriptures': 'Study of Scriptures',
      'studyParticipation.seva': 'Seva',
      'studyParticipation.otherPractice': 'Other',
      'studyParticipation.regularlyEngageTeachings': 'Do you regularly engage in the study of Siddha Yoga teachings?',
      'studyParticipation.yes': 'Yes',
      'studyParticipation.no': 'No',
      'studyParticipation.ifYesSelected': 'If \'Yes\' is selected, with multiple select for the options',
      'studyParticipation.selectOptions': 'Select options',
      'studyParticipation.siddhaYogaPathWebsite': 'Siddha Yoga Path Website',
      'studyParticipation.siddhaYogaBooks': 'Siddha Yoga Books',
      'studyParticipation.homeStudyCourse': 'Home Study Course',
      'studyParticipation.neeleshwari': 'Neeleshwari',
      'studyParticipation.howOftenVisitWebsite': 'How often you visit your Siddha Yoga Path Website?',
      'studyParticipation.daily': 'Daily',
      'studyParticipation.participateSadhanaCircle': 'Are you participating in a sadhana circle?',
      'studyParticipation.howOften': 'How often?',
      'studyParticipation.firstShaktipatIntensive': 'In which year did you take your first Shaktipat Intensive?',
      'studyParticipation.selectFirstIntensiveYear': 'Select First Intensive Year',
      'studyParticipation.eventParticipationHistory': 'Siddha Yoga events you have participated in',
      'studyParticipation.eventParticipationHistorySubHeader': 'For the below events, list the years in which you participated in the last five years.',
      'studyParticipation.shaktipatIntensive': 'Shaktipat Intensive',
      'studyParticipation.audioSatsangs': 'Audio Satsangs',
      'studyParticipation.liveStreams': 'Live Streams',
      'studyParticipation.muktanandaDhyanSaptah': 'Muktananda Dhyan Saptah',
      'studyParticipation.sweetSurpriseSatsang': 'Sweet Surprise Satsang',
      'studyParticipation.retreatHistory': 'Siddha Yoga retreats you have participated in',
      'studyParticipation.retreatName': 'Retreat Name',
      'studyParticipation.selectRetreatName': 'Select Retreat Name',
      'studyParticipation.retreatDate': 'Retreat Date',
      'studyParticipation.selectDate': 'Select date',
      'studyParticipation.deleteRetreat': 'Delete Retreat',
      'studyParticipation.addNewRetreat': 'Add New Retreat',
      'studyParticipation.sevaHistory': 'Seva History',
      'studyParticipation.ashramName': 'Ashram Name / Center Name / Home',
      'studyParticipation.selectAshramName': 'Select Ashram Name',
      'studyParticipation.sevaRole': 'Seva Role',
      'studyParticipation.department': 'Department',
      'studyParticipation.year': 'Year',
      'studyParticipation.duration': 'Duration',
      'studyParticipation.supervisorName': 'Supervisor Name',
      'studyParticipation.deleteSeva': 'Delete Seva',
      'studyParticipation.addNewSeva': 'Add New Seva',
      'validation.beginPracticeYearRequired': 'This field is required.',
      'validation.practiceBeginDetailsRequired': 'This field is required.',
      'validation.otherCentreRequired': 'This field is required when \'other\' is selected.',
      'validation.centerVisitFrequencyRequired': 'This field is required.',
      'validation.currentSYPracticesRequired': 'This field is required.',
      'validation.isEngagedWithTeachingsRequired': 'This field is required.',
      'validation.isParticipateInSadhanaCircleRequired': 'This field is required.',
      'validation.firstIntensiveYearRequired': 'This field is required.',
      'validation.retreatNameRequired': 'Retreat name is required.',
      'validation.retreatDateRequired': 'Retreat date is required.',
      'validation.fillAllFieldsBeforeAdding': 'Please fill in all required fields before adding a new retreat.',
      'validation.atLeastOneRetreatRequired': 'At least 1 retreat history entry is required.',
      'validation.fillAllFieldsBeforeAddingSeva': 'Please fill in all required fields before adding a new seva.',
      'validation.atLeastOneSevaRequired': 'At least 1 seva history entry is required.',

      // Other Personal Information Page
      'otherPersonal.title': 'Other Personal Information',
      'otherPersonal.involvedOtherSpiritual': 'Have you ever been involved with any other spiritual organisation?',
      'otherPersonal.whichOnesWhen': 'Which ones and when?',
      'otherPersonal.enterOrganisationDetails': 'Enter Organisation Details',
      'otherPersonal.askedToLeaveAshram': 'Have you ever been asked to leave a Siddha Yoga ashram or center?',
      'otherPersonal.fromWhereWhenReason': 'From where, when and for what reason?',
      'otherPersonal.enterReason': 'Enter Reason',
      'otherPersonal.criminalOffence': 'Have you ever been convicted of a criminal or financial offence?',
      'otherPersonal.pleaseDescribe': 'Please describe:',
      'otherPersonal.enterDetails': 'Enter Details',
      'otherPersonal.drugHistory': 'Have you had any history of illegal drug use and/or treatment in the last five years?',
      'validation.involvedOtherSpiritualRequired': 'This field is required.',
      'validation.otherOrganisationDetailsRequired': 'This field is required.',
      'validation.askedToLeaveAshramRequired': 'This field is required.',
      'validation.reasonForAskToLeaveRequired': 'This field is required.',
      'validation.criminalOffenceRequired': 'This field is required.',
      'validation.offenceDetailsRequired': 'This field is required.',
      'validation.drugHistoryRequired': 'This field is required.',
      'validation.drugHistoryDetailsRequired': 'This field is required.',

      // References Page
      'references.title': 'Referees',
      'references.instructionText': 'Referees - Those who can speak about you and your seva. E.g. Seva supervisors, fellow Siddha Yogis or Centre leadership. Please do not list family members.',
      'references.reference1Name': 'Reference 1 - Name:',
      'references.reference2Name': 'Reference 2 - Name:',
      'references.emailAddress': 'E-mail Address:',
      'references.phoneNumber': 'Phone Number:',
      'references.relationship': 'Relationship:',
      'references.selectRelationship': '-- Select Relationship --',
      'references.sevaSupervisor': 'Seva Supervisor',
      'references.fellowSiddhaYogi': 'Fellow Siddha Yogi',
      'references.centreLeadership': 'Centre Leadership',
      'references.enterName': 'Enter name',
      'references.enterEmail': 'Enter email',
      'references.enterPhoneNumber': 'Enter phone number',
      'references.consentText': 'Consent: I consent to the above referees being contacted',
      'validation.reference1NameRequired': 'This field is required.',
      'validation.reference1EmailRequired': 'This field is required.',
      'validation.reference1EmailInvalid': 'Please enter a valid email address.',
      'validation.reference1PhoneRequired': 'Mobile is required and must be 10 digits.',
      'validation.reference1RelationshipRequired': 'This field is required.',
      'validation.reference2NameRequired': 'This field is required.',
      'validation.reference2EmailRequired': 'This field is required.',
      'validation.reference2PhoneRequired': 'Mobile is required and must be 10 digits.',
      'validation.reference2RelationshipRequired': 'This field is required.',
      'validation.consentRequired': 'Please accept the declaration to proceed.',

      // Medical History Page
      'medicalHistory.title': 'Medical History',
      'medicalHistory.height': 'Height',
      'medicalHistory.weight': 'Weight(kg)',
      'medicalHistory.enterHeight': 'Enter Height',
      'medicalHistory.select': 'Select',
      'medicalHistory.bloodPressure': 'Blood Pressure:',
      'medicalHistory.bloodSugarDiabetes': 'Blood Sugar or Diabetes:',
      'medicalHistory.brainStroke': 'Brain Stroke:',
      'medicalHistory.heartDisease': 'Heart Disease:',
      'medicalHistory.tuberculosis': 'Tuberculosis (TB):',
      'medicalHistory.asthma': 'Asthma:',
      'medicalHistory.cancer': 'Cancer:',
      'medicalHistory.hivAids': 'HIV / Aids:',
      'medicalHistory.respiratoryLungProblem': 'Respiratory / Lung problem:',
      'medicalHistory.visionHearingSpeechProblem': 'Problem related to Vision, Hearing or Speech:',
      'medicalHistory.depressionPsychological': 'Have you ever been diagnosed with depression or other psychological condition?:',
      'medicalHistory.otherMedicalCondition': 'Any medical condition other than those mentioned above?',
      'medicalHistory.typhoidFever': 'Typhoid fever?',
      'medicalHistory.psychiatricDisorder': 'Psychiatric disorder?',
      'medicalHistory.parasites': 'IsParasites?',
      'medicalHistory.hepatitis': 'IsHepatitis A, B, or C or yellow jaundice?',
      'medicalHistory.regularMedication': 'Are you required to take medicines regularly?',
      'medicalHistory.provideCurrentReading': 'Provide current reading',
      'medicalHistory.describeCurrentCondition': 'Describe current condition',
      'medicalHistory.whenTreatmentComplete': 'When did the treatment complete?',
      'medicalHistory.specify': 'Specify:',
      'medicalHistory.pleaseDescribe': 'Please describe',
      'medicalHistory.ifYesWhen': 'If Yes, when?',
      'medicalHistory.ifYesWhenWhatKind': 'If Yes, when & what kind?',
      'medicalHistory.ifYesWhenWhichOne': 'If Yes, when & which one?',
      'medicalHistory.pleaseSpecifyMedicine': 'Please specify medicine names and dosage:',
      'medicalHistory.enterBpRange': 'Enter BP Range',
      'medicalHistory.enterBloodSugarRange': 'Enter Blood Sugar Range',
      'medicalHistory.enterDetails': 'Enter Details',
      'validation.heightRequired': 'Height is required.',
      'validation.weightRequired': 'Weight is required.',
      'validation.bloodPressureRequired': 'This field is required.',
      'validation.bpRangeRequired': 'This field is required.',
      'validation.bloodSugarRequired': 'This field is required.',
      'validation.bloodSugarRangeRequired': 'This field is required.',
      'validation.brainStrokeRequired': 'This field is required.',
      'validation.brainStrokeDetailsRequired': 'This field is required.',
      'validation.heartProblemRequired': 'This field is required.',
      'validation.heartProblemDetailsRequired': 'This field is required.',
      'validation.tuberculosisRequired': 'This field is required.',
      'validation.tuberculosisDetailsRequired': 'This field is required.',
      'validation.asthmaRequired': 'This field is required.',
      'validation.asthmaDetailsRequired': 'This field is required.',
      'validation.cancerRequired': 'This field is required.',
      'validation.cancerDetailsRequired': 'This field is required.',
      'validation.hivRequired': 'This field is required.',
      'validation.hivConditionDetailsRequired': 'This field is required.',
      'validation.lungProblemRequired': 'This field is required.',
      'validation.lungProblemDetailsRequired': 'This field is required.',
      'validation.visionProblemRequired': 'This field is required.',
      'validation.visionProblemDetailsRequired': 'This field is required.',
      'validation.depressionRequired': 'This field is required.',
      'validation.depressionDetailsRequired': 'This field is required.',
      'validation.otherMedicalConditionRequired': 'This field is required.',
      'validation.otherMedicalConditionDetailsRequired': 'This field is required.',
      'validation.typhoidFeverRequired': 'This field is required.',
      'validation.typhoidFeverDetailsRequired': 'This field is required.',
      'validation.psychiatricDisorderRequired': 'This field is required.',
      'validation.psychiatricDisorderDetailsRequired': 'This field is required.',
      'validation.parasitesRequired': 'This field is required.',
      'validation.parasitesDetailsRequired': 'This field is required.',
      'validation.hepatitisRequired': 'This field is required.',
      'validation.hepatitisDetailsRequired': 'This field is required.',
      'validation.regularMedicationRequired': 'This field is required.',
      'validation.medicationsRequired': 'This field is required.',

      // Educational and Work Experience Page
      'educationWork.title': 'Educational and Work Experience',
      'educationWork.hasEducation': 'Do you have any educational qualifications?',
      'educationWork.hasWorkExperience': 'Do you have any work experience?',
      'educationWork.educationalBackground': 'Education',
      'educationWork.workExperience': 'Work Experience',
      'educationWork.srNo': 'Sr No',
      'educationWork.degreeDiploma': 'Degree / Diploma',
      'educationWork.fieldOfStudySpecialization': 'Field of Study / Specialization',
      'educationWork.university': 'University',
      'educationWork.yearOfCompletion': 'Year of Completion',
      'educationWork.action': 'Action',
      'educationWork.nameOfCompany': 'Name of Company',
      'educationWork.designationJobTitle': 'Designation / Job Title',
      'educationWork.fromToYear': 'From - To (Year)',
      'educationWork.fieldTrade': 'Field / Trade',
      'educationWork.educationNumber': 'Education #',
      'educationWork.workExperienceNumber': 'Work Experience #',
      'educationWork.enterDegreeDiploma': 'Enter Degree/Diploma',
      'educationWork.enterFieldOfStudy': 'Enter field of study',
      'educationWork.enterUniversity': 'Enter University',
      'educationWork.enterCompanyName': 'Enter company name',
      'educationWork.enterJobDesignation': 'Enter Job Designation',
      'educationWork.from': 'From (Year)',
      'educationWork.to': 'To (Year)',
      'educationWork.fieldTradeExample': 'E.g. Sales, IT, HR, Finance',
      'educationWork.removeEducation': 'Remove Education',
      'educationWork.removeWorkExperience': 'Remove Work Experience',
      'educationWork.addEducation': 'Add Education',
      'educationWork.addWorkExperience': 'Add Work Experience',
      'educationWork.atLeastOneEducationRequired': 'At least one educational record must be completed with all required fields.',
      'educationWork.atLeastOneWorkRequired': 'At least one work experience record must be completed with all required fields.',
      'validation.degreeDiplomaRequired': 'Degree/Diploma is required',
      'validation.degreeDiplomaMaxLength': 'Degree/Diploma cannot exceed 50 characters',
      'validation.fieldOfStudyRequired': 'Field of Study / Specialization is required',
      'validation.fieldOfStudyMaxLength': 'Field of Study / Specialization cannot exceed 50 characters',
      'validation.universityRequired': 'University is required',
      'validation.universityMaxLength': 'University cannot exceed 50 characters',
      'validation.yearRequired': 'Year is required',
      'validation.yearInvalidLength': 'Year must be exactly 4 digits',
      'validation.yearInvalidFormat': 'Year must contain only numbers',
      'validation.yearFuture': 'Year cannot be greater than current year',
      'validation.companyNameRequired': 'Company Name is required',
      'validation.companyNameMaxLength': 'Company Name cannot exceed 50 characters',
      'validation.designationRequired': 'Designation / Job Title is required',
      'validation.designationMaxLength': 'Designation / Job Title cannot exceed 50 characters',
      'validation.fromYearRequired': 'From year is required',
      'validation.fromYearInvalidLength': 'From year must be exactly 4 digits',
      'validation.fromYearInvalidFormat': 'From year must contain only numbers',
      'validation.fromYearFuture': 'From year cannot be greater than current year',
      'validation.toYearRequired': 'To year is required',
      'validation.toYearInvalidLength': 'To year must be exactly 4 digits',
      'validation.toYearInvalidFormat': 'To year must contain only numbers',
      'validation.toYearFuture': 'To year cannot be greater than current year',
      'validation.yearsFuture': 'Years cannot be greater than current year',
      'validation.invalidYearRange': 'From year must be less than To year',
      'validation.fieldTradeRequired': 'Field / Trade is required',
      'validation.fieldTradeMaxLength': 'Field / Trade cannot exceed 50 characters'
      ,

      // Physical Ability Page
      'physicalAbility.title': 'Physical Ability',
      'physicalAbility.neckOrShouldersQuestion': 'Do you have pain or limitation in your neck or shoulders?',
      'physicalAbility.backQuestion': 'Do you have pain or limitation in your back?',
      'physicalAbility.kneesQuestion': 'Do you have pain or limitation in your knees?',
      'physicalAbility.hipsQuestion': 'Do you have pain or limitation in your hips?',
      'physicalAbility.feetQuestion': 'Do you have pain or limitation in your feet?',
      'physicalAbility.handsWristsArmsQuestion': 'Do you have pain or limitation in your hands, wrists, or arms?',
      'physicalAbility.preventedActivitiesQuestion': 'What activities are you prevented from doing?',
      'physicalAbility.repetitiveStrainInjuryQuestion': 'Have you had repetitive strain injury?',
      'physicalAbility.whenAndHowLong': 'When & for how long?',
      'physicalAbility.affectedAreas': 'Details of affected areas?',

      // Generic placeholder (used across pages)
      'placeholder.enterDetails': 'Enter Details',

      // Physical Fitness Page
      'physicalFitness.title': 'Physical Fitness',
      'physicalFitness.canClimbStairs': 'Can you climb 3 flights of stairs easily?',
      'physicalFitness.why': 'Why?',
      'physicalFitness.canWalkDistance': 'Can you walk 1.5 km or 1 mile without a problem?',
      'physicalFitness.canLiftWeight': 'Can you lift 4 kg (10 lbs) without a problem?',
      'physicalFitness.canStandDuration': 'Can you stand for 30 minutes without a problem?',
      'physicalFitness.canSitDuration': 'Can you sit for 30 minutes without a problem?',
      'physicalFitness.canBendOver': 'Can you bend over without a problem?',
      'physicalFitness.ifNoWhy': 'If no, Why?',
      'physicalFitness.doExerciseRegularly': 'Do you exercise regularly?',
      'physicalFitness.exerciseType': 'What type of exercise do you do?',
      'physicalFitness.exerciseFrequency': 'How often do you exercise?',
      'physicalFitness.overallHealth': 'How is your overall health?',
      'physicalFitness.healthPoor': 'Poor',
      'physicalFitness.healthGood': 'Good',
      'physicalFitness.healthExcellent': 'Excellent',
      'physicalFitness.healthComments': 'Please tell us anything that would help us know about your health and physical condition:',
      'physicalFitness.forFemalePregnancy': 'For Female Students: Are you pregnant?',
      'physicalFitness.pregnancyDueDate': 'If yes, please state your due date:',
      'placeholder.enterReason': 'Enter Reason',
      'placeholder.enterExerciseType': 'Enter Exercise Type',
      'placeholder.enterExerciseFrequency': 'Enter Exercise Frequency (e.g., Daily, Weekly)',
      'placeholder.enterComments': 'Enter Comments',
      'placeholder.enterDueDate': 'Enter Due Date'
      ,

      // Documents Page
      'documents.title': 'Upload Documents',
      'documents.guidelines.photo.header': 'Standard Guidelines: For Your Passport Size Photo',
      'documents.guidelines.photo.li1': 'Photo should be similar to a passport-type photograph.',
      'documents.guidelines.photo.li2': 'Ensure the uploaded photo is in JPEG or PNG format.',
      'documents.guidelines.photo.li3': 'Keep the file size below 5 MB.',
      'documents.guidelines.photo.li4': 'Ensure the photo is of good resolution.',
      'documents.guidelines.photo.li5': 'Recheck the content of the photo before uploading.',
      'documents.guidelines.id.header': 'Standard Guidelines: ID Proof',
      'documents.guidelines.id.li1': 'Please upload your Aadhaar Card or Passport ID document.',
      'documents.guidelines.id.li2': 'Ensure the uploaded document is in PDF, JPEG, or PNG format.',
      'documents.guidelines.id.li3': 'Keep the file size below 5 MB.',
      'documents.guidelines.id.li4': 'Recheck before uploading to ensure it is the correct document.',
      'documents.fileInfo.title': 'File Upload Guidelines:',
      'documents.fileInfo.li1': 'Passport Size Photo: JPEG or PNG format only, max 5 MB',
      'documents.fileInfo.li2': 'All Other Documents: PDF, JPEG, or PNG format, max 5 MB',
      'documents.fileInfo.li3': 'File size limit applies to all document types',
      'documents.table.srNo': 'Sr No',
      'documents.table.documentName': 'Document Name',
      'documents.table.fileName': 'File Name',
      'documents.table.action': 'Action',
      'documents.table.infoTitle': 'File Restrictions:',
      'documents.table.infoText': 'Passport Photo: JPEG/PNG only | All Documents: Max 5MB | Formats: PDF, JPEG, PNG',
      'documents.section.mandatory': 'Mandatory Documents',
      'documents.section.nonMandatory': 'Non-Mandatory Documents',
      'documents.doc.passportPhoto': 'Passport size photograph',
      'documents.doc.aadharCard': 'ID Proof - Aadhaar Card or Passport',
      'documents.doc.photoIdProof': 'Photo ID Proof',
      'documents.doc.panCard': 'PAN Card',
      'documents.doc.resume': 'Resume',
      'documents.doc.sevaProfileDoc': 'Seva Profile Document',
      'documents.doc.vsDocument': 'VS Document',
      'documents.doc.other': 'Other',
      'documents.noFile': 'No file uploaded',
      'documents.addFileTooltip.photo': 'Add File (JPEG/PNG only, max 5MB)',
      'documents.addFileTooltip.default': 'Add File (PDF/JPEG/PNG, max 5MB)',
      'documents.deleteFileTooltip': 'Delete File',
      'documents.requiredMark': '* Required',
      'documents.validation.passportRequired': 'Passport size photograph is required. Please upload before proceeding.',
      'documents.validation.aadharRequired': 'Aadhaar card is required. Please upload before proceeding.',
      'documents.uploading': 'Uploading file...',
      'documents.uploadingTitle': 'Uploading',
      'documents.deleting': 'Deleting file...',
      'documents.deletingTitle': 'Deleting',
      'documents.deleteConfirm': 'Are you sure you want to delete this document?',

      // Agreement Page
      'agreement.title': 'ACKNOWLEDGEMENTS',
      'agreement.acknowledgement.title': 'In consideration of the opportunity to visit the Gurudev Siddha Peeth (the \'Ashram\'), I acknowledge',
      'agreement.declaration': 'Declaration: I hereby certify that the information given above are true and correct.',
      'agreement.button.proceed': 'PROCEED',
      'agreement.button.submit': 'SUBMIT',
      'agreement.clause1': 'All the information on this application is true and complete. I understand that if I give any false information or omit or misrepresent any information that is asked, my application may be rejected. If any false information, omission, or misrepresentation is discovered after my application has been accepted or I have started on in the position for which I am accepted, I understand that my acceptance may be revoked or my Gurukula student status may be terminated.',
      'agreement.clause2': 'If any of the information on this application changes, I will promptly inform Gurudev Siddha Peeth Gurukula Applications.',
      'agreement.clause3': 'I understand that Gurudev Siddha Peeth reserves the right to alter my length of stay at Gurudev Siddha Peeth for any reason.',
      'agreement.clause4': 'I understand that this application is not an agreement or contract. It is an application to Gurudev Siddha Peeth to be considered for acceptance as a Gurukula student and/or retreat participant at Gurudev Siddha Peeth\'s ashram site in Ganeshpuri, India (the "Ashram"). I understand that receipt and/or submission by me of this application is not an assurance that Gurudev Siddha Peeth will accept me for a position as a Gurukula student and/or retreat participant and I acknowledge that no promises have been made to me to this effect. I also acknowledge that if I am accepted for a position as a Gurukula student and/or retreat participant, my stay at the Ashram may be terminated by me or Gurudev Siddha Peeth at any time, and that no express or implied promises or representations have been made, except as specifically set forth in this application. This application shall remain the property of Gurudev Siddha Peeth.',
      'agreement.clause5': 'I authorize Gurudev Siddha Peeth representatives to inquire into my background, previous work experience, and education and to contact my references and previous employers to confirm information supplied by me and to obtain other information about me in connection with my application. I authorize any person or organization to provide information about me to Gurudev Siddha Peeth in connection with such inquiry and release any person or organization from all liability for providing this information.',
      'agreement.clause6': 'I understand that all decisions concerning my application are made by Gurudev Siddha Peeth.',
      'agreement.clause7': 'I acknowledge that I have read and fully understand this application including the above statements and authorization for a background inquiry. I agree that by submitting this application electronically this certification will be binding and effective as if it is an original. I also agree that a scanned or facsimile signed copy of this certification will be binding and effective as if it is an original.',
      'agreement.clause8': 'I acknowledge that Gurudev Siddha Peeth reserves the right to select individuals who practice Siddha Yoga meditation, based on their Siddha Yoga background as well as other skills and qualifications.',
      'agreement.clause9': 'I have read, understood, and agree with the statements above.',
      'agreement.acknowledgement.clause1': 'I understand and acknowledge that Gurudev Siddha Peeth is allowing me to stay at the Ashram in partial reliance upon this Declaration and Agreement of Release, Waiver and Indemnity (the "Agreement") which I have freely executed as a condition precedent to my staying in the Ashram.',
      'agreement.acknowledgement.clause2': 'I have or will have the health insurance and the financial means to address any health needs or emergencies that may occur during my visit.',
      'agreement.acknowledgement.clause3': 'I undertake to bear all expenses, including medical expenses that are incurred or are likely to be incurred as a result of any accident, loss of property or illness occurring during my stay or in connection with my departure or return from the Ashram.',
      'agreement.acknowledgement.clause4': 'I acknowledge that GSP is not obligated to attend to any of my medical needs during my visit to the Ashram. In case of a medical emergency during my visit, I am not able to secure appropriate medical treatment; I authorized a representative of GSP to secure whatever treatment is necessary. GSP may, but is not obligated to, take any actions it considers warranted under the circumstances, regarding my health and safety. Such actions do not create a special relationship between GSP and me.',
      'agreement.acknowledgement.clause5': 'I understand that despite the best efforts of Gurudev Siddha Peeth, I may become ill or injured during my visit, and I release Gurudev Siddha Peeth, its Trustees, and/or agents from all claims I may have for injuries, illness, or other loss, related to my visit, and waive any and all rights to bring such claims including claims of negligence.',
      'agreement.acknowledgement.clause6': 'I fully assume any and all risks, costs, and responsibilities associated with or arising from my stay at the Ashram.',
      'agreement.acknowledgement.clause7': 'I shall unconditionally indemnify and hold Gurudev Siddha Peeth harmless against any and all claims, incurred by Gurudev Siddha Peeth as a consequence of any acts or omissions on my part or of any breach committed by me of the commitments or undertakings herein.',
      'agreement.acknowledgement.clause8': 'I hereby release and forever discharge Gurudev Siddha Peeth from all grievances, claims, disputes, liabilities, demands, and causes of action known or unknown, fixed or contingent, which I may have or claim to have against the Gurudev Siddha Peeth as a result of my stay in the Ashram as a Gurukula student, and do hereby agree not to file a claim or make any other application to assert such claims.',
      'agreement.acknowledgement.clause9': 'The foregoing release and hold-harmless agreements shall be binding on my successors, heirs, and assigns.',
      'agreement.acknowledgement.clause10': 'I acknowledge that I have read, understood and agree with the above statements.',

      // Summary & Requirements Modal - Gurukula Student
      'modal.gurukula.title': 'Gurukula Student: Applying for Seva Stay',
      'modal.gurukula.subtitle': 'Applying for seva stay of six months or higher',
      'modal.gurukula.req.age18+': 'Applicant has to be 18 years of age or older.',
      'modal.gurukula.req.sadhana2y': 'Practice of Siddha Yoga sadhana of at least two years is required.',
      'modal.gurukula.req.intensive3y': 'Participation in at least one Siddha Yoga Shaktipat Intensive in the last three years is required.',
      'modal.gurukula.req.sweetSurprise': 'Participation in a Sweet Surprise satsang in the past two years is required.',
      'modal.gurukula.req.understandingSeva': 'An understanding of seva as a spiritual practice is required.',
      'modal.gurukula.req.visitingSeva': 'Must have offered seva in Gurudev Siddha Peeth as a Visiting sevite in the past five years.',
      'modal.gurukula.req.sixMonths': 'Must be able to dedicate six months or more to offer seva.',
      'modal.gurukula.req.language': 'Proficiency in English and/or Hindi with the ability to understand seva instructions (written and verbal) and to share sadhana experiences is required.',
      'modal.gurukula.req.health': 'Good health and physical fitness to offer seva and participate fully in the Ashram Daily Schedule is required.',
      'modal.gurukula.req.centreParticipation2y': 'It is preferred to have regular participation in a Siddha Yoga meditation centre or Siddha Yoga chanting and meditation group, and in the global Siddha Yoga webcasts for the past two years.',
      'modal.gurukula.req.sevaParticipation1y': 'It is preferred to have regular participation in seva at a Siddha Yoga meditation centre, an ashram or chanting and meditation group for the past one year.',

      // Summary & Requirements Modal - Pilgrimage to the Heart Retreat
      'modal.pth.title': 'Pilgrimage to the Heart Retreat',
      'modal.pth.subtitle': 'An opportunity for Siddha Yoga students to intensify their sadhana. Each day of the retreat includes chanting, meditation, contemplation, study, hatha yoga, the practice of silence, and at the centre of it all, the ever-deepening connection with the inner Self.',
      'modal.pth.req.age18+': 'Applicant has to be 18 years of age or older.',
      'modal.pth.req.sadhana2y': 'Practice of Siddha Yoga sadhana of at least two years is required.',
      'modal.pth.req.sadhana1yNote': 'For students between 18 to 29 years of age, a practice of at least one year of Siddha Yoga sadhana is required.',
      'modal.pth.req.intensive3y': 'Participation in at least one Siddha Yoga Shaktipat Intensive in the last three years is required.',
      'modal.pth.req.dailyMeditation': 'Practice of daily meditation for the past one year is required.',
      'modal.pth.req.language': 'Proficiency in the language in which the retreat is offered is required.',
      'modal.pth.req.health': 'Good health and physical fitness to participate fully in the retreat schedule from early morning to evening is required.',
      'modal.pth.req.dakshina': 'It is preferred to have a regular practice of dakshina.',
      'modal.pth.req.centreParticipation2y': 'It is preferred to have regular participation in a Siddha Yoga meditation centre or Siddha Yoga chanting and meditation group, and in the global Siddha Yoga webcasts for the past two years.',
      'modal.pth.req.sevaParticipation1y': 'It is preferred to have regular participation in seva at a Siddha Yoga meditation centre, an ashram or chanting and meditation group for the past one year.',

      // Summary & Requirements Modal - Siddha Yoga Family Retreat
      'modal.familyRetreat.title': 'Siddha Yoga Family Retreat',
      'modal.familyRetreat.subtitle': 'In the Siddha Yoga Family Retreat, families with children from ages 10 to 17 learn and practise ways of honouring the Self that abides and manifests through them, others, and the world around them.',
      'modal.familyRetreat.parents.title': 'Prerequisites for applying: For Parents',
      'modal.familyRetreat.parents.sadhana2y': 'Practice of Siddha Yoga sadhana of at least two years is required.',
      'modal.familyRetreat.parents.intensive3y': 'Participation in at least one Siddha Yoga Shaktipat Intensive in the last three years is required.',
      'modal.familyRetreat.parents.hindi': 'Proficiency in Hindi is required.',
      'modal.familyRetreat.parents.health': 'Good health and physical fitness to participate fully in the retreat sessions and ashram daily schedule is required.',
      'modal.familyRetreat.parents.centreParticipation1y': 'It is preferred to have regular participation in a Siddha Yoga meditation centre or Siddha Yoga chanting and meditation group, and in the global Siddha Yoga webcasts for the past one year.',
      'modal.familyRetreat.parents.sevaParticipation1y': 'It is preferred to have regular participation in seva at a Siddha Yoga meditation centre, an ashram or chanting and meditation group for the past one year.',
      'modal.familyRetreat.parents.dakshina': 'It is preferred to have a regular practice of dakshina.',
      'modal.familyRetreat.children.title': 'Prerequisites for applying: For Children',
      'modal.familyRetreat.children.age10to17': 'Age of 10 to 17 years is required.',
      'modal.familyRetreat.children.example': 'Example for the purpose of explanation only: If the child\'s date of birth is 1 January 2000 and arrival date to the ashram is between 1 January 2010 and 31 December 2017, then he/she is eligible for the Siddha Yoga Family Retreat.',
      'modal.familyRetreat.children.hindi': 'Basic proficiency in Hindi is required.',
      'modal.familyRetreat.children.motivation': 'It is required that the child is self-motivated to participate in the retreat sessions and ashram daily schedule.',
      'modal.familyRetreat.children.intensive': 'It is preferred to have participated in a Siddha Yoga Shaktipat Intensive.',
      'modal.familyRetreat.children.satsang': 'It is preferred to have regular participation in children or yuva satsangs in the past two years.',

      // Summary & Requirements Modal - Siddha Yoga Retreat for Young Adults
      'modal.kypr.title': 'Siddha Yoga Retreat for Young Adults',
      'modal.kypr.subtitle': 'In the welcoming atmosphere of Gurudev Siddha Peeth, young Siddha Yoga students participate in the Siddha Yoga practices of chanting and meditation, as well as in active learning sessions and quiet time in nature. As participants engage fully in the retreat, they strengthen their connection with the inner Self. Through their dynamic engagement with spiritual practices and study in this transformative environment, they bring lasting benefit to their sadhana.',
      'modal.kypr.req.age18to30': 'Age 18 years to 30 years is required.',
      'modal.kypr.req.ageExample': 'Example for the purpose of explanation only: If the applicant\'s date of birth is 1 January 2000 and arrival date to the ashram is between 1 January 2018 and 31 December 2030, then he/she is eligible for the Siddha Yoga Retreat for Young Adults.',
      'modal.kypr.req.sadhana1y': 'Practice of Siddha Yoga sadhana of at least one year is required.',
      'modal.kypr.req.intensive3y': 'Participation in at least one Siddha Yoga Shaktipat Intensive in the last three years is required.',
      'modal.kypr.req.dailyMeditation': 'Practice of daily meditation for the past one year is required.',
      'modal.kypr.req.hindi': 'Proficiency in Hindi is required.',
      'modal.kypr.req.health': 'Good health and physical fitness to participate fully in the retreat sessions and ashram daily schedule is required.',
      'modal.kypr.req.centreParticipation1y': 'It is preferred to have regular participation in a Siddha Yoga meditation centre or Siddha Yoga chanting and meditation group, and in the global Siddha Yoga webcasts for the past one year.',
      'modal.kypr.req.sevaParticipation1y': 'It is preferred to have regular participation in seva at a Siddha Yoga meditation centre, an ashram or chanting and meditation group for the past one year.',
      
      // Summary & Requirements Modal - Visiting Seva
      'modal.visitingSeva.title': 'Visiting Seva: Applying for Seva Stay',
      'modal.visitingSeva.subtitle': 'Applying for seva stay of one to five months',
      'modal.common.prerequisitesTitle': 'Prerequisites for applying:',
      'modal.common.ageNote': '(The age brackets are applicable and enforced on the day of arrival.)',
      'modal.common.guardianshipNote': '(To know details about the guardianship process, please contact the Applications department of Gurudev Siddha Peeth.)',
      'modal.common.exampleTitle': 'Example for the purpose of explanation only:',
      'modal.visitingSeva.req.age18+': 'Applicant has to be 18 years of age or older.',
      'modal.visitingSeva.req.sadhana1y': 'Practice of Siddha Yoga sadhana of at least one year is required.',
      'modal.visitingSeva.req.intensive3y': 'Participation in at least one Siddha Yoga Shaktipat Intensive in the last three years is required.',
      'modal.visitingSeva.req.language': 'Basic proficiency in English and/or Hindi with the ability to understand seva instructions (written and verbal) and to share sadhana experiences is required.',
      'modal.visitingSeva.req.health': 'Good health and physical fitness to offer seva and participate fully in the Ashram Daily Schedule is required.',
      'modal.visitingSeva.req.centreParticipation': 'It is preferred to have a regular participation in a Siddha Yoga meditation centre or Siddha Yoga chanting and meditation group, and in the global Siddha Yoga webcasts for the past one year.',
      'modal.visitingSeva.req.sevaParticipation': 'It is preferred to have regular participation in seva at a Siddha Yoga meditation centre, an ashram or chanting and meditation group for the past one year.',
      'modal.visitingSeva.children5to12.title': 'Visiting Seva for children: Age 5 to 12 years',
      'modal.visitingSeva.children5to12.req1': 'Children between the age of five and nine years must come with both parents.',
      'modal.visitingSeva.children5to12.req2': 'Children between the age of 10 and 12 years must come with both parents or one parent and a guardian.',
      'modal.visitingSeva.children5to12.req3': 'At least one parent must agree to be with their child/children continuously.',
      'modal.visitingSeva.children5to12.req4': 'Parents and guardian accompanying the child / children must meet the Visiting Seva requirements.',
      'modal.visitingSeva.children5to12.req5': 'Parents can apply for seven days with their child/children.',
      'modal.visitingSeva.children5to12.req6': 'Can apply to Gurudev Siddha Peeth anytime except during the retreat dates that are posted on the website.',
      'modal.visitingSeva.children13to17.title': 'Visiting Seva for children: Age 13 to 17 years',
      'modal.visitingSeva.children13to17.req1': 'Must be accompanied by at least one parent or one guardian of the same gender.',
      'modal.visitingSeva.children13to17.req2': 'Parent/Guardian accompanying the child/children must meet the Visiting Sevite requirements.',
      'modal.visitingSeva.children13to17.req3': 'Can apply to offer seva in Gurudev Siddha Peeth except during retreats.',
      'modal.visitingSeva.example.p1': "If the child's date of birth is 1 January 2000 and arrival date to the ashram is between 1 January 2005 and 31 December 2009, then the child must come with both parents. If the arrival date of the child to the ashram is between 1 January 2010 and 31 December 2012 then the child must come with both parents or one parent and a guardian.",
      'modal.visitingSeva.example.p2': "If the child's date of birth is 1 January 2000 and arrival date to the ashram is between 1 January 2013 and 31 December 2017, then the child must be accompanied by at least one parent or one guardian of the same gender.",
      // Contemplation (Visiting Seva)
      'contemplation.title': 'Contemplation Questions',
      'contemplation.chooseSubmissionTitle': 'Choose Your Submission Method',
      'contemplation.chooseSubmissionSubtitle': 'Select how you would like to submit your contemplation responses',
      'contemplation.card.form.title': 'Fill Form Online',
      'contemplation.card.form.description': 'Answer questions directly in the form below',
      'contemplation.card.file.title': 'Upload PDF',
      'contemplation.card.file.description': 'Upload your completed contemplation document',
      'contemplation.tooltip': 'This is a tooltip',
      'contemplation.alert.title': 'Please read before answering the contemplation questions',
      'contemplation.alert.intro': 'Write an essay response of 100 to 150 words to each of the following questions. Please be specific in your answers and use examples wherever possible',
      'contemplation.alert.demonstrate': 'Your response should demonstrate that:',
      'contemplation.alert.point1': 'You have committed yourself to a conscious and sustained engagement with Siddha Yoga meditation.',
      'contemplation.alert.point2': 'You have taken responsibility for your learning, your experience and your spiritual path.',
      'contemplation.alert.point3': 'You have made an effort to clearly articulate your understanding and experience.',
      'contemplation.answerPlaceholder': 'Please answer the question here',
      'contemplation.words': 'words',
      'contemplation.q1.title': '1. How are you studying and practising the following in your life? ',
      'contemplation.q1a.label': '1. How are you studying and practising the following in your life? A. Siddha Yoga Teachings and Practices [Mention specific teachings, practices (frequency, duration), etc.]:',
      'contemplation.q1b.label': 'B. Siddha Yoga Path Website [Mention specific elements (Talks, Commentaries, Photo galleries, etc.), how you engage with them, how frequently, etc.]:',
      'contemplation.q2.intro': '2. Describe with examples one specific benefit you have experienced from your engagement with the Siddha Yoga teachings and practices:',
      'contemplation.q2a.label': 'A. Siddha Yoga Teachings and Practices:',
      'contemplation.q2b.label': 'B. Siddha Yoga Path Website:',
      'contemplation.q3.label': '3. Seva is an opportunity to learn and cultivate virtues. How will you work on yourself while offering seva?',
      'contemplation.q4.label': '4. Gurudev Siddha Peeth\'s sacred atmosphere is sustained by the Ashram’s daily schedule and discipline. How will you uphold them both?',
      'contemplation.q5.label': '5. How does seva help you to understand and implement Shree Gurumayi\'s teachings?',

      // Additional validation used in contemplation
      'validation.minLength100': 'Your answer must contain minimum 100 words.',
      'validation.maxLength1000': 'Maximum length is 5000 characters.',

      // Accommodation Details (Visiting Seva)
      'accommodation.title': 'Information Related to Ashram Stay',
      'accommodation.housingPreference.label': 'What is your housing preference?:',
      'accommodation.housingPreference.options.dorm': 'Dorm',
      'accommodation.housingPreference.options.double': 'Double',
      'accommodation.housingPreference.options.doubleAc': 'Double with AC',
      'accommodation.housingPreference.options.single': 'Single',
      'accommodation.housingPreference.options.singleAc': 'Single with AC',
      'accommodation.validation.housingPreferenceRequired': 'Housing preference is required.',
      'accommodation.snoreQuestion': 'Have you been told that you snore?',
      'accommodation.validation.snoreRequired': 'Snoring information is required.',
      'accommodation.roommateConcernQuestion': 'Do you have concerns about sharing accommodation with roommates?',
      'accommodation.validation.roommateConcernRequired': 'Roommate concern information is required.',
      'accommodation.describeConcerns': 'Please describe:',
      'accommodation.otherRequests': 'Do you have any requests related to accommodations?',
      'accommodation.placeholder.enterRequests': 'Enter requests',

      // Skills (Visiting Seva)
      'skills.title': 'Skills',
      'skills.instructions': 'Other Skills (Please mark your skill level in ANY of the following fields 1 = Basic, 2 = Good, 3 = Very Good)',
      'skills.level.basic': 'Basic',
      'skills.level.good': 'Good',
      'skills.level.veryGood': 'Very Good',
      'skills.category.management': 'Management',
      'skills.category.design': 'Design',
      'skills.category.arts': 'Arts',
      'skills.category.construction': 'Construction',
      'skills.category.finance': 'Finance',
      'skills.category.foodServices': 'Food Services',
      'skills.category.healthMedical': 'Health & Medical',
      'skills.category.technical': 'Technical',
      'skills.software.word': 'Word',
      'skills.software.excel': 'Excel',
      'skills.software.powerPoint': 'PowerPoint',
      'skills.software.outlook': 'Outlook',
      'skills.otherSkills': 'Other Skills',
      'skills.placeholder.otherSkills': 'Specify other skills',

      // Tentative Dates (Visiting Seva)
      'tentativeDates.title': 'Your dates of availability for offering',
      'tentativeDates.tooltip': 'This is a tooltip',
      'tentativeDates.info.readBefore': 'Please read the information below before choosing your available dates',
      'tentativeDates.info.paragraph1a': 'In the space below, please indicate the dates that you will be available to come.',
      'tentativeDates.info.gsp': 'Gurudev Siddha Peeth (GSP)',
      'tentativeDates.info.paragraph1b': 'When a seva opportunity matches your skills and your availability, you may be invited to come to offer seva.This invitation will be made sufficiently in advance so you can make your travel and personal arrangements with ease.',
      'tentativeDates.info.paragraph1c': 'Your stay is confirmed only once you are formally invited to come to GSP.',
      'tentativeDates.factors.title': 'Factors to keep in mind when selecting your available dates',
      'tentativeDates.factors.healthTitle': 'Good health:',
      'tentativeDates.factors.healthText': 'You must be in good health to fully participate in the ashram daily schedule.',
      'tentativeDates.factors.weatherTitle': 'Weather:',
      'tentativeDates.factors.weatherText': ' During the monsoon season Ganeshpuri sees heavy rains for prolonged periods of time and high humidity may lead to growth of mold, even in rooms. In the summer too, Ganeshpuri is very hot and humid. This is so you are aware and can plan accordingly.',
      'tentativeDates.factors.recentEventsTitle': 'If you have recently experienced any of the following, you are requested to come at least 6 months after the experience or when you are fully recovered, whichever is later:',
      'tentativeDates.factors.recent.divorce': 'Divorce or termination of a long-term relationship',
      'tentativeDates.factors.recent.death': 'Death of a loved one',
      'tentativeDates.factors.recent.addictionRecovery': 'Recovery from alcohol or drug addiction',
      'tentativeDates.factors.recent.surgeryIllness': 'Major surgery or a serious illness',
      'tentativeDates.factors.recent.mentalHealthTreatment': 'Treatment for depression or any other mental health condition',
      'tentativeDates.arrivalDate': 'Requested Arrival Date:',
      'tentativeDates.arrivalDatePlaceholder': 'Select Requested Arrival Date',
      'tentativeDates.departureDate': 'Requested Departure Date:',
      'tentativeDates.departureDatePlaceholder': 'Select Requested Departure Date',
      'tentativeDates.arrivalBeforeDeparture': 'Requested Arrival Date must be before or on Requested Departure Date.',
      'tentativeDates.departureAfterArrival': 'Requested Departure Date must be after or on Requested Arrival Date.',
      'validation.arrivalDateRequired': 'Arrival date is required.',
      'validation.departureDateRequired': 'Departure date is required.',

      // PTH Contemplation
      'pthContemplation.title': 'Contemplation Questions',
      'pthContemplation.chooseSubmissionTitle': 'Choose Your Submission Method',
      'pthContemplation.chooseSubmissionSubtitle': 'Select how you would like to submit your contemplation responses',
      'pthContemplation.card.form.title': 'Fill Form Online',
      'pthContemplation.card.form.description': 'Answer questions directly in the form below',
      'pthContemplation.card.file.title': 'Upload PDF',
      'pthContemplation.card.file.description': 'Upload your completed contemplation document',
      'pthContemplation.q1.label': '1. How are you studying and practising the teachings of Siddha Yoga? What benefits are you experiencing from this study and practice?',
      'pthContemplation.q2.label': '2. In what ways have you tried to deepen your meditation practice? Include specific tools that you have used.',
      'pthContemplation.q3.label': '3. How does your regular practice of meditation influence the various aspects of your daily life, namely your interactions with others, your work and your inner state?',
      'pthContemplation.q4.label': '4. State your purpose for participating in this Siddha Yoga retreat or event at this point in time? How will you prepare yourself to support this purpose?',
      'pthContemplation.placeholder.answerHere': 'Please answer the question here',
      'validation.minLength10': 'Minimum length is 10 characters.',

      'validation.centreRequired': 'Center is required.'

    },

    hi: {
      // Common
      'common.instructions': 'निर्देश',
      'common.contactUs': 'संपर्क करें',
      'common.indianApplications': 'भारतीय आवेदन',
      'common.email': 'ईमेल',
      'common.phone': 'फोन',
      'common.mobile': 'मोबाइल',

      // Instructions
      'instructions.registration.title': 'पंजीकरण निर्देश',
      'instructions.registration.text': 'यदि आप पंजीकृत नहीं हैं, तो',
      'instructions.registration.clickHere': 'यहाँ क्लिक करें',
      'instructions.registration.createAccount': 'अपना खाता बनाने के लिए।',
      'instructions.registration.passwordInfo': 'एक सुरक्षित पासवर्ड चुनें (न्यूनतम 8 अक्षर, बड़े अक्षर, छोटे अक्षर और विशेष अक्षर सहित)।',
      'instructions.registration.mandatoryFields': 'पंजीकरण प्रक्रिया पूरी करने के लिए सभी फ़ील्ड अनिवार्य हैं।',
      'instructions.login.title': 'लॉगिन निर्देश',
      'instructions.login.text': 'यदि आप पहले से पंजीकृत हैं, तो OTP (वन टाइम पासवर्ड) प्राप्त करने के लिए कृपया अपना ईमेल पता या मोबाइल नंबर दर्ज करें।',
      'instructions.login.otpText': 'लॉगिन करने के लिए OTP दर्ज करें।',
      'instructions.login.warning': 'अपना OTP किसी के साथ साझा न करें।',

      // Login Page
      'login.title': 'लॉगिन',
      'login.welcome': 'गुरुदेव सिद्ध पीठ आवेदन पोर्टल में आपका स्वागत है',

      // Registration Page
      'registration.title': 'पंजीकरण',
      'registration.welcome': 'गुरुदेव सिद्ध पीठ आवेदन पोर्टल में आपका स्वागत है',

      // Form Fields
      'form.loginMethod': 'लॉगिन विधि',
      'form.email': 'ईमेल',
      'form.mobile': 'मोबाइल नंबर',
      'form.emailAddress': 'ईमेल पता',
      'form.emailPlaceholder': 'अपना ईमेल पता दर्ज करें',
      'form.mobileNumber': 'मोबाइल नंबर',
      'form.mobilePlaceholder': 'अपना मोबाइल नंबर दर्ज करें',
      'form.otp': 'OTP',
      'form.sequenceNo': 'क्रम संख्या',
      'form.otpPlaceholder': 'OTP दर्ज करें',
      'form.resendOtp': 'OTP पुनः भेजें',
      'form.firstName': 'पहला नाम',
      'form.middleName': 'मध्य नाम',
      'form.lastName': 'अंतिम नाम',
      'form.firstNamePlaceholder': 'अपना पहला नाम दर्ज करें',
      'form.middleNamePlaceholder': 'अपना मध्य नाम दर्ज करें',
      'form.lastNamePlaceholder': 'अपना अंतिम नाम दर्ज करें',
      'form.dateOfBirth': 'जन्म तिथि',
      'form.dateOfBirthPlaceholder': 'जन्म तिथि चुनें',
      'form.gender': 'लिंग',
      'form.selectGender': 'लिंग चुनें',
      'form.male': 'पुरुष',
      'form.female': 'महिला',
      'form.other': 'अन्य',
      'form.specifyGender': 'कृपया अपना लिंग निर्दिष्ट करें',
      'form.genderPlaceholder': 'अपना लिंग दर्ज करें',
      'form.password': 'पासवर्ड',
      'form.confirmPassword': 'पासवर्ड की पुष्टि करें',
      'form.passwordPlaceholder': 'अपना पासवर्ड दर्ज करें',
      'form.confirmPasswordPlaceholder': 'अपने पासवर्ड की पुष्टि करें',
      'form.countryCode': 'देश कोड',
      'form.selectCountry': 'देश चुनें',

      // Validation Messages
      'validation.emailRequired': 'ईमेल आवश्यक है।',
      'validation.emailInvalid': 'कृपया एक वैध ईमेल पता दर्ज करें।',
      'validation.countryCodeRequired': 'देश कोड आवश्यक है।',
      'validation.mobileRequired': 'मोबाइल नंबर आवश्यक है।',
      'validation.mobileInvalid': 'अमान्य मोबाइल नंबर प्रारूप।',
      'validation.mobileMaxLength': 'मोबाइल नंबर 10 अंकों से अधिक नहीं हो सकता।',
      'validation.mobileMustDiffer': 'आपातकालीन संपर्क नंबर वर्तमान पते के नंबर से अलग होना चाहिए।',
      'validation.otpRequired': 'OTP आवश्यक है।',
      'validation.firstNameRequired': 'पहला नाम आवश्यक है।',
      'validation.firstNameMaxLength': 'पहला नाम 50 अक्षरों से अधिक नहीं हो सकता।',
      'validation.middleNameMaxLength': 'मध्य नाम 50 अक्षरों से अधिक नहीं हो सकता।',
      'validation.lastNameRequired': 'अंतिम नाम आवश्यक है।',
      'validation.lastNameMaxLength': 'अंतिम नाम 50 अक्षरों से अधिक नहीं हो सकता।',
      'validation.dateOfBirthRequired': 'जन्म तिथि आवश्यक है।',
      'validation.genderRequired': 'लिंग आवश्यक है।',
      'validation.genderMaxLength': 'लिंग 20 अक्षरों से अधिक नहीं हो सकता।',
      'validation.emailMaxLength': 'ईमेल 100 अक्षरों से अधिक नहीं हो सकता।',
      'validation.passwordRequired': 'पासवर्ड आवश्यक है।',
      'validation.passwordMinLength': 'पासवर्ड कम से कम 6 अक्षर का होना चाहिए।',
      'validation.confirmPasswordRequired': 'पासवर्ड की पुष्टि आवश्यक है।',
      'validation.passwordsMismatch': 'पासवर्ड मेल नहीं खाते।',

      // Buttons
      'button.login': 'लॉगिन',
      'button.sendOtp': 'OTP भेजें',
      'button.createAccount': 'नया खाता बनाएं',
      'button.register': 'पंजीकरण करें',
      'button.registering': 'पंजीकरण हो रहा है...',
      'button.backToLogin': 'लॉगिन पेज पर वापस जाएं',
      'button.submit': 'जमा करें',
      'button.cancel': 'रद्द करें',
      'button.back': 'वापस',
      'button.next': 'आगे',
      'button.previous': 'पिछला',
      'button.proceed': 'आगे बढ़ें',
      'button.saveAsDraft': 'ड्राफ्ट के रूप में सहेजें',

      // Success Messages
      'success.registrationComplete': 'पंजीकरण सफलतापूर्वक पूरा हुआ!',
      'success.otpSent': 'OTP सफलतापूर्वक भेजा गया!',
      'success.loginSuccess': 'लॉगिन सफल!',
      'success.informationSaved': 'जानकारी सफलतापूर्वक सहेजी गई',
      'success.documentUploaded': 'दस्तावेज़ सफलतापूर्वक अपलोड हो गया',
      'success.documentDeleted': 'दस्तावेज़ सफलतापूर्वक हटा दिया गया',
      'success.title': 'सफलता',

      // Error Messages
      'error.registrationFailed': 'पंजीकरण असफल। कृपया पुनः प्रयास करें।',
      'error.loginFailed': 'लॉगिन असफल। कृपया अपनी साख जांचें।',
      'error.otpInvalid': 'अमान्य OTP। कृपया पुनः प्रयास करें।',
      'error.networkError': 'नेटवर्क त्रुटि। कृपया अपना कनेक्शन जांचें।',
      'error.uploadFailed': 'दस्तावेज़ अपलोड करने में विफल। कृपया पुनः प्रयास करें।',
      'error.deleteFailed': 'दस्तावेज़ हटाने में विफल। कृपया पुनः प्रयास करें।',
      'error.title': 'त्रुटि',

      // Profile Summary Page
      'profile.title': 'सेवा प्रोफाइल',
      'profile.importantInfo': 'महत्वपूर्ण जानकारी',
      'profile.infoText': 'गुरुदेव सिद्ध पीठ में सेवा या रिट्रीट के लिए आवेदन करने के लिए, पूरी तरह से सेवा प्रोफाइल भरें और फिर संबंधित ध्यान फॉर्म भरें।',
      'profile.mustComplete': 'आपको ध्यान फॉर्म को अनलॉक करने के लिए अपना सेवा प्रोफाइल पूरा करना होगा।',
      'profile.progress': 'सेवा प्रोफाइल प्रगति',
      'profile.progressInstructions': 'प्रगति निर्देश',
      'profile.progressText': 'सेवा और रिट्रीट के लिए आवेदन करने के लिए ध्यान फॉर्म को अनलॉक करने के लिए 100% प्रगति तक पहुंचने के लिए अपना सेवा प्रोफाइल फॉर्म पूरी तरह भरें।',
      'profile.updateProfile': 'अपना सेवा प्रोफाइल अपडेट करें',
      'profile.complete': 'पूरा',
      'profile.pending': 'लंबित',
      'profile.myApplications': 'मेरे आवेदन',
      'profile.validTill': 'वैध तक',
      'profile.expiredOn': 'समाप्त हुआ',
      'profile.contemplationForms': 'सेवा और रिट्रीट के लिए ध्यान फॉर्म',
      'profile.completeProfileToUnlock': 'नीचे दिए गए ध्यान फॉर्म को अनलॉक करने के लिए अपना सेवा प्रोफाइल पूरा करें',
      'profile.fillContemplationForm': 'जिस सेवा या रिट्रीट के लिए आप आवेदन करना चाहते हैं, उसके लिए ध्यान फॉर्म भरें और जमा करें।',
      'profile.desc.visitingSeva': 'अल्पकालिक सेवा अवसरों के लिए आवेदन करें',
      'profile.desc.gurukulaSeva': 'आध्यात्मिक अध्ययन कार्यक्रम में छात्र के रूप में जुड़ें',
      'profile.desc.kypr': 'युवा वयस्कों के लिए बनाया गया आध्यात्मिक रिट्रीट',
      'profile.desc.sypr': 'परिवार-केन्द्रित आध्यात्मिक रिट्रीट अनुभव',
      'profile.desc.familyRetreat': 'परिवार-केन्द्रित आध्यात्मिक रिट्रीट अनुभव',
      'profile.desc.pth': 'गहन आध्यात्मिक यात्रा और रिट्रीट',
      'profile.desc.homeSeva': 'विशिष्ट परियोजनाओं के लिए घर से सेवा दें',
      'profile.childrenNote': '5 से 12 वर्ष की आयु के बच्चों को ध्यान फॉर्म भरने की आवश्यकता नहीं है।',
      'profile.needHelp': 'मदद चाहिए?',
      'profile.sevaProfile': 'सेवा प्रोफाइल',
      'profile.visitingSeva': 'विजिटिंग सेवा',
      'profile.gurukulaSeva': 'गुरुकुल सेवा',
      'profile.homeSeva': 'होम सेवा',
      'profile.familyRetreat': 'फैमिली रिट्रीट',
      'profile.pth': 'हृदय कि तीर्थयात्रा ',
      'profile.sypr': 'SYPR',
      'profile.kypr': 'KYPR',
      'profile.downloadTemplateInfo': 'ध्यान फॉर्म टेम्पलेट डाउनलोड करें, भरें और अपलोड करें',

      // Help Page
      'help.title': 'सहायता निर्देश',
      'help.documentTitle': 'सहायता दस्तावेज़',
      'help.introText': 'नीचे दिए गए चरण आपकी सहायता करेंगे कि पोर्टल का उपयोग कैसे करें और गुरुदेव सिद्ध पीठ में सेवा या रिट्रीट के लिए आवेदन कैसे करें। पूर्ण प्रक्रिया समझने हेतु वीडियो भी देखें।',
      'help.instructions': 'निर्देश',
      'help.instructionsSubtitle': 'नीचे दिए गए चरणों का पालन करके सेवा या रिट्रीट के लिए आवेदन करने की अपनी यात्रा पूरी करें।',
      'help.step1.title': 'चरण 1: अपना सेवा प्रोफ़ाइल भरें',
      'help.step1.desc1': 'अपनी व्यक्तिगत जानकारी दर्ज करके सेवा प्रोफ़ाइल फ़ॉर्म पूरा करें।',
      'help.step1.desc2': '\'Save as draft\' पर क्लिक करके आप अपनी जानकारी बीच में सेव कर सकते हैं और बाद में जारी रख सकते हैं।',
      'help.step1.desc3': 'चिंतन–मनन फ़ॉर्म केवल तब उपलब्ध होगा जब सेवा प्रोफ़ाइल 100% पूरा हो जाएगा। इसके बिना आवेदन अधूरा रहेगा।',
      'help.step2.title': 'चरण 2: आवेदन भरें और जमा करें',
      'help.step2.desc1': 'सेवा प्रोफ़ाइल पूरा करने के बाद संबंधित चिंतन–मनन फ़ॉर्म भरें।',
      'help.step2.desc2': 'उदाहरण: यदि आप विज़िटिंग सेवा के लिए आवेदन करना चाहते हैं, तो \'Visiting Seva\' पर क्लिक करें।',
      'help.step2.desc3': 'दोनों फ़ॉर्म पूरी तरह भरने के बाद "Submit" पर क्लिक करें।',
      'help.downloadTitle': 'डाउनलोड दस्तावेज़',
      'help.downloadText': 'विस्तृत मार्गदर्शन के लिए सहायता दस्तावेज़ डाउनलोड करें।',
      'help.downloadButton': 'सहायता दस्तावेज़ डाउनलोड करें',
      'help.videoTitle': 'वीडियो ट्यूटोरियल',
      'help.videoText': 'दृश्य मार्गदर्शन के लिए नीचे दिया गया वीडियो ट्यूटोरियल देखें।',

      // Seva Profile Component
      'sevaProfile.formTitle': 'सेवा प्रोफ़ाइल फ़ॉर्म',
      'sevaProfile.applicantLabel': 'आवेदक:',
      'sevaProfile.goBackHome': 'मुखपृष्ठ पर वापस जाएँ',

      // Personal Details Page (additional)
      'personalDetails.title': 'व्यक्तिगत विवरण',

      // Common (additional)
      'common.select': 'चुनें',
      'common.yes': 'हाँ',
      'common.no': 'नहीं',

      // Form Fields (additional)
      'form.age': 'आयु',
      'form.maritalStatus': 'वैवाहिक स्थिति',
      'form.beforeMarriageFullName': 'विवाह से पहले पूरा नाम',
      'form.isFirstStayQuestion': 'क्या गुरुदेव सिद्ध पीठ में आपका यह पहला प्रवास होगा?',
      'form.lastStayQuestion': 'यदि नहीं, तो आपका पिछला प्रवास कब था? (माह और वर्ष बताएं)',
      'form.middleNameVariants': "मध्य नाम / पिता का नाम / पति का नाम",

      // Placeholders (additional)
      'placeholder.firstName': 'अपना पहला नाम दर्ज करें',
      'placeholder.middleName': 'अपना मध्य नाम दर्ज करें',
      'placeholder.lastName': 'अपना अंतिम नाम दर्ज करें',
      'placeholder.dateOfBirth': 'जन्म तिथि चुनें',
      'placeholder.ageAuto': 'आयु स्वचालित रूप से गणना की जाएगी',
      'placeholder.selectMaritalStatus': 'वैवाहिक स्थिति चुनें',
      'placeholder.beforeMarriageFullName': 'विवाह से पहले पूरा नाम',
      'placeholder.selectGender': 'लिंग चुनें',
      'placeholder.enterGender': 'अपना लिंग दर्ज करें',
      'placeholder.lastStayMonthYear': 'पिछले प्रवास का माह और वर्ष चुनें',

      // Options (additional)
      'options.single': 'अविवाहित',
      'options.married': 'विवाहित',
      'options.other': 'अन्य',
      'options.male': 'पुरुष',
      'options.female': 'महिला',

      // Validation (additional)
      'validation.requiredField': 'यह फ़ील्ड अनिवार्य है।',
      'validation.middleNameRequired': 'मध्य नाव आवश्यक है।',
      'validation.beforeMarriageFullNameRequired': 'विवाह से पहले पूरा नाम आवश्यक है।',
      'validation.otherGenderRequired': 'अन्य लिंग आवश्यक है।',
      'validation.lastStayDateRequired': 'पिछले प्रवास का माह और वर्ष आवश्यक है।',

      // Health Insurance Page
      'healthInsurance.title': 'स्वास्थ्य बीमा',
      'healthInsurance.question.hasInsurance': 'क्या आपके पास ऐसा चिकित्सा बीमा है जो GSP में आपके प्रवास को कवर करेगा?',
      'healthInsurance.companyName': 'कंपनी का नाम',
      'healthInsurance.companyNamePlaceholder': 'कंपनी का नाम दर्ज करें',
      'healthInsurance.policyNumber': 'पॉलिसी नंबर',
      'healthInsurance.policyNumberPlaceholder': 'पॉलिसी नंबर दर्ज करें',
      'healthInsurance.validUntil': 'मान्य तिथि',
      'healthInsurance.validUntilPlaceholder': 'मान्य तिथि चुनें',
      'healthInsurance.question.financialSupport': 'यदि नहीं, तो क्या आप किसी चिकित्सा आपातकाल की स्थिति में खुद का वित्तीय समर्थन कर सकते हैं?',
      'healthInsurance.declaration': 'घोषणा: मैं hereby प्रमाणित करता/करती हूं कि ऊपर दी गई जानकारी सही और सत्य है।',
      'healthInsurance.declarationRequired': 'कृपया आगे बढ़ने के लिए घोषणा स्वीकार करें।',

      // Hospital & Allergies Page
      'hospital.title': 'अस्पताल में भर्ती और एलर्जी',
      'hospital.question.pastHospitalized': 'क्या आप पहले किसी अस्पताल में भर्ती होने के कारण वर्तमान में प्रभावित हैं?',
      'hospital.pleaseDescribe': 'कृपया विवरण दें',
      'hospital.enterDetails': 'विवरण दर्ज करें',
      'hospital.question.surgeryDone': 'क्या आपकी कोई शल्यक्रिया हुई है?',
      'hospital.immunizations.title': 'टीकाकरण: आपने निम्नलिखित टीकाकरण अंतिम बार लगभग किस वर्ष लिया?',
      'hospital.immunizations.tetanus': 'टे़टेनस टीकाकरण:',
      'hospital.immunizations.tb': 'क्षय रोग (BCG):',
      'hospital.immunizations.polio': 'पोलियो टीकाकरण:',
      'hospital.immunizations.other': 'अन्य:',
      'hospital.pickYear': 'वर्ष चुनें',
      'hospital.allergies.title': 'एलर्जी – क्या आपको निम्नलिखित में से किसी चीज़ से एलर्जी (या संवेदनशीलता) है?',
      'hospital.allergies.mould': 'फफूंदी एलर्जी:',
      'hospital.allergies.pollen': 'पराग एलर्जी:',
      'hospital.allergies.beeSting': 'मधुमक्खी के काटने की एलर्जी:',
      'hospital.medicineAllergies': 'आपको किन दवाओं से एलर्जी है? (नहीं होने पर "none" लिखें)',
      'hospital.enterMedicineAllergies': 'दवा एलर्जी दर्ज करें या "none" लिखें',
      'hospital.foodAllergies': 'कौन से खाद्य पदार्थों से आप संवेदनशील या एलर्जिक हैं? (नहीं होने पर "none" लिखें)',
      'hospital.enterFoodAllergies': 'खाद्य एलर्जी दर्ज करें या "none" लिखें',
      'hospital.otherAllergies': 'कोई अन्य एलर्जी? कृपया सूची दें:',
      'hospital.enterOtherAllergies': 'अन्य एलर्जी दर्ज करें',

      // Language Proficiency Page
      'language.title': 'भाषा प्रवीणता',
      'language.languageName': 'भाषा का नाम',
      'language.speaking': 'बोलना',
      'language.reading': 'पढ़ना',
      'language.writing': 'लिखना',
      'language.selectLanguage': 'भाषा चुनें',
      'language.select': 'चुनें',
      'language.addNewLanguage': 'नई भाषा जोड़ें',
      'language.delete': 'हटाएं',
      'language.proficiencyA': 'A: 75 – 100%',
      'language.proficiencyB': 'B: 50 – 75%',
      'language.proficiencyC': 'C: 0 - 50%',
      'language.proficiencyD': 'D: None',
      'validation.languageNameRequired': 'भाषा का नाम आवश्यक है।',
      'validation.speakingRequired': 'बोलने की प्रवीणता आवश्यक है।',
      'validation.readingRequired': 'पढ़ने की प्रवीणता आवश्यक है।',
      'validation.writingRequired': 'लिखने की प्रवीणता आवश्यक है।',
      'validation.completeLastRow': 'नई भाषा जोड़ने से पहले कृपया पिछली भाषा पंक्ति को पूरा करें।',
      'validation.hindiEnglishRequired': 'हिंदी और अंग्रेजी भाषाएं आवश्यक हैं और हटाई नहीं जा सकतीं।',
      'validation.minimumLanguagesRequired': 'कम से कम 2 भाषा प्रविष्टियां (हिंदी और अंग्रेजी) आवश्यक हैं।',
      'validation.completeAllFields': 'सबमिट करने से पहले कृपया सभी आवश्यक फ़ील्ड को पूरा करें।',

      // Current Address Page
      'address.title': 'वर्तमान निवासी पता',
      'address.streetAddress': 'घर का नाम, नंबर और सड़क का पता दर्ज करें:',
      'address.townCity': 'शहर/नगर:',
      'address.district': 'जिला:',
      'address.state': 'राज्य:',
      'address.pinCode': 'पिन कोड:',
      'address.mobilePersonal': 'मोबाइल (व्यक्तिगत):',
      'address.mobileAlternate': 'मोबाइल (वैकल्पिक नंबर):',
      'address.email': 'ईमेल:',
      'address.selectState': 'राज्य चुनें',
      'address.selectCountry': 'चुनें',
      'address.enterStreetAddress': 'सड़क का पता दर्ज करें',
      'address.enterTownCity': 'शहर/नगर दर्ज करें',
      'address.enterDistrict': 'जिला दर्ज करें',
      'address.enterPinCode': 'पिन कोड दर्ज करें',
      'address.enterHomePhone': 'घर का फोन दर्ज करें',
      'address.enterWorkPhone': 'कार्यालय का फोन दर्ज करें',
      'address.enterEmail': 'ईमेल दर्ज करें',
      'validation.streetAddressRequired': 'सड़क का पता आवश्यक है।',
      'validation.townCityRequired': 'शहर/नगर आवश्यक है।',
      'validation.districtRequired': 'जिला आवश्यक है।',
      'validation.stateRequired': 'राज्य आवश्यक है।',
      'validation.pinCodeRequired': 'पिन कोड आवश्यक है और 6 अंकों का होना चाहिए।',

      // Permanent Address Page
      'permanentAddress.title': 'स्थायी निवासी पता',
      'permanentAddress.sameAsCurrent': 'स्थायी निवासी पता',
      'permanentAddress.isSameAsCurrent': 'वर्तमान निवासी पते के समान है',
      'permanentAddress.checkboxTooltip': 'यदि आपका स्थायी पता आपके वर्तमान निवासी पते से मेल खाता है तो इस बॉक्स को चेक करें',

      // Emergency Contact Page
      'emergencyContact.title': 'आपातकालीन संपर्क जानकारी',
      'emergencyContact.contactName': 'आपातकालीन संपर्क का नाम:',
      'emergencyContact.relationship': 'आपातकालीन संपर्क का रिश्ता:',
      'emergencyContact.selectRelationship': '-- रिश्ता चुनें --',
      'emergencyContact.enterContactName': 'आपातकालीन संपर्क का नाम',
      'emergencyContact.enterEmergencyMobile': 'आपातकालीन मोबाइल दर्ज करें',
      'emergencyContact.enterWorkPhone': 'कार्यालय का फोन दर्ज करें',
      'emergencyContact.enterEmergencyEmail': 'आपातकालीन ईमेल दर्ज करें',
      'validation.contactNameRequired': 'आपातकालीन संपर्क का नाम आवश्यक है।',
      'validation.relationshipRequired': 'रिश्ता आवश्यक है।',

      // Study Participation Information Page
      'studyParticipation.title': 'सिद्धयोग भागीदारी',
      'studyParticipation.beginPracticeYear': 'आपने किस वर्ष सिद्धयोग का अभ्यास शुरू किया था?',
      'studyParticipation.selectPracticeYear': 'अभ्यास वर्ष चुनें',
      'studyParticipation.whereBeginPractice': 'आपने अपना अभ्यास कहाँ शुरू किया था?',
      'studyParticipation.selectOption': 'एक विकल्प चुनें',
      'studyParticipation.siddhaYogaCentre': 'सिद्धयोग केंद्र',
      'studyParticipation.home': 'घर',
      'studyParticipation.other': 'अन्य',
      'studyParticipation.ifOtherMention': 'यदि अन्य, तो सिद्धयोग केंद्र और शहर का नाम बताएं',
      'studyParticipation.enterCentreName': 'केंद्र का नाम और शहर दर्ज करें',
      'studyParticipation.howOftenVisit': 'आप अपने सिद्धयोग केंद्र में कितनी बार जाते हैं?',
      'studyParticipation.selectFrequency': 'आवृत्ति चुनें',
      'studyParticipation.weekly': 'साप्ताहिक',
      'studyParticipation.monthly': 'मासिक',
      'studyParticipation.occasionally': 'कभी-कभी',
      'studyParticipation.never': 'कभी नहीं',
      'studyParticipation.whichCentreVisit': 'आप कौन से सिद्धयोग केंद्र में जाते हैं?',
      'studyParticipation.state': 'राज्य',
      'studyParticipation.district': 'जिला',
      'studyParticipation.centre': 'केंद्र',
      'studyParticipation.selectState': 'राज्य चुनें',
      'studyParticipation.selectDistrict': 'जिला चुनें',
      'studyParticipation.selectCentre': 'केंद्र चुनें',
      'studyParticipation.enterCentrePlaceholder': 'कृपया केंद्र दर्ज करें',
      'studyParticipation.whichPracticeEngage': 'आप नियमित रूप से किस सिद्धयोग अभ्यास में संलग्न हैं?',
      'studyParticipation.selectPractice': 'अभ्यास चुनें',
      'studyParticipation.none': 'कोई नहीं',
      'studyParticipation.meditation': 'ध्यान',
      'studyParticipation.chanting': 'जप',
      'studyParticipation.yogaAsanas': 'योग आसन',
      'studyParticipation.studyScriptures': 'शास्त्रों का अध्ययन',
      'studyParticipation.seva': 'सेवा',
      'studyParticipation.otherPractice': 'अन्य',
      'studyParticipation.regularlyEngageTeachings': 'क्या आप नियमित रूप से सिद्धयोग शिक्षाओं के अध्ययन में संलग्न हैं?',
      'studyParticipation.yes': 'हाँ',
      'studyParticipation.no': 'नहीं',
      'studyParticipation.ifYesSelected': 'यदि \'हाँ\' चुना गया है, तो विकल्पों के लिए बहुविकल्पीय चयन',
      'studyParticipation.selectOptions': 'विकल्प चुनें',
      'studyParticipation.siddhaYogaPathWebsite': 'सिद्धयोग पथ वेबसाइट',
      'studyParticipation.siddhaYogaBooks': 'सिद्धयोग पुस्तकें',
      'studyParticipation.homeStudyCourse': 'घर अध्ययन पाठ्यक्रम',
      'studyParticipation.neeleshwari': 'नीलेश्वरी',
      'studyParticipation.howOftenVisitWebsite': 'आप अपनी सिद्धयोग पथ वेबसाइट पर कितनी बार जाते हैं?',
      'studyParticipation.daily': 'दैनिक',
      'studyParticipation.participateSadhanaCircle': 'क्या आप साधना चक्र में भाग ले रहे हैं?',
      'studyParticipation.howOften': 'कितनी बार?',
      'studyParticipation.firstShaktipatIntensive': 'आपने अपना पहला शक्तिपात इंटेंसिव किस वर्ष लिया था?',
      'studyParticipation.selectFirstIntensiveYear': 'पहला इंटेंसिव वर्ष चुनें',
      'studyParticipation.eventParticipationHistory': 'कार्यक्रम भागीदारी इतिहास (पिछले 5 वर्ष)',
      'studyParticipation.eventParticipationHistorySubHeader': 'नीचे दिए गए कार्यक्रमों के लिए, उन वर्षों की सूची बनाएं जिनमें आपने पिछले पांच वर्षों में भाग लिया था।',
      'studyParticipation.shaktipatIntensive': 'शक्तिपात इंटेंसिव',
      'studyParticipation.audioSatsangs': 'ऑडियो सत्संग',
      'studyParticipation.liveStreams': 'लाइव स्ट्रीम',
      'studyParticipation.muktanandaDhyanSaptah': 'मुक्तानंद ध्यान सप्ताह',
      'studyParticipation.sweetSurpriseSatsang': 'स्वीट सरप्राइज सत्संग',
      'studyParticipation.retreatHistory': 'रिट्रीट इतिहास',
      'studyParticipation.retreatName': 'रिट्रीट का नाम',
      'studyParticipation.selectRetreatName': 'रिट्रीट का नाम चुनें',
      'studyParticipation.retreatDate': 'रिट्रीट की तारीख',
      'studyParticipation.selectDate': 'तारीख चुनें',
      'studyParticipation.deleteRetreat': 'रिट्रीट हटाएं',
      'studyParticipation.addNewRetreat': 'नया रिट्रीट जोड़ें',
      'studyParticipation.sevaHistory': 'सेवा इतिहास',
      'studyParticipation.ashramName': 'आश्रम का नाम',
      'studyParticipation.selectAshramName': 'आश्रम का नाम चुनें',
      'studyParticipation.sevaRole': 'सेवा भूमिका',
      'studyParticipation.department': 'विभाग',
      'studyParticipation.year': 'वर्ष',
      'studyParticipation.duration': 'अवधि',
      'studyParticipation.supervisorName': 'पर्यवेक्षक का नाम',
      'studyParticipation.deleteSeva': 'सेवा हटाएं',
      'studyParticipation.addNewSeva': 'नई सेवा जोड़ें',
      'validation.beginPracticeYearRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.practiceBeginDetailsRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.otherCentreRequired': '\'अन्य\' चुने जाने पर यह फ़ील्ड आवश्यक है।',
      'validation.centerVisitFrequencyRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.currentSYPracticesRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.isEngagedWithTeachingsRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.isParticipateInSadhanaCircleRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.firstIntensiveYearRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.retreatNameRequired': 'रिट्रीट का नाम आवश्यक है।',
      'validation.retreatDateRequired': 'रिट्रीट की तारीख आवश्यक है।',
      'validation.fillAllFieldsBeforeAdding': 'नया रिट्रीट जोडण्यापूर्वी कृपया सभी आवश्यक फ़ील्ड भरें।',
      'validation.atLeastOneRetreatRequired': 'कम से कम 1 रिट्रीट इतिहास प्रविष्टि आवश्यक है।',
      'validation.fillAllFieldsBeforeAddingSeva': 'नई सेवा जोड़ने से पहले कृपया सभी आवश्यक फ़ील्ड भरें।',
      'validation.atLeastOneSevaRequired': 'कम से कम 1 सेवा इतिहास प्रविष्टि आवश्यक है।',

      // Other Personal Information Page
      'otherPersonal.title': 'अन्य व्यक्तिगत जानकारी',
      'otherPersonal.involvedOtherSpiritual': 'क्या आप कभी किसी अन्य आध्यात्मिक संगठनों से जुड़े रहे हैं?',
      'otherPersonal.whichOnesWhen': 'कौन से और कब?',
      'otherPersonal.enterOrganisationDetails': 'संगठन विवरण दर्ज करें',
      'otherPersonal.askedToLeaveAshram': 'क्या आपसे कभी सिद्धयोग आश्रम या केंद्र छोड़ने के लिए कहा गया है?',
      'otherPersonal.fromWhereWhenReason': 'कहाँ से, कब और किस कारण से?',
      'otherPersonal.enterReason': 'कारण दर्ज करें',
      'otherPersonal.criminalOffence': 'क्या आप कभी मामूली यातायात उल्लंघन के अलावा किसी आपराधिक अपराध के लिए दोषी ठहराए गए हैं?',
      'otherPersonal.pleaseDescribe': 'कृपया वर्णन करें:',
      'otherPersonal.enterDetails': 'विवरण दर्ज करें',
      'otherPersonal.drugHistory': 'क्या आपके पास पिछले पांच वर्षों में अवैध नशीली दवाओं के उपयोग और/या उपचार का कोई इतिहास है?',
      'validation.involvedOtherSpiritualRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.otherOrganisationDetailsRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.askedToLeaveAshramRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.reasonForAskToLeaveRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.criminalOffenceRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.offenceDetailsRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.drugHistoryRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.drugHistoryDetailsRequired': 'यह फ़ील्ड आवश्यक है।',

      // References Page
      'references.title': 'संदर्भ',
      'references.instructionText': 'कृपया सेवा पर्यवेक्षकों, साथी सिद्ध योगियों, या केंद्र नेतृत्व से संदर्भ प्रदान करें। कृपया अपने परिवार के सदस्य को सूचीबद्ध न करें।',
      'references.reference1Name': 'संदर्भ 1 - नाम:',
      'references.reference2Name': 'संदर्भ 2 - नाम:',
      'references.emailAddress': 'ई-मेल पता:',
      'references.phoneNumber': 'फोन नंबर:',
      'references.relationship': 'रिश्ता:',
      'references.selectRelationship': '-- रिश्ता चुनें --',
      'references.sevaSupervisor': 'सेवा पर्यवेक्षक',
      'references.fellowSiddhaYogi': 'साथी सिद्ध योगी',
      'references.centreLeadership': 'केंद्र नेतृत्व',
      'references.enterName': 'नाम दर्ज करें',
      'references.enterEmail': 'ई-मेल दर्ज करें',
      'references.enterPhoneNumber': 'फोन नंबर दर्ज करें',
      'references.consentText': 'मैं उपरोक्त संदर्भों से संपर्क करने की सहमति देता हूं।',
      'validation.reference1NameRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.reference1EmailRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.reference1EmailInvalid': 'कृपया एक वैध ई-मेल पता दर्ज करें।',
      'validation.reference1PhoneRequired': 'मोबाइल आवश्यक है और 10 अंकों का होना चाहिए।',
      'validation.reference1RelationshipRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.reference2NameRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.reference2EmailRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.reference2PhoneRequired': 'मोबाइल आवश्यक है आणि 10 अंकांचा असावा.',
      'validation.reference2RelationshipRequired': 'यह फ़ील्ड आवश्यक आहे.',
      'validation.consentRequired': 'आगे बढ़ने के लिए कृपया घोषणा को स्वीकार करें।',

      // Medical History Page
      'medicalHistory.title': 'चिकित्सा इतिहास',
      'medicalHistory.height': 'ऊंचाई',
      'medicalHistory.weight': 'वजन (किलो)',
      'medicalHistory.enterHeight': 'ऊंचाई दर्ज करें',
      'medicalHistory.select': 'चुनें',
      'medicalHistory.bloodPressure': 'रक्तचाप:',
      'medicalHistory.bloodSugarDiabetes': 'रक्त शर्करा या मधुमेह:',
      'medicalHistory.brainStroke': 'मस्तिष्क आघात:',
      'medicalHistory.heartDisease': 'हृदय रोग:',
      'medicalHistory.tuberculosis': 'क्षय रोग (टीबी):',
      'medicalHistory.asthma': 'दमा:',
      'medicalHistory.cancer': 'कैंसर:',
      'medicalHistory.hivAids': 'एचआईवी / एड्स:',
      'medicalHistory.respiratoryLungProblem': 'श्वसन / फेफड़े की समस्या:',
      'medicalHistory.visionHearingSpeechProblem': 'दृष्टि, श्रवण या वाक् से संबंधित समस्या:',
      'medicalHistory.depressionPsychological': 'क्या आपको कभी अवसाद या अन्य मनोवैज्ञानिक स्थिति का निदान किया गया है?:',
      'medicalHistory.otherMedicalCondition': 'ऊपर उल्लिखित के अलावा कोई अन्य चिकित्सा स्थिति?',
      'medicalHistory.typhoidFever': 'टाइफाइड बुखार?',
      'medicalHistory.psychiatricDisorder': 'मनोरोग विकार?',
      'medicalHistory.parasites': 'परजीवी?',
      'medicalHistory.hepatitis': 'हेपेटाइटिस ए, बी, या सी या पीलिया?',
      'medicalHistory.regularMedication': 'क्या आपको नियमित रूप से दवाएं लेने की आवश्यकता है?',
      'medicalHistory.provideCurrentReading': 'वर्तमान रीडिंग प्रदान करें',
      'medicalHistory.describeCurrentCondition': 'वर्तमान स्थिति का वर्णन करें',
      'medicalHistory.whenTreatmentComplete': 'उपचार कब पूरा हुआ?',
      'medicalHistory.specify': 'निर्दिष्ट करें:',
      'medicalHistory.pleaseDescribe': 'कृपया वर्णन करें',
      'medicalHistory.ifYesWhen': 'यदि हां, तो कब?',
      'medicalHistory.ifYesWhenWhatKind': 'यदि हां, तो कब और किस प्रकार का?',
      'medicalHistory.ifYesWhenWhichOne': 'यदि हां, तो कब और कौन सा?',
      'medicalHistory.pleaseSpecifyMedicine': 'कृपया दवा के नाम और खुराक निर्दिष्ट करें:',
      'medicalHistory.enterBpRange': 'बीपी रेंज दर्ज करें',
      'medicalHistory.enterBloodSugarRange': 'रक्त शर्करा रेंज दर्ज करें',
      'medicalHistory.enterDetails': 'विवरण दर्ज करें',
      'validation.heightRequired': 'ऊंचाई आवश्यक है।',
      'validation.weightRequired': 'वजन आवश्यक है।',
      'validation.bloodPressureRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.bpRangeRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.bloodSugarRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.bloodSugarRangeRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.brainStrokeRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.brainStrokeDetailsRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.heartProblemRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.heartProblemDetailsRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.tuberculosisRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.tuberculosisDetailsRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.asthmaRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.asthmaDetailsRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.cancerRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.cancerDetailsRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.hivRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.hivConditionDetailsRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.lungProblemRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.lungProblemDetailsRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.visionProblemRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.visionProblemDetailsRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.depressionRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.depressionDetailsRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.otherMedicalConditionRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.otherMedicalConditionDetailsRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.typhoidFeverRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.typhoidFeverDetailsRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.psychiatricDisorderRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.psychiatricDisorderDetailsRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.parasitesRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.parasitesDetailsRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.hepatitisRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.hepatitisDetailsRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.regularMedicationRequired': 'यह फ़ील्ड आवश्यक है।',
      'validation.medicationsRequired': 'यह फ़ील्ड आवश्यक है।',

      // Educational and Work Experience Page
      'educationWork.title': 'शैक्षिक और कार्य अनुभव',
      'educationWork.educationalBackground': 'शैक्षिक पृष्ठभूमि',
      'educationWork.workExperience': 'कार्य अनुभव',
      'educationWork.srNo': 'क्र. सं.',
      'educationWork.degreeDiploma': 'डिग्री / डिप्लोमा',
      'educationWork.fieldOfStudySpecialization': 'अध्ययन का क्षेत्र / विशेषज्ञता',
      'educationWork.university': 'विश्वविद्यालय',
      'educationWork.yearOfCompletion': 'पूर्णता का वर्ष',
      'educationWork.action': 'कार्य',
      'educationWork.nameOfCompany': 'कंपनी का नाम',
      'educationWork.designationJobTitle': 'पदनाम / नौकरी का शीर्षक',
      'educationWork.fromToYear': 'से - तक (वर्ष)',
      'educationWork.fieldTrade': 'क्षेत्र / व्यापार',
      'educationWork.educationNumber': 'शिक्षा #',
      'educationWork.workExperienceNumber': 'कार्य अनुभव #',
      'educationWork.enterDegreeDiploma': 'Enter Degree/Diploma',
      'educationWork.enterFieldOfStudy': 'Enter field of study',
      'educationWork.enterUniversity': 'Enter University',
      'educationWork.enterCompanyName': 'Enter company name',
      'educationWork.enterJobDesignation': 'Enter Job Designation',
      'educationWork.from': 'From (Year)',
      'educationWork.to': 'To (Year)',
      'educationWork.fieldTradeExample': 'E.g. Sales, IT, HR, Finance',
      'educationWork.removeEducation': 'Remove Education',
      'educationWork.removeWorkExperience': 'Remove Work Experience',
      'educationWork.addEducation': 'Add Education',
      'educationWork.addWorkExperience': 'Add Work Experience',
      'educationWork.atLeastOneEducationRequired': 'At least one educational record must be completed with all required fields.',
      'educationWork.atLeastOneWorkRequired': 'At least one work experience record must be completed with all required fields.',
      'validation.degreeDiplomaRequired': 'Degree/Diploma is required',
      'validation.degreeDiplomaMaxLength': 'Degree/Diploma cannot exceed 50 characters',
      'validation.fieldOfStudyRequired': 'Field of Study / Specialization is required',
      'validation.fieldOfStudyMaxLength': 'Field of Study / Specialization cannot exceed 50 characters',
      'validation.universityRequired': 'University is required',
      'validation.universityMaxLength': 'University cannot exceed 50 characters',
      'validation.yearRequired': 'Year is required',
      'validation.yearInvalidLength': 'Year must be exactly 4 digits',
      'validation.yearInvalidFormat': 'Year must contain only numbers',
      'validation.yearFuture': 'Year cannot be greater than current year',
      'validation.companyNameRequired': 'Company Name is required',
      'validation.companyNameMaxLength': 'Company Name cannot exceed 50 characters',
      'validation.designationRequired': 'Designation / Job Title is required',
      'validation.designationMaxLength': 'Designation / Job Title cannot exceed 50 characters',
      'validation.fromYearRequired': 'From year is required',
      'validation.fromYearInvalidLength': 'From year must be exactly 4 digits',
      'validation.fromYearInvalidFormat': 'From year must contain only numbers',
      'validation.fromYearFuture': 'From year cannot be greater than current year',
      'validation.toYearRequired': 'To year is required',
      'validation.toYearInvalidLength': 'To year must be exactly 4 digits',
      'validation.toYearInvalidFormat': 'To year must contain only numbers',
      'validation.toYearFuture': 'To year cannot be greater than current year',
      'validation.yearsFuture': 'Years cannot be greater than current year',
      'validation.invalidYearRange': 'From year must be less than To year',
      'validation.fieldTradeRequired': 'Field / Trade is required',
      'validation.fieldTradeMaxLength': 'Field / Trade cannot exceed 50 characters'
      ,

      // Physical Ability Page
      'physicalAbility.title': 'शारीरिक क्षमता',
      'physicalAbility.neckOrShouldersQuestion': 'क्या आपकी गर्दन या कंधों में दर्द या सीमितता है?',
      'physicalAbility.backQuestion': 'क्या आपकी पीठ में दर्द या सीमितता है?',
      'physicalAbility.kneesQuestion': 'क्या आपके घुटनों में दर्द या सीमितता है?',
      'physicalAbility.hipsQuestion': 'क्या आपके कूल्हों में दर्द या सीमितता है?',
      'physicalAbility.feetQuestion': 'क्या आपके पैरों में दर्द या सीमितता है?',
      'physicalAbility.handsWristsArmsQuestion': 'क्या आपके हाथों, कलाई या बाहों में दर्द या सीमितता है?',
      'physicalAbility.preventedActivitiesQuestion': 'आप किन गतिविधियों को करने से रोके जाते हैं?',
      'physicalAbility.repetitiveStrainInjuryQuestion': 'क्या आपको रिपेटिटिव स्ट्रेन इंजरी हुई है?',
      'physicalAbility.whenAndHowLong': 'कब और कितने समय के लिए?',
      'physicalAbility.affectedAreas': 'Details of affected areas?',

      // Generic placeholder (used across pages)
      'placeholder.enterDetails': 'Enter Details',

      // Physical Fitness Page
      'physicalFitness.title': 'शारीरिक फिटनेस',
      'physicalFitness.canClimbStairs': 'Can you climb 3 flights of stairs easily?',
      'physicalFitness.why': 'Why?',
      'physicalFitness.canWalkDistance': 'Can you walk 1.5 km or 1 mile without a problem?',
      'physicalFitness.canLiftWeight': 'Can you lift 4 kg (10 lbs) without a problem?',
      'physicalFitness.canStandDuration': 'Can you stand for 30 minutes without a problem?',
      'physicalFitness.canSitDuration': 'Can you sit for 30 minutes without a problem?',
      'physicalFitness.canBendOver': 'Can you bend over without a problem?',
      'physicalFitness.ifNoWhy': 'If no, Why?',
      'physicalFitness.doExerciseRegularly': 'Do you exercise regularly?',
      'physicalFitness.exerciseType': 'What type of exercise do you do?',
      'physicalFitness.exerciseFrequency': 'How often do you exercise?',
      'physicalFitness.overallHealth': 'How is your overall health?',
      'physicalFitness.healthPoor': 'Poor',
      'physicalFitness.healthGood': 'Good',
      'physicalFitness.healthExcellent': 'Excellent',
      'physicalFitness.healthComments': 'Please tell us anything that would help us know about your health and physical condition:',
      'physicalFitness.forFemalePregnancy': 'For Female Students: Are you pregnant?',
      'physicalFitness.pregnancyDueDate': 'If yes, please state your due date:',
      'placeholder.enterReason': 'Enter Reason',
      'placeholder.enterExerciseType': 'Enter Exercise Type',
      'placeholder.enterExerciseFrequency': 'Enter Exercise Frequency (e.g., Daily, Weekly)',
      'placeholder.enterComments': 'Enter Comments',
      'placeholder.enterDueDate': 'Enter Due Date'
      ,

      // Documents Page
      'documents.title': 'दस्तावेज़ अपलोड करें',
      'documents.guidelines.photo.header': 'मानक निर्देश: पासपोर्ट साइज फोटो',
      'documents.guidelines.photo.li1': 'फोटो पासपोर्ट में लगने वाली फोटो के समान होनी चाहिए।',
      'documents.guidelines.photo.li2': 'अपलोड की गई फोटो JPEG या PNG फ़ॉर्मेट में होनी चाहिए।',
      'documents.guidelines.photo.li3': 'फोटो की फ़ाइल का आकार 1 MB से कम होना चाहिए।',
      'documents.guidelines.photo.li4': 'फोटो का रेज़ॉल्यूशन अच्छा होना आवश्यक है।',
      'documents.guidelines.photo.li5': 'Recheck the content of the photo before uploading.',
      'documents.guidelines.id.header': 'मानक निर्देश: पहचान पत्र (ID)',
      'documents.guidelines.id.li1': 'कृपया अपना आधार कार्ड या पासपोर्ट पहचान-पत्र अपलोड करें।',
      'documents.guidelines.id.li2': 'अपलोड किया गया दस्तावेज़ PDF, JPEG या PNG फ़ॉर्मेट में होना चाहिए।',
      'documents.guidelines.id.li3': 'दस्तावेज़ की फ़ाइल का आकार 1 MB से कम होना चाहिए।',
      'documents.guidelines.id.li4': 'Recheck before uploading to ensure it is the correct document.',
      'documents.fileInfo.title': 'File Upload Guidelines:',
      'documents.fileInfo.li1': 'Passport Size Photo: JPEG or PNG format only, max 5 MB',
      'documents.fileInfo.li2': 'All Other Documents: PDF, JPEG, or PNG format, max 5 MB',
      'documents.fileInfo.li3': 'File size limit applies to all document types',
      'documents.table.srNo': 'Sr No',
      'documents.table.documentName': 'Document Name',
      'documents.table.fileName': 'File Name',
      'documents.table.action': 'Action',
      'documents.table.infoTitle': 'File Restrictions:',
      'documents.table.infoText': 'Passport Photo: JPEG/PNG only | All Documents: Max 5MB | Formats: PDF, JPEG, PNG',
      'documents.section.mandatory': 'Mandatory Documents',
      'documents.section.nonMandatory': 'Non-Mandatory Documents',
      'documents.doc.passportPhoto': 'Passport size photograph',
      'documents.doc.aadharCard': 'ID Proof - Aadhaar Card or Passport',
      'documents.doc.photoIdProof': 'Photo ID Proof',
      'documents.doc.panCard': 'PAN Card',
      'documents.doc.resume': 'Resume',
      'documents.doc.sevaProfileDoc': 'Seva Profile Document',
      'documents.doc.vsDocument': 'VS Document',
      'documents.doc.other': 'Other',
      'documents.noFile': 'No file uploaded',
      'documents.addFileTooltip.photo': 'Add File (JPEG/PNG only, max 5MB)',
      'documents.addFileTooltip.default': 'Add File (PDF/JPEG/PNG, max 5MB)',
      'documents.deleteFileTooltip': 'Delete File',
      'documents.requiredMark': '* Required',
      'documents.validation.passportRequired': 'Passport size photograph is required. Please upload before proceeding.',
      'documents.validation.aadharRequired': 'Aadhaar card is required. Please upload before proceeding.',
      'documents.uploading': 'फाइल अपलोड हो रही है...',
      'documents.uploadingTitle': 'अपलोड हो रहा है',
      'documents.deleting': 'फाइल हटाई जा रही है...',
      'documents.deletingTitle': 'हटाया जा रहा है',
      'documents.deleteConfirm': 'क्या आप वाकई इस दस्तावेज़ को हटाना चाहते हैं?',

      // Agreement Page (Hindi)
      'agreement.title': 'आवेदक समझौते, प्रतिबद्धताएं और स्वीकृतियां',
      'agreement.acknowledgement.title': 'गुरुदेव सिद्ध पीठ (आश्रम) की यात्रा के अवसर पर विचार करते हुए, मैं स्वीकार करता हूं',
      'agreement.declaration': 'घोषणा: मैं यहां प्रमाणित करता हूं कि ऊपर दी गई जानकारी सही और सत्य है।',
      'agreement.button.proceed': 'आगे बढ़ें',
      'agreement.button.submit': 'जमा करें',
      'agreement.clause1': 'इस आवेदन पर सभी जानकारी सही और पूर्ण है। मैं समझता हूं कि यदि मैं कोई गलत जानकारी देता हूं या पूछी गई किसी भी जानकारी को छुपाता हूं या गलत तरीके से प्रस्तुत करता हूं, तो मेरा आवेदन अस्वीकार किया जा सकता है। यदि मेरे आवेदन के स्वीकार होने के बाद या मैंने जिस पद के लिए स्वीकार किया गया है उस पर काम शुरू करने के बाद कोई गलत जानकारी, चूक, या गलत प्रस्तुतीकरण खोजा जाता है, तो मैं समझता हूं कि मेरी स्वीकृति रद्द की जा सकती है या मेरी गुरुकुल छात्र स्थिति समाप्त की जा सकती है।',
      'agreement.clause2': 'यदि इस आवेदन पर कोई भी जानकारी बदलती है, तो मैं तुरंत गुरुदेव सिद्ध पीठ गुरुकुल आवेदनों को सूचित करूंगा।',
      'agreement.clause3': 'मैं समझता हूं कि गुरुदेव सिद्ध पीठ किसी भी कारण से गुरुदेव सिद्ध पीठ में मेरे रहने की अवधि को बदलने का अधिकार सुरक्षित रखता है।',
      'agreement.clause4': 'मैं समझता हूं कि यह आवेदन कोई समझौता या अनुबंध नहीं है। यह गुरुदेव सिद्ध पीठ के लिए गुरुकुल छात्र और/या रिट्रीट प्रतिभागी के रूप में स्वीकृति के लिए विचार किए जाने के लिए एक आवेदन है। मैं समझता हूं कि मेरे द्वारा इस आवेदन की प्राप्ति और/या प्रस्तुति यह आश्वासन नहीं है कि गुरुदेव सिद्ध पीठ मुझे गुरुकुल छात्र और/या रिट्रीट प्रतिभागी के रूप में स्वीकार करेगा और मैं स्वीकार करता हूं कि इस संबंध में मुझसे कोई वादे नहीं किए गए हैं। मैं यह भी स्वीकार करता हूं कि यदि मुझे गुरुकुल छात्र और/या रिट्रीट प्रतिभागी के रूप में स्वीकार किया जाता है, तो आश्रम में मेरा रहना किसी भी समय मेरे या गुरुदेव सिद्ध पीठ द्वारा समाप्त किया जा सकता है, और कोई स्पष्ट या निहित वादे या प्रतिनिधित्व नहीं किए गए हैं, सिवाय इस आवेदन में विशेष रूप से निर्धारित के। यह आवेदन गुरुदेव सिद्ध पीठ की संपत्ति रहेगा।',
      'agreement.clause5': 'मैं गुरुदेव सिद्ध पीठ के प्रतिनिधियों को मेरे पृष्ठभूमि, पिछले कार्य अनुभव और शिक्षा की जांच करने और मेरे संदर्भों और पिछले नियोक्ताओं से संपर्क करने के लिए मेरे द्वारा प्रदान की गई जानकारी की पुष्टि करने और मेरे आवेदन से संबंधित मेरे बारे में अन्य जानकारी प्राप्त करने के लिए अधिकृत करता हूं। मैं किसी भी व्यक्ति या संगठन को ऐसी जांच से संबंधित गुरुदेव सिद्ध पीठ को मेरे बारे में जानकारी प्रदान करने के लिए अधिकृत करता हूं और इस जानकारी को प्रदान करने के लिए किसी भी व्यक्ति या संगठन को सभी दायित्व से मुक्त करता हूं।',
      'agreement.clause6': 'मैं समझता हूं कि मेरे आवेदन से संबंधित सभी निर्णय गुरुदेव सिद्ध पीठ द्वारा किए जाते हैं।',
      'agreement.clause7': 'मैं स्वीकार करता हूं कि मैंने इस आवेदन को पढ़ा है और पूरी तरह से समझा है जिसमें पृष्ठभूमि जांच के लिए उपरोक्त बयान और प्राधिकरण शामिल हैं। मैं सहमत हूं कि इस आवेदन को इलेक्ट्रॉनिक रूप से प्रस्तुत करके यह प्रमाणपत्र मूल के रूप में बाध्यकारी और प्रभावी होगा। मैं यह भी सहमत हूं कि इस प्रमाणपत्र की स्कैन की गई या फैक्स हस्ताक्षरित प्रति मूल के रूप में बाध्यकारी और प्रभावी होगी।',
      'agreement.clause8': 'मैं स्वीकार करता हूं कि गुरुदेव सिद्ध पीठ सिद्ध योग ध्यान का अभ्यास करने वाले व्यक्तियों को चुनने का अधिकार सुरक्षित रखता है, उनकी सिद्ध योग पृष्ठभूमि के साथ-साथ अन्य कौशल और योग्यताओं के आधार पर।',
      'agreement.clause9': 'मैंने उपरोक्त बयानों को पढ़ा है, समझा है और उनसे सहमत हूं।',
      'agreement.acknowledgement.clause1': 'मैं समझता हूं और स्वीकार करता हूं कि गुरुदेव सिद्ध पीठ मुझे आश्रम में रहने की अनुमति दे रहा है, इस रिलीज, वेवर और इंडेम्निटी की घोषणा और समझौते (समझौता) पर आंशिक निर्भरता के आधार पर जिसे मैंने आश्रम में रहने की पूर्व शर्त के रूप में स्वतंत्र रूप से निष्पादित किया है।',
      'agreement.acknowledgement.clause2': 'मेरे पास स्वास्थ्य बीमा और वित्तीय साधन हैं या होंगे जो मेरी यात्रा के दौरान होने वाली किसी भी स्वास्थ्य आवश्यकता या आपातकाल का समाधान करने के लिए आवश्यक हैं।',
      'agreement.acknowledgement.clause3': 'मैं सभी खर्चों को वहन करने का उत्तरदायित्व लेता हूं, जिसमें चिकित्सा खर्च शामिल हैं जो मेरे रहने के दौरान या आश्रम से मेरे प्रस्थान या वापसी से संबंधित किसी भी दुर्घटना, संपत्ति की हानि या बीमारी के परिणामस्वरूप होने वाले या होने की संभावना वाले हैं।',
      'agreement.acknowledgement.clause4': 'मैं स्वीकार करता हूं कि GSP मेरी यात्रा के दौरान मेरी किसी भी चिकित्सा आवश्यकता को पूरा करने के लिए बाध्य नहीं है। मेरी यात्रा के दौरान चिकित्सा आपातकाल की स्थिति में, मैं उपयुक्त चिकित्सा उपचार सुरक्षित करने में सक्षम नहीं हूं; मैंने GSP के प्रतिनिधि को आवश्यक जो भी उपचार सुरक्षित करने के लिए अधिकृत किया है। GSP परिस्थितियों के अनुसार उचित समझे जाने वाले किसी भी कार्य को कर सकता है, लेकिन बाध्य नहीं है, मेरे स्वास्थ्य और सुरक्षा के संबंध में। ऐसे कार्य GSP और मेरे बीच कोई विशेष संबंध नहीं बनाते।',
      'agreement.acknowledgement.clause5': 'मैं समझता हूं कि गुरुदेव सिद्ध पीठ के सर्वोत्तम प्रयासों के बावजूद, मैं अपनी यात्रा के दौरान बीमार या घायल हो सकता हूं, और मैं गुरुदेव सिद्ध पीठ, इसके ट्रस्टियों और/या एजेंटों को अपनी यात्रा से संबंधित चोटों, बीमारी या अन्य हानि के लिए मेरे पास होने वाले सभी दावों से मुक्त करता हूं, और लापरवाही सहित ऐसे दावों लाने के सभी अधिकारों का त्याग करता हूं।',
      'agreement.acknowledgement.clause6': 'मैं आश्रम में अपने रहने से जुड़े या उत्पन्न होने वाले सभी जोखिमों, लागतों और जिम्मेदारियों को पूरी तरह से स्वीकार करता हूं।',
      'agreement.acknowledgement.clause7': 'मैं गुरुदेव सिद्ध पीठ को मेरे द्वारा किए गए किसी भी कार्य या चूक या यहां निर्धारित प्रतिबद्धताओं या उत्तरदायित्वों के मेरे द्वारा किए गए किसी भी उल्लंघन के परिणामस्वरूप गुरुदेव सिद्ध पीठ द्वारा वहन किए गए सभी दावों के खिलाफ बिना शर्त क्षतिपूर्ति करूंगा और हानिरहित रखूंगा।',
      'agreement.acknowledgement.clause8': 'मैं यहां गुरुदेव सिद्ध पीठ को सभी शिकायतों, दावों, विवादों, दायित्वों, मांगों और कार्रवाई के कारणों से मुक्त और सदैव के लिए मुक्त करता हूं, ज्ञात या अज्ञात, निश्चित या आकस्मिक, जो मैं गुरुकुल छात्र के रूप में आश्रम में अपने रहने के परिणामस्वरूप गुरुदेव सिद्ध पीठ के खिलाफ रख सकता हूं या रखने का दावा कर सकता हूं, और यहां सहमत हूं कि ऐसे दावों को स्थापित करने के लिए कोई दावा दायर नहीं करूंगा या कोई अन्य आवेदन नहीं करूंगा।',
      'agreement.acknowledgement.clause9': 'उपरोक्त रिलीज और हानिरहित समझौते मेरे उत्तराधिकारियों, वारिसों और असाइनियों पर बाध्यकारी होंगे।',
      'agreement.acknowledgement.clause10': 'मैं स्वीकार करता हूं कि मैंने उपरोक्त बयानों को पढ़ा है, समझा है और उनसे सहमत हूं।',

      // Summary & Requirements Modal - Gurukula Student (Hindi)
      'modal.gurukula.title': 'गुरुकुल छात्र: सेवा निवास के लिए आवेदन',
      'modal.gurukula.subtitle': 'छह महीने या उससे अधिक के सेवा निवास के लिए आवेदन',
      'modal.gurukula.req.age18+': 'आवेदक की आयु 18 वर्ष या उससे अधिक होनी चाहिए।',
      'modal.gurukula.req.sadhana2y': 'कम से कम दो वर्षों के सिद्ध योग साधना का अभ्यास आवश्यक है।',
      'modal.gurukula.req.intensive3y': 'पिछले तीन वर्षों में कम से कम एक सिद्ध योग शक्तिपात इंटेन्सिव में भागीदारी आवश्यक है।',
      'modal.gurukula.req.sweetSurprise': 'पिछले दो वर्षों में एक स्वीट सरप्राइज सत्संग में भागीदारी आवश्यक है।',
      'modal.gurukula.req.understandingSeva': 'सेवा को आध्यात्मिक अभ्यास के रूप में समझना आवश्यक है।',
      'modal.gurukula.req.visitingSeva': 'पिछले पांच वर्षों में गुरुदेव सिद्ध पीठ में विजिटिंग सेवित के रूप में सेवा देना आवश्यक है।',
      'modal.gurukula.req.sixMonths': 'छह महीने या उससे अधिक समय तक सेवा देने के लिए समर्पित होने में सक्षम होना आवश्यक है।',
      'modal.gurukula.req.language': 'सेवा निर्देशों (लिखित और मौखिक) को समझने और साधना अनुभव साझा करने के लिए अंग्रेजी और/या हिंदी में प्रवीणता आवश्यक है।',
      'modal.gurukula.req.health': 'सेवा देने और आश्रम के दैनिक कार्यक्रम में पूर्ण रूप से भाग लेने के लिए अच्छा स्वास्थ्य और शारीरिक फिटनेस आवश्यक है।',
      'modal.gurukula.req.centreParticipation2y': 'पिछले दो वर्षों के लिए सिद्ध योग ध्यान केंद्र या सिद्ध योग जप और ध्यान समूह, और वैश्विक सिद्ध योग वेबकास्ट में नियमित भागीदारी वांछनीय है।',
      'modal.gurukula.req.sevaParticipation1y': 'पिछले एक वर्ष के लिए सिद्ध योग ध्यान केंद्र, आश्रम या जप और ध्यान समूह में सेवा में नियमित भागीदारी वांछनीय है।',

      // Summary & Requirements Modal - Pilgrimage to the Heart Retreat (Hindi)
      'modal.pth.title': 'हृदय की तीर्थयात्रा रिट्रीट',
      'modal.pth.subtitle': 'सिद्ध योग छात्रों के लिए अपनी साधना को तीव्र करने का अवसर। रिट्रीट के प्रत्येक दिन में जप, ध्यान, चिंतन, अध्ययन, हठ योग, मौन का अभ्यास शामिल है, और इस सब के केंद्र में, आंतरिक आत्मा के साथ गहराता संबंध।',
      'modal.pth.req.age18+': 'आवेदक की आयु 18 वर्ष या उससे अधिक होनी चाहिए।',
      'modal.pth.req.sadhana2y': 'कम से कम दो वर्षों के सिद्ध योग साधना का अभ्यास आवश्यक है।',
      'modal.pth.req.sadhana1yNote': '18 से 29 वर्ष की आयु के छात्रों के लिए, कम से कम एक वर्ष के सिद्ध योग साधना का अभ्यास आवश्यक है।',
      'modal.pth.req.intensive3y': 'पिछले तीन वर्षों में कम से कम एक सिद्ध योग शक्तिपात इंटेन्सिव में भागीदारी आवश्यक है।',
      'modal.pth.req.dailyMeditation': 'पिछले एक वर्ष के लिए दैनिक ध्यान का अभ्यास आवश्यक है।',
      'modal.pth.req.language': 'जिस भाषा में रिट्रीट आयोजित किया जाता है, उसमें प्रवीणता आवश्यक है।',
      'modal.pth.req.health': 'सुबह से शाम तक रिट्रीट कार्यक्रम में पूर्ण रूप से भाग लेने के लिए अच्छा स्वास्थ्य और शारीरिक फिटनेस आवश्यक है।',
      'modal.pth.req.dakshina': 'दक्षिणा का नियमित अभ्यास वांछनीय है।',
      'modal.pth.req.centreParticipation2y': 'पिछले दो वर्षों के लिए सिद्ध योग ध्यान केंद्र या सिद्ध योग जप और ध्यान समूह, और वैश्विक सिद्ध योग वेबकास्ट में नियमित भागीदारी वांछनीय है।',
      'modal.pth.req.sevaParticipation1y': 'पिछले एक वर्ष के लिए सिद्ध योग ध्यान केंद्र, आश्रम या जप और ध्यान समूह में सेवा में नियमित भागीदारी वांछनीय है।',

      // Summary & Requirements Modal - Siddha Yoga Family Retreat (Hindi)
      'modal.familyRetreat.title': 'सिद्ध योग फैमिली रिट्रीट',
      'modal.familyRetreat.subtitle': 'सिद्ध योग फैमिली रिट्रीट में, 10 से 17 वर्ष की आयु के बच्चों वाले परिवार आत्मा का सम्मान करने के तरीके सीखते और अभ्यास करते हैं जो उनके माध्यम से, दूसरों के माध्यम से और उनके आसपास की दुनिया में प्रकट होता है।',
      'modal.familyRetreat.parents.title': 'आवेदन करने की पूर्व-आवश्यकताएं: माता-पिता के लिए',
      'modal.familyRetreat.parents.sadhana2y': 'कम से कम दो वर्षों के सिद्ध योग साधना का अभ्यास आवश्यक है।',
      'modal.familyRetreat.parents.intensive3y': 'पिछले तीन वर्षों में कम से कम एक सिद्ध योग शक्तिपात इंटेन्सिव में भागीदारी आवश्यक है।',
      'modal.familyRetreat.parents.hindi': 'हिंदी में प्रवीणता आवश्यक है।',
      'modal.familyRetreat.parents.health': 'रिट्रीट सत्रों और आश्रम के दैनिक कार्यक्रम में पूर्ण रूप से भाग लेने के लिए अच्छा स्वास्थ्य और शारीरिक फिटनेस आवश्यक है।',
      'modal.familyRetreat.parents.centreParticipation1y': 'पिछले एक वर्ष के लिए सिद्ध योग ध्यान केंद्र या सिद्ध योग जप और ध्यान समूह, और वैश्विक सिद्ध योग वेबकास्ट में नियमित भागीदारी वांछनीय है।',
      'modal.familyRetreat.parents.sevaParticipation1y': 'पिछले एक वर्ष के लिए सिद्ध योग ध्यान केंद्र, आश्रम या जप और ध्यान समूह में सेवा में नियमित भागीदारी वांछनीय है।',
      'modal.familyRetreat.parents.dakshina': 'दक्षिणा का नियमित अभ्यास वांछनीय है।',
      'modal.familyRetreat.children.title': 'आवेदन करने की पूर्व-आवश्यकताएं: बच्चों के लिए',
      'modal.familyRetreat.children.age10to17': '10 से 17 वर्ष की आयु आवश्यक है।',
      'modal.familyRetreat.children.example': 'केवल स्पष्टीकरण के उद्देश्य से उदाहरण: यदि बच्चे की जन्म तिथि 1 जनवरी 2000 है और आश्रम में आगमन की तिथि 1 जनवरी 2010 और 31 दिसंबर 2017 के बीच है, तो वह सिद्ध योग फैमिली रिट्रीट के लिए पात्र है।',
      'modal.familyRetreat.children.hindi': 'हिंदी में बुनियादी प्रवीणता आवश्यक है।',
      'modal.familyRetreat.children.motivation': 'यह आवश्यक है कि बच्चा रिट्रीट सत्रों और आश्रम के दैनिक कार्यक्रम में भाग लेने के लिए स्व-प्रेरित हो।',
      'modal.familyRetreat.children.intensive': 'सिद्ध योग शक्तिपात इंटेन्सिव में भाग लेना वांछनीय है।',
      'modal.familyRetreat.children.satsang': 'पिछले दो वर्षों में बच्चों या युवा सत्संगों में नियमित भागीदारी वांछनीय है।',

      // Summary & Requirements Modal - Siddha Yoga Retreat for Young Adults (Hindi)
      'modal.kypr.title': 'युवा वयस्कों के लिए सिद्ध योग रिट्रीट',
      'modal.kypr.subtitle': 'गुरुदेव सिद्ध पीठ के स्वागतपूर्ण वातावरण में, युवा सिद्ध योग छात्र जप और ध्यान के सिद्ध योग अभ्यासों में भाग लेते हैं, साथ ही सक्रिय शिक्षण सत्रों और प्रकृति में शांत समय में भी। जैसे-जैसे प्रतिभागी रिट्रीट में पूरी तरह से संलग्न होते हैं, वे आंतरिक आत्मा के साथ अपने संबंध को मजबूत करते हैं। आध्यात्मिक अभ्यासों और इस परिवर्तनकारी वातावरण में अध्ययन के साथ अपनी गतिशील भागीदारी के माध्यम से, वे अपनी साधना में स्थायी लाभ लाते हैं।',
      'modal.kypr.req.age18to30': '18 से 30 वर्ष की आयु आवश्यक है।',
      'modal.kypr.req.ageExample': 'केवल स्पष्टीकरण के उद्देश्य से उदाहरण: यदि आवेदक की जन्म तिथि 1 जनवरी 2000 है और आश्रम में आगमन की तिथि 1 जनवरी 2018 और 31 दिसंबर 2030 के बीच है, तो वह युवा वयस्कों के लिए सिद्ध योग रिट्रीट के लिए पात्र है।',
      'modal.kypr.req.sadhana1y': 'कम से कम एक वर्ष के सिद्ध योग साधना का अभ्यास आवश्यक है।',
      'modal.kypr.req.intensive3y': 'पिछले तीन वर्षों में कम से कम एक सिद्ध योग शक्तिपात इंटेन्सिव में भागीदारी आवश्यक है।',
      'modal.kypr.req.dailyMeditation': 'पिछले एक वर्ष के लिए दैनिक ध्यान का अभ्यास आवश्यक है।',
      'modal.kypr.req.hindi': 'हिंदी में प्रवीणता आवश्यक है।',
      'modal.kypr.req.health': 'रिट्रीट सत्रों और आश्रम के दैनिक कार्यक्रम में पूर्ण रूप से भाग लेने के लिए अच्छा स्वास्थ्य और शारीरिक फिटनेस आवश्यक है।',
      'modal.kypr.req.centreParticipation1y': 'पिछले एक वर्ष के लिए सिद्ध योग ध्यान केंद्र या सिद्ध योग जप और ध्यान समूह, और वैश्विक सिद्ध योग वेबकास्ट में नियमित भागीदारी वांछनीय है।',
      'modal.kypr.req.sevaParticipation1y': 'पिछले एक वर्ष के लिए सिद्ध योग ध्यान केंद्र, आश्रम या जप और ध्यान समूह में सेवा में नियमित भागीदारी वांछनीय है।',

      // Summary & Requirements Modal - Visiting Seva (Hindi)
      'modal.visitingSeva.title': 'विजिटिंग सेवा: सेवा निवास के लिए आवेदन',
      'modal.visitingSeva.subtitle': 'एक से पाँच महीनों के सेवा निवास के लिए आवेदन',
      'modal.common.prerequisitesTitle': 'आवेदन करने की पूर्व-आवश्यकताएँ:',
      'modal.common.ageNote': '(आयु सीमा आगमन के दिन लागू और सुनिश्चित की जाती है।)',
      'modal.common.guardianshipNote': '(अभिभावक प्रक्रिया के विवरण जानने के लिए, कृपया गुरुदेव सिद्ध पीठ के आवेदन विभाग से संपर्क करें।)',
      'modal.common.exampleTitle': 'केवल स्पष्टीकरण के उद्देश्य के लिए उदाहरण:',
      'modal.visitingSeva.req.age18+': 'आवेदक की आयु 18 वर्ष या उससे अधिक होनी चाहिए।',
      'modal.visitingSeva.req.sadhana1y': 'कम से कम एक वर्ष का सिद्ध योग साधना का अभ्यास आवश्यक है।',
      'modal.visitingSeva.req.intensive3y': 'पिछले तीन वर्षों में कम से कम एक सिद्ध योग शक्तिपात इंटेंसिव में भाग लेना आवश्यक है।',
      'modal.visitingSeva.req.language': 'अंग्रेज़ी और/या हिंदी में बुनियादी दक्षता आवश्यक है ताकि सेवा निर्देशों (लिखित और मौखिक) को समझा जा सके और साधना अनुभव साझा किए जा सकें।',
      'modal.visitingSeva.req.health': 'सेवा देने और आश्रम के दैनिक कार्यक्रम में पूर्ण रूप से भाग लेने के लिए अच्छा स्वास्थ्य और शारीरिक क्षमता आवश्यक है।',
      'modal.visitingSeva.req.centreParticipation': 'पिछले एक वर्ष से सिद्ध योग ध्यान केंद्र या जप और ध्यान समूह में तथा वैश्विक सिद्ध योग वेबकास्ट में नियमित भागीदारी वांछनीय है।',
      'modal.visitingSeva.req.sevaParticipation': 'पिछले एक वर्ष से सिद्ध योग ध्यान केंद्र, आश्रम या जप और ध्यान समूह में नियमित सेवा भागीदारी वांछनीय है।',
      'modal.visitingSeva.children5to12.title': 'विजिटिंग सेवा बच्चों के लिए: आयु 5 से 12 वर्ष',
      'modal.visitingSeva.children5to12.req1': 'पाँच से नौ वर्ष की आयु के बच्चों को दोनों माता-पिता के साथ आना आवश्यक है।',
      'modal.visitingSeva.children5to12.req2': '10 से 12 वर्ष की आयु के बच्चों को दोनों माता-पिता या एक माता-पिता और एक अभिभावक के साथ आना आवश्यक है।',
      'modal.visitingSeva.children5to12.req3': 'कम से कम एक माता-पिता को अपने बच्चे/बच्चों के साथ लगातार रहने के लिए सहमत होना चाहिए।',
      'modal.visitingSeva.children5to12.req4': 'बच्चे/बच्चों के साथ आने वाले माता-पिता और अभिभावक को विजिटिंग सेवा की आवश्यकताओं को पूरा करना चाहिए।',
      'modal.visitingSeva.children5to12.req5': 'माता-पिता अपने बच्चे/बच्चों के साथ सात दिनों के लिए आवेदन कर सकते हैं।',
      'modal.visitingSeva.children5to12.req6': 'वेबसाइट पर प्रकाशित रिट्रीट तिथियों को छोड़कर किसी भी समय गुरुदेव सिद्ध पीठ में आवेदन कर सकते हैं।',
      'modal.visitingSeva.children13to17.title': 'विजिटिंग सेवा बच्चों के लिए: आयु 13 से 17 वर्ष',
      'modal.visitingSeva.children13to17.req1': 'कम से कम एक माता-पिता या उसी लिंग के एक अभिभावक के साथ आना आवश्यक है।',
      'modal.visitingSeva.children13to17.req2': 'बच्चे/बच्चों के साथ आने वाले माता-पिता/अभिभावक को विजिटिंग सेवाइट आवश्यकताओं को पूरा करना चाहिए।',
      'modal.visitingSeva.children13to17.req3': 'रिट्रीट के दौरान छोड़कर, गुरुदेव सिद्ध पीठ में सेवा देने के लिए आवेदन कर सकते हैं।',
      'modal.visitingSeva.example.p1': 'यदि बच्चे की जन्म तिथि 1 जनवरी 2000 है और आश्रम में आगमन की तिथि 1 जनवरी 2005 से 31 दिसंबर 2009 के बीच है, तो बच्चे को दोनों माता-पिता के साथ आना आवश्यक है। यदि आश्रम में बच्चे के आगमन की तिथि 1 जनवरी 2010 से 31 दिसंबर 2012 के बीच है, तो बच्चे को दोनों माता-पिता या एक माता-पिता और एक अभिभावक के साथ आना आवश्यक है।',
      'modal.visitingSeva.example.p2': 'यदि बच्चे की जन्म तिथि 1 जनवरी 2000 है और आश्रम में आगमन की तिथि 1 जनवरी 2013 से 31 दिसंबर 2017 के बीच है, तो बच्चे को कम से कम एक माता-पिता या उसी लिंग के एक अभिभावक के साथ आना आवश्यक है।',
      // Contemplation (Visiting Seva)
      'contemplation.title': 'Contemplation Questions',
      'contemplation.chooseSubmissionTitle': 'Choose Your Submission Method',
      'contemplation.chooseSubmissionSubtitle': 'Select how you would like to submit your contemplation responses',
      'contemplation.card.form.title': 'Fill Form Online',
      'contemplation.card.form.description': 'Answer questions directly in the form below',
      'contemplation.card.file.title': 'Upload PDF',
      'contemplation.card.file.description': 'Upload your completed contemplation document',
      'contemplation.tooltip': 'This is a tooltip',
      'contemplation.alert.title': 'Please read before answering the contemplation questions',
      'contemplation.alert.intro': 'Write an essay response of minimum 100 words to each of the following questions. Please be specific in your answers and use examples wherever possible.',
      'contemplation.alert.demonstrate': 'Your response should demonstrate that:',
      'contemplation.alert.point1': 'You have committed yourself to a conscious and sustained engagement with Siddha Yoga meditation.',
      'contemplation.alert.point2': 'You have taken responsibility for your learning, your experience and your spiritual path.',
      'contemplation.alert.point3': 'You have made an effort to clearly articulate your understanding and experience.',
      'contemplation.answerPlaceholder': 'Please answer the question here',
      'contemplation.words': 'words',
      'contemplation.q1a.label': '1. How are you studying and practising the following in your life? A. Siddha Yoga Teachings and Practices [Mention specific teachings, practices (frequency, duration), etc.]:',
      'contemplation.q1b.label': 'B. Siddha Yoga Path Website [Mention specific elements (Talks, Commentaries, Photo galleries, etc.), how you engage with them, how frequently, etc.]:',
      'contemplation.q2.intro': '2. Describe with examples one specific benefit you have experienced from your engagement with the Siddha Yoga teachings and practices',
      'contemplation.q2a.label': 'A. Siddha Yoga Teachings and Practices.',
      'contemplation.q2b.label': 'B. Siddha Yoga Path Website.',
      'contemplation.q3.label': '3. Seva is an opportunity to learn and cultivate virtues. How are you working on yourself while offering seva – i.e. cultivating virtues, eliminating limitations/shortcomings, etc.?',
      'contemplation.q4.label': '4. Gurudev Siddha Peeth\'s sacred atmosphere is sustained by the Ashram’s daily schedule and discipline. How will you uphold them both?',
      'contemplation.q5.label': '5. How does seva help you to understand and implement Shree Gurumayi\'s teachings?',

      // Additional validation used in contemplation
      'validation.minLength100': 'Your answer must contain minimum 100 words.',
      'validation.maxLength1000': 'Maximum length is 5000 characters.',

      // Accommodation Details (Visiting Seva)
      'accommodation.title': 'Information Related to Ashram Stay',
      'accommodation.housingPreference.label': 'What is your housing preference?:',
      'accommodation.housingPreference.options.dorm': 'Dorm',
      'accommodation.housingPreference.options.double': 'Double',
      'accommodation.housingPreference.options.doubleAc': 'Double with AC',
      'accommodation.housingPreference.options.single': 'Single',
      'accommodation.housingPreference.options.singleAc': 'Single with AC',
      'accommodation.validation.housingPreferenceRequired': 'Housing preference is required.',
      'accommodation.snoreQuestion': 'Have you been told that you snore?',
      'accommodation.validation.snoreRequired': 'Snoring information is required.',
      'accommodation.roommateConcernQuestion': 'Do you have concerns about living with roommates at Gurudev Siddha Peeth?',
      'accommodation.validation.roommateConcernRequired': 'Roommate concern information is required.',
      'accommodation.describeConcerns': 'Please describe:',
      'accommodation.otherRequests': 'Do you have any requests related to accommodations?',
      'accommodation.placeholder.enterRequests': 'Enter requests',

      // Skills (Visiting Seva)
      'skills.title': 'Skills',
      'skills.instructions': 'Other Skills (Please mark your skill level in ANY of the following fields 1 = Basic, 2 = Good, 3 = Very Good)',
      'skills.level.basic': 'Basic',
      'skills.level.good': 'Good',
      'skills.level.veryGood': 'Very Good',
      'skills.category.management': 'Management',
      'skills.category.design': 'Design',
      'skills.category.arts': 'Arts',
      'skills.category.construction': 'Construction',
      'skills.category.finance': 'Finance',
      'skills.category.foodServices': 'Food Services',
      'skills.category.healthMedical': 'Health & Medical',
      'skills.category.technical': 'Technical',
      'skills.software.word': 'Word',
      'skills.software.excel': 'Excel',
      'skills.software.powerPoint': 'PowerPoint',
      'skills.software.outlook': 'Outlook',
      'skills.otherSkills': 'Other Skills',
      'skills.placeholder.otherSkills': 'Specify other skills',

      // Tentative Dates (Visiting Seva)
      'tentativeDates.title': 'Your dates of availability for offering',
      'tentativeDates.tooltip': 'This is a tooltip',
      'tentativeDates.info.readBefore': 'Please read the information below before choosing your available dates',
      'tentativeDates.info.paragraph1a': 'In the space below, please indicate the dates that you will be available to come.',
      'tentativeDates.info.gsp': 'Gurudev Siddha Peeth (GSP)',
      'tentativeDates.info.paragraph1b': 'When a seva opportunity matches your skills and your availability, you may be invited to come to offer seva. This invitation will be made sufficiently in advance so you can make your travel and personal arrangements with ease.',
      'tentativeDates.info.paragraph1c': 'Your stay is confirmed only once you are formally invited to come to GSP.',
      'tentativeDates.factors.title': 'Factors to keep in mind when selecting your available dates',
      'tentativeDates.factors.healthTitle': 'Good health:',
      'tentativeDates.factors.healthText': 'You must be in good health to fully participate in the ashram daily schedule.',
      'tentativeDates.factors.weatherTitle': 'Weather:',
      'tentativeDates.factors.weatherText': ' During the monsoon season Ganeshpuri sees heavy rains for prolonged periods of time and high humidity may lead to growth of mold, even in rooms. In the summer too, Ganeshpuri is very hot and humid. This is so you are aware and can plan accordingly.',
      'tentativeDates.factors.recentEventsTitle': 'If you have recently experienced any of the following, you are requested to come at least 6 months after the experience or when you are fully recovered, whichever is later:',
      'tentativeDates.factors.recent.divorce': 'Divorce or termination of a long-term relationship',
      'tentativeDates.factors.recent.death': 'Death of a loved one',
      'tentativeDates.factors.recent.addictionRecovery': 'Recovery from alcohol or drug addiction',
      'tentativeDates.factors.recent.surgeryIllness': 'Major surgery or a serious illness',
      'tentativeDates.factors.recent.mentalHealthTreatment': 'Treatment for depression or any other mental health condition',
      'tentativeDates.arrivalDate': 'Requested Arrival Date:',
      'tentativeDates.arrivalDatePlaceholder': 'Select Requested Arrival Date',
      'tentativeDates.departureDate': 'Requested Departure Date:',
      'tentativeDates.departureDatePlaceholder': 'Select Requested Departure Date',
      'tentativeDates.arrivalBeforeDeparture': 'Requested Arrival Date must be before or on Requested Departure Date.',
      'tentativeDates.departureAfterArrival': 'Requested Departure Date must be after or on Requested Arrival Date.',
      'validation.arrivalDateRequired': 'Arrival date is required.',
      'validation.departureDateRequired': 'Departure date is required.',

      // PTH Contemplation
      'pthContemplation.title': 'रिट्रीट और अभ्यास',
      'pthContemplation.chooseSubmissionTitle': 'अपनी सबमिशन विधि चुनें',
      'pthContemplation.chooseSubmissionSubtitle': 'चिंतन प्रतिक्रियाएँ कैसे जमा करना चाहते हैं, चुनें',
      'pthContemplation.card.form.title': 'ऑनलाइन फ़ॉर्म भरें',
      'pthContemplation.card.form.description': 'नीचे दिए गए फॉर्म में सीधे उत्तर दें',
      'pthContemplation.card.file.title': 'PDF अपलोड करें',
      'pthContemplation.card.file.description': 'अपना पूर्ण चिंतन दस्तावेज़ अपलोड करें',
      'pthContemplation.q1.label': '1. आप सिद्धयोग की शिक्षाओं का अध्ययन और अभ्यास कैसे कर रहे हैं? इस अध्ययन और अभ्यास से आपको क्या लाभ मिल रहे हैं?',
      'pthContemplation.q2.label': '2. आपने अपने ध्यान अभ्यास को गहरा करने के लिए किन तरीकों का उपयोग किया है? प्रयुक्त विशिष्ट साधनों का उल्लेख करें।',
      'pthContemplation.q3.label': '3. आपके नियमित ध्यान अभ्यास का आपके दैनिक जीवन के विभिन्न पहलुओं — दूसरों के साथ आपके संबंध, आपका कार्य और आपकी आंतरिक स्थिति — पर क्या प्रभाव पड़ता है?',
      'pthContemplation.q4.label': '4. इस समय सिद्धयोग रिट्रीट या कार्यक्रम में भाग लेने का आपका उद्देश्य क्या है? इस उद्देश्य का समर्थन करने के लिए आप स्वयं को कैसे तैयार करेंगे?',
      'pthContemplation.placeholder.answerHere': 'कृपया यहां उत्तर लिखें',
      'validation.minLength10': 'न्यूनतम लंबाई 10 अक्षर है।'
    },

    mr: {
      // Common
      'common.instructions': 'सूचना',
      'common.contactUs': 'आमच्याशी संपर्क साधा',
      'common.indianApplications': 'भारतीय अर्ज',
      'common.email': 'ईमेल',
      'common.phone': 'फोन',
      'common.mobile': 'मोबाइल',

      // Instructions
      'instructions.registration.title': 'नोंदणी सूचना',
      'instructions.registration.text': 'जर तुम्ही नोंदणीकृत नसाल तर,',
      'instructions.registration.clickHere': 'येथे क्लिक करा',
      'instructions.registration.createAccount': 'तुमचे खाते तयार करण्यासाठी।',
      'instructions.registration.passwordInfo': 'एक सुरक्षित पासवर्ड निवडा (किमान 8 अक्षरे, मोठे अक्षर, लहान अक्षर आणि विशेष अक्षरे समाविष्ट).',
      'instructions.registration.mandatoryFields': 'नोंदणी प्रक्रिया पूर्ण करण्यासाठी सर्व फील्ड अनिवार्य आहेत.',
      'instructions.login.title': 'लॉगिन सूचना',
      'instructions.login.text': 'जर तुम्ही आधीच नोंदणीकृत असाल तर, OTP (वन टाइम पासवर्ड) मिळवण्यासाठी कृपया तुमचा ईमेल पत्ता किंवा मोबाइल नंबर प्रविष्ट करा।',
      'instructions.login.otpText': 'लॉगिन करण्यासाठी OTP प्रविष्ट करा।',
      'instructions.login.warning': 'तुमचा OTP कोणाशीही सामायिक करू नका।',

      // Login Page
      'login.title': 'लॉगिन / Login',
      'login.welcome': 'गुरुदेव सिद्ध पीठ अर्ज पोर्टलमध्ये आपले स्वागत आहे',

      // Registration Page
      'registration.title': 'नोंदणी / Registration',
      'registration.welcome': 'गुरुदेव सिद्ध पीठ अर्ज पोर्टलमध्ये आपले स्वागत आहे',

      // Form Fields
      'form.loginMethod': 'लॉगिन पद्धत',
      'form.email': 'ईमेल',
      'form.mobile': 'मोबाइल नंबर',
      'form.emailAddress': 'ईमेल पत्ता',
      'form.emailPlaceholder': 'तुमचा ईमेल पत्ता प्रविष्ट करा',
      'form.mobileNumber': 'मोबाइल नंबर',
      'form.mobilePlaceholder': 'तुमचा मोबाइल नंबर प्रविष्ट करा',
      'form.otp': 'OTP',
      'form.sequenceNo': 'क्रमांक',
      'form.otpPlaceholder': 'OTP प्रविष्ट करा',
      'form.resendOtp': 'OTP पुन्हा पाठवा',
      'form.firstName': 'पहिले नाव',
      'form.middleName': 'मध्य नाव',
      'form.lastName': 'आडनाव',
      'form.firstNamePlaceholder': 'तुमचे पहिले नाव प्रविष्ट करा',
      'form.middleNamePlaceholder': 'तुमचे मध्य नाव प्रविष्ट करा',
      'form.lastNamePlaceholder': 'तुमचे आडनाव प्रविष्ट करा',
      'form.dateOfBirth': 'जन्मतारीख',
      'form.dateOfBirthPlaceholder': 'जन्मतारीख निवडा',
      'form.gender': 'लिंग',
      'form.selectGender': 'लिंग निवडा',
      'form.male': 'पुरुष',
      'form.female': 'स्त्री',
      'form.other': 'इतर',
      'form.specifyGender': 'कृपया तुमचे लिंग निर्दिष्ट करा',
      'form.genderPlaceholder': 'तुमचे लिंग प्रविष्ट करा',
      'form.password': 'पासवर्ड',
      'form.confirmPassword': 'पासवर्डची पुष्टी करा',
      'form.passwordPlaceholder': 'तुमचा पासवर्ड प्रविष्ट करा',
      'form.confirmPasswordPlaceholder': 'तुमच्या पासवर्डची पुष्टी करा',
      'form.countryCode': 'देश कोड',
      'form.selectCountry': 'देश निवडा',

      // Validation Messages
      'validation.emailRequired': 'ईमेल आवश्यक आहे।',
      'validation.emailInvalid': 'कृपया वैध ईमेल पत्ता प्रविष्ट करा।',
      'validation.countryCodeRequired': 'देश कोड आवश्यक आहे।',
      'validation.mobileRequired': 'मोबाइल नंबर आवश्यक आहे।',
      'validation.mobileInvalid': 'अवैध मोबाइल नंबर स्वरूप।',
      'validation.mobileMaxLength': 'मोबाइल नंबर 10 अंकांपेक्षा जास्त असू शकत नाही।',
      'validation.mobileMustDiffer': 'आपत्कालीन संपर्क क्रमांक वर्तमान पत्त्याच्या क्रमांकापेक्षा वेगळा असावा।',
      'validation.otpRequired': 'OTP आवश्यक आहे।',
      'validation.firstNameRequired': 'पहिले नाव आवश्यक आहे।',
      'validation.firstNameMaxLength': 'पहिले नाव 50 अक्षरांपेक्षा जास्त असू शकत नाही।',
      'validation.middleNameMaxLength': 'मध्य नाव 50 अक्षरांपेक्षा जास्त असू शकत नाही।',
      'validation.lastNameRequired': 'आडनाव आवश्यक आहे।',
      'validation.lastNameMaxLength': 'आडनाव 50 अक्षरांपेक्षा जास्त असू शकत नाही।',
      'validation.dateOfBirthRequired': 'जन्मतारीख आवश्यक आहे।',
      'validation.genderRequired': 'लिंग आवश्यक आहे।',
      'validation.genderMaxLength': 'लिंग 20 अक्षरांपेक्षा जास्त असू शकत नाही।',
      'validation.emailMaxLength': 'ईमेल 100 अक्षरांपेक्षा जास्त असू शकत नाही।',
      'validation.passwordRequired': 'पासवर्ड आवश्यक आहे।',
      'validation.passwordMinLength': 'पासवर्ड किमान 6 अक्षरे असावे।',
      'validation.confirmPasswordRequired': 'पासवर्डची पुष्टी आवश्यक आहे।',
      'validation.passwordsMismatch': 'पासवर्ड जुळत नाहीत।',

      // Buttons
      'button.login': 'लॉगिन',
      'button.sendOtp': 'OTP पाठवा',
      'button.createAccount': 'नवीन खाते तयार करा',
      'button.register': 'नोंदणी करा',
      'button.registering': 'नोंदणी होत आहे...',
      'button.backToLogin': 'लॉगिन पेजवर परत जा',
      'button.submit': 'सबमिट करा',
      'button.cancel': 'रद्द करा',
      'button.back': 'मागे',
      'button.next': 'पुढे',
      'button.previous': 'मागील',
      'button.proceed': 'पुढे जा',
      'button.saveAsDraft': 'मसुदा म्हणून जतन करा',

      // Success Messages
      'success.registrationComplete': 'नोंदणी यशस्वीरित्या पूर्ण झाली!',
      'success.otpSent': 'OTP यशस्वीरित्या पाठवले!',
      'success.loginSuccess': 'लॉगिन यशस्वी!',
      'success.informationSaved': 'माहिती यशस्वीरित्या जतन केली',
      'success.documentUploaded': 'दस्तऐवज यशस्वीरित्या अपलोड केला',
      'success.documentDeleted': 'दस्तऐवज यशस्वीरित्या हटवला',
      'success.title': 'यश',

      // Error Messages
      'error.registrationFailed': 'नोंदणी अयशस्वी। कृपया पुन्हा प्रयत्न करा।',
      'error.loginFailed': 'लॉगिन अयशस्वी। कृपया तुमची साख तपासा।',
      'error.otpInvalid': 'अवैध OTP। कृपया पुन्हा प्रयत्न करा।',
      'error.networkError': 'नेटवर्क त्रुटी। कृपया तुमचे कनेक्शन तपासा।',
      'error.uploadFailed': 'दस्तऐवज अपलोड करण्यात अयशस्वी। कृपया पुन्हा प्रयत्न करा।',
      'error.deleteFailed': 'दस्तऐवज हटवण्यात अयशस्वी। कृपया पुन्हा प्रयत्न करा।',
      'error.title': 'त्रुटी',

      // Profile Summary Page
      'profile.title': 'सेवा प्रोफाइल',
      'profile.importantInfo': 'महत्वाची माहिती',
      'profile.infoText': 'गुरुदेव सिद्ध पीठात सेवा किंवा रिट्रीटसाठी अर्ज करण्यासाठी, पूर्णपणे सेवा प्रोफाइल भरा आणि नंतर संबंधित ध्यान फॉर्म भरा।',
      'profile.mustComplete': 'ध्यान फॉर्म अनलॉक करण्यासाठी तुम्हाला तुमचा सेवा प्रोफाइल पूर्ण करावा लागेल।',
      'profile.progress': 'सेवा प्रोफाइल प्रगती',
      'profile.progressInstructions': 'प्रगती सूचना',
      'profile.progressText': 'सेवा आणि रिट्रीटसाठी अर्ज करण्यासाठी ध्यान फॉर्म अनलॉक करण्यासाठी 100% प्रगती गाठण्यासाठी तुमचा सेवा प्रोफाइल फॉर्म पूर्णपणे भरा।',
      'profile.updateProfile': 'तुमचा सेवा प्रोफाइल अपडेट करा',
      'profile.complete': 'पूर्ण',
      'profile.pending': 'प्रलंबित',
      'profile.myApplications': 'माझे अर्ज',
      'profile.validTill': 'वैध पर्यंत',
      'profile.expiredOn': 'कालबाह्य झाले',
      'profile.contemplationForms': 'सेवा आणि रिट्रीटसाठी ध्यान फॉर्म',
      'profile.completeProfileToUnlock': 'खालील ध्यान फॉर्म अनलॉक करण्यासाठी तुमचा सेवा प्रोफाइल पूर्ण करा',
      'profile.fillContemplationForm': 'तुम्ही ज्या सेवा किंवा रिट्रीटसाठी अर्ज करू इच्छिता त्यासाठी ध्यान फॉर्म भरा आणि सबमिट करा।',
      'profile.desc.visitingSeva': 'अल्पकालीन सेवांच्या संधींसाठी अर्ज करा',
      'profile.desc.gurukulaSeva': 'आध्यात्मिक अध्ययन कार्यक्रमात विद्यार्थी म्हणून सहभागी व्हा',
      'profile.desc.kypr': 'तरुणांसाठी तयार केलेला आध्यात्मिक रिट्रीट',
      'profile.desc.sypr': 'कुटुंबकेंद्रित आध्यात्मिक रिट्रीट अनुभव',
      'profile.desc.familyRetreat': 'कुटुंबकेंद्रित आध्यात्मिक रिट्रीट अनुभव',
      'profile.desc.pth': 'सखोल आध्यात्मिक यात्रा आणि रिट्रीट',
      'profile.desc.homeSeva': 'विशिष्ट प्रकल्पांसाठी घरी बसून सेवा द्या',
      'profile.childrenNote': '5 ते 12 वर्ष वयोगटातील मुलांना ध्यान फॉर्म भरण्याची गरज नाही।',
      'profile.needHelp': 'मदत हवी?',
      'profile.sevaProfile': 'सेवा प्रोफाइल',
      'profile.visitingSeva': 'विजिटिंग सेवा',
      'profile.gurukulaSeva': 'गुरुकुल सेवा',
      'profile.homeSeva': 'होम सेवा',
      'profile.familyRetreat': 'फॅमिली रिट्रीट',
      'profile.pth': 'हृदयाची तीर्थयात्रा',
      'profile.sypr': 'SYPR',
      'profile.kypr': 'KYPR',

      // Seva Profile Component
      'sevaProfile.formTitle': 'सेवा प्रोफाइल फॉर्म',
      'sevaProfile.applicantLabel': 'अर्जदार:',
      'sevaProfile.goBackHome': 'मुखपृष्ठावर परत जा',

      // Personal Details Page (additional)
      'personalDetails.title': 'वैयक्तिक तपशील',

      // Common (additional)
      'common.select': 'निवडा',
      'common.yes': 'होय',
      'common.no': 'नाही',

      // Form Fields (additional)
      'form.age': 'वय',
      'form.maritalStatus': 'वैवाहिक स्थिती',
      'form.beforeMarriageFullName': 'लग्नापूर्वीचे पूर्ण नाव',
      'form.isFirstStayQuestion': 'गुरुदेव सिद्ध पीठ येथे हा तुमचा पहिला मुक्काम असेल का?',
      'form.lastStayQuestion': 'नसल्यास, तुमचा शेवटचा मुक्काम कधी होता? (महिना आणि वर्ष द्या)',
      'form.middleNameVariants': 'मध्य नाव / वडिलांचे नाव / पतीचे नाव',

      // Placeholders (additional)
      'placeholder.firstName': 'पहिले नाव प्रविष्ट करा',
      'placeholder.middleName': 'मध्य नाव प्रविष्ट करा',
      'placeholder.lastName': 'आडनाव प्रविष्ट करा',
      'placeholder.dateOfBirth': 'जन्मतारीख निवडा',
      'placeholder.ageAuto': 'वय आपोआप गणना होईल',
      'placeholder.selectMaritalStatus': 'वैवाहिक स्थिती निवडा',
      'placeholder.beforeMarriageFullName': 'लग्नापूर्वीचे पूर्ण नाव',
      'placeholder.selectGender': 'लिंग निवडा',
      'placeholder.enterGender': 'लिंग प्रविष्ट करा',
      'placeholder.lastStayMonthYear': 'शेवटच्या मुक्कामाचा महिना आणि वर्ष निवडा',

      // Options (additional)
      'options.single': 'अविवाहित',
      'options.married': 'विवाहित',
      'options.other': 'इतर',
      'options.male': 'पुरुष',
      'options.female': 'स्त्री',

      // Validation (additional)
      'validation.requiredField': 'हे क्षेत्र आवश्यक आहे.',
      'validation.middleNameRequired': 'मध्य नाव आवश्यक आहे.',
      'validation.beforeMarriageFullNameRequired': 'लग्नापूर्वीचे पूर्ण नाव आवश्यक आहे.',
      'validation.otherGenderRequired': 'इतर लिंग आवश्यक आहे.',
      'validation.lastStayDateRequired': 'शेवटच्या मुक्कामाचा महिना आणि वर्ष आवश्यक आहे.',

      // Health Insurance Page
      'healthInsurance.title': 'आरोग्य विमा',
      'healthInsurance.question.hasInsurance': 'GSP मधील तुमच्या मुक्कामासाठी कव्हर करणारा आरोग्य विमा आहे का?',
      'healthInsurance.companyName': 'कंपनीचे नाव',
      'healthInsurance.companyNamePlaceholder': 'कंपनीचे नाव प्रविष्ट करा',
      'healthInsurance.policyNumber': 'पॉलिसी क्रमांक',
      'healthInsurance.policyNumberPlaceholder': 'पॉलिसी क्रमांक प्रविष्ट करा',
      'healthInsurance.validUntil': 'वैध पर्यंत',
      'healthInsurance.validUntilPlaceholder': 'वैध पर्यंतची तारीख निवडा',
      'healthInsurance.question.financialSupport': 'नसल्यास, वैद्यकीय आपत्कालीन स्थितीत तुम्ही स्वतःचा आर्थिक आधार करू शकता का?',
      'healthInsurance.declaration': 'घोषणा: मी वरील दिलेली माहिती अचूक आणि बरोबर असल्याचे प्रमाणित करतो/करते.',
      'healthInsurance.declarationRequired': 'कृपया पुढे जाण्यासाठी घोषणा स्वीकारा.',

      // Hospital & Allergies Page
      'hospital.title': 'अस्पताल में भर्ती और एलर्जी',
      'hospital.question.pastHospitalized': 'क्या आप पहले किसी अस्पताल में भर्ती होने के कारण वर्तमान में प्रभावित हैं?',
      'hospital.pleaseDescribe': 'कृपया विवरण दें',
      'hospital.enterDetails': 'विवरण दर्ज करें',
      'hospital.question.surgeryDone': 'क्या आपकी कोई शल्यक्रिया हुई है?',
      'hospital.immunizations.title': 'टीकाकरण: आपने निम्नलिखित टीकाकरण अंतिम बार लगभग किस वर्ष लिया?',
      'hospital.immunizations.tetanus': 'टे़टेनस टीकाकरण:',
      'hospital.immunizations.tb': 'क्षय रोग (BCG):',
      'hospital.immunizations.polio': 'पोलियो टीकाकरण:',
      'hospital.immunizations.other': 'अन्य:',
      'hospital.pickYear': 'वर्ष चुनें',
      'hospital.allergies.title': 'एलर्जी – क्या आपको निम्नलिखित में से किसी चीज़ से एलर्जी (या संवेदनशीलता) है?',
      'hospital.allergies.mould': 'फफूंदी एलर्जी:',
      'hospital.allergies.pollen': 'पराग एलर्जी:',
      'hospital.allergies.beeSting': 'मधुमक्खी के काटने की एलर्जी:',
      'hospital.medicineAllergies': 'आपको किन दवाओं से एलर्जी है? (नहीं होने पर "none" लिखें)',
      'hospital.enterMedicineAllergies': 'दवा एलर्जी दर्ज करें या "none" लिखें',
      'hospital.foodAllergies': 'कौन से खाद्य पदार्थों से आप संवेदनशील या एलर्जिक हैं? (नहीं होने पर "none" लिखें)',
      'hospital.enterFoodAllergies': 'खाद्य एलर्जी दर्ज करें या "none" लिखें',
      'hospital.otherAllergies': 'कोई अन्य एलर्जी? कृपया सूची दें:',
      'hospital.enterOtherAllergies': 'अन्य एलर्जी दर्ज करें',

      // Language Proficiency Page
      'language.title': 'भाषा प्रवीणता',
      'language.languageName': 'भाषेचे नाव',
      'language.speaking': 'बोलना',
      'language.reading': 'पढ़ना',
      'language.writing': 'लिखना',
      'language.selectLanguage': 'भाषा निवडा',
      'language.select': 'निवडा',
      'language.addNewLanguage': 'नई भाषा जोड़ें',
      'language.delete': 'हटवा',
      'language.proficiencyA': 'A: 75 – 100%',
      'language.proficiencyB': 'B: 50 – 75%',
      'language.proficiencyC': 'C: 0 - 50%',
      'language.proficiencyD': 'D: None',
      'validation.languageNameRequired': 'भाषेचे नाव आवश्यक आहे.',
      'validation.speakingRequired': 'बोलने की प्रवीणता आवश्यक आहे.',
      'validation.readingRequired': 'पढ़ने की प्रवीणता आवश्यक आहे.',
      'validation.writingRequired': 'लिखने की प्रवीणता आवश्यक आहे.',
      'validation.completeLastRow': 'नई भाषा जोड़ने से पहले कृपया पिछली भाषा पंक्ति को पूरा करें।',
      'validation.hindiEnglishRequired': 'हिंदी आणि इंग्रजी भाषाएं आवश्यक हैं और हटवल्या जाऊ शकत नाहीत.',
      'validation.minimumLanguagesRequired': 'कम से कम 2 भाषा प्रविष्टियां (हिंदी आणि इंग्रजी) आवश्यक आहेत.',
      'validation.completeAllFields': 'सबमिट करने से पहले कृपया सभी आवश्यक फ़ील्ड को पूरा करें।',

      // Current Address Page
      'address.title': 'सध्याचा निवासी पत्ता',
      'address.streetAddress': 'घराचे नाव, क्रमांक आणि रस्त्याचा पत्ता प्रविष्ट करा:',
      'address.townCity': 'शहर/नगर:',
      'address.district': 'जिल्हा:',
      'address.state': 'राज्य:',
      'address.pinCode': 'पिन कोड:',
      'address.mobilePersonal': 'मोबाइल (व्यक्तिगत):',
      'address.mobileAlternate': 'मोबाइल (वैकल्पिक नंबर):',
      'address.email': 'ईमेल:',
      'address.selectState': 'राज्य निवडा',
      'address.selectCountry': 'चुनें',
      'address.enterStreetAddress': 'सड़क का पता दर्ज करें',
      'address.enterTownCity': 'शहर/नगर दर्ज करें',
      'address.enterDistrict': 'जिला दर्ज करें',
      'address.enterPinCode': 'पिन कोड दर्ज करें',
      'address.enterHomePhone': 'घर का फोन दर्ज करें',
      'address.enterWorkPhone': 'कार्यालय का फोन दर्ज करें',
      'address.enterEmail': 'ईमेल दर्ज करें',
      'validation.streetAddressRequired': 'सड़क का पता आवश्यक है।',
      'validation.townCityRequired': 'शहर/नगर आवश्यक है।',
      'validation.districtRequired': 'जिला आवश्यक है।',
      'validation.stateRequired': 'राज्य आवश्यक है।',
      'validation.pinCodeRequired': 'पिन कोड आवश्यक है और 6 अंकों का असावा.',

      // Permanent Address Page
      'permanentAddress.title': 'स्थायी निवासी पत्ता',
      'permanentAddress.sameAsCurrent': 'स्थायी निवासी पत्ता',
      'permanentAddress.isSameAsCurrent': 'सध्याच्या निवासी पत्त्यासारखा आहे',
      'permanentAddress.checkboxTooltip': 'तुमचा स्थायी पत्ता तुमच्या सध्याच्या निवासी पत्त्याशी जुळत असल्यास हा बॉक्स चेक करा',

      // Emergency Contact Page
      'emergencyContact.title': 'आपत्कालीन संपर्क माहिती',
      'emergencyContact.contactName': 'आपत्कालीन संपर्क का नाम:',
      'emergencyContact.relationship': 'आपत्कालीन संपर्क का रिश्ता:',
      'emergencyContact.selectRelationship': '-- रिश्ता चुनें --',
      'emergencyContact.enterContactName': 'आपत्कालीन संपर्क का नाम',
      'emergencyContact.enterEmergencyMobile': 'आपत्कालीन मोबाइल दर्ज करें',
      'emergencyContact.enterWorkPhone': 'कार्यालय का फोन दर्ज करें',
      'emergencyContact.enterEmergencyEmail': 'आपत्कालीन ईमेल दर्ज करें',
      'validation.contactNameRequired': 'आपत्कालीन संपर्क का नाम आवश्यक है।',
      'validation.relationshipRequired': 'रिश्ता आवश्यक है।',

      // Study Participation Information Page
      'studyParticipation.title': 'सिद्धयोग सहभाग',
      'studyParticipation.beginPracticeYear': 'तुम्ही कोणत्या वर्षी सिद्ध योगाचा सराव सुरू केला?',
      'studyParticipation.selectPracticeYear': 'सराव वर्ष निवडा',
      'studyParticipation.whereBeginPractice': 'तुम्ही तुमचा सराव कुठे सुरू केला?',
      'studyParticipation.selectOption': 'एक पर्याय निवडा',
      'studyParticipation.siddhaYogaCentre': 'सिद्धयोग केंद्र',
      'studyParticipation.home': 'घर',
      'studyParticipation.other': 'इतर',
      'studyParticipation.ifOtherMention': 'इतर असल्यास, सिद्धयोग केंद्र आणि शहराचे नाव सांगा',
      'studyParticipation.enterCentreName': 'केंद्राचे नाव आणि शहर दर्ज करें',
      'studyParticipation.howOftenVisit': 'तुम्ही तुमच्या सिद्धयोग केंद्रात किती वेळा जाता?',
      'studyParticipation.selectFrequency': 'वारंवारता निवडा',
      'studyParticipation.weekly': 'साप्ताहिक',
      'studyParticipation.monthly': 'मासिक',
      'studyParticipation.occasionally': 'कधीकधी',
      'studyParticipation.never': 'कधीही नाही',
      'studyParticipation.whichCentreVisit': 'तुम्ही कोणत्या सिद्धयोग केंद्रात जाता?',
      'studyParticipation.state': 'राज्य',
      'studyParticipation.district': 'जिला',
      'studyParticipation.centre': 'केंद्र',
      'studyParticipation.selectState': 'राज्य निवडा',
      'studyParticipation.selectDistrict': 'जिला निवडा',
      'studyParticipation.selectCentre': 'केंद्र निवडा',
      'studyParticipation.enterCentrePlaceholder': 'कृपया केंद्र प्रविष्ट करा',
      'studyParticipation.whichPracticeEngage': 'तुम्ही नियमितपणे किस सिद्धयोग सरावात गुंतलेले आहात?',
      'studyParticipation.selectPractice': 'सराव निवडा',
      'studyParticipation.none': 'काही नाही',
      'studyParticipation.meditation': 'ध्यान',
      'studyParticipation.chanting': 'जप',
      'studyParticipation.yogaAsanas': 'योग आसन',
      'studyParticipation.studyScriptures': 'शास्त्रांचा अभ्यास',
      'studyParticipation.seva': 'सेवा',
      'studyParticipation.otherPractice': 'इतर',
      'studyParticipation.regularlyEngageTeachings': 'तुम्ही नियमितपणे सिद्धयोग शिकवणींच्या अभ्यासात गुंतलेले आहात का?',
      'studyParticipation.yes': 'होय',
      'studyParticipation.no': 'नाही',
      'studyParticipation.ifYesSelected': '\'होय\' निवडल्यास, पर्यायांसाठी बहुविकल्पीय निवड',
      'studyParticipation.selectOptions': 'पर्याय निवडा',
      'studyParticipation.siddhaYogaPathWebsite': 'सिद्धयोग पथ वेबसाइट',
      'studyParticipation.siddhaYogaBooks': 'सिद्धयोग पुस्तकें',
      'studyParticipation.homeStudyCourse': 'घर अध्ययन पाठ्यक्रम',
      'studyParticipation.neeleshwari': 'नीलेश्वरी',
      'studyParticipation.howOftenVisitWebsite': 'आप अपनी सिद्धयोग पथ वेबसाइट पर कितनी बार जाते हैं?',
      'studyParticipation.daily': 'दैनिक',
      'studyParticipation.participateSadhanaCircle': 'क्या आप साधना चक्र में भाग ले रहे हैं?',
      'studyParticipation.howOften': 'कितनी बार?',
      'studyParticipation.firstShaktipatIntensive': 'तुम्ही तुमचा पहिला शक्तिपात इंटेंसिव कोणत्या वर्षी घेतला?',
      'studyParticipation.selectFirstIntensiveYear': 'पहिला इंटेंसिव वर्ष निवडा',
      'studyParticipation.eventParticipationHistory': 'कार्यक्रम सहभाग इतिहास (गेल्या 5 वर्षे)',
      'studyParticipation.eventParticipationHistorySubHeader': 'खालील कार्यक्रमांसाठी, गेल्या पाच वर्षांत तुम्ही सहभागी झालेल्या वर्षांची यादी करा.',
      'studyParticipation.shaktipatIntensive': 'शक्तिपात इंटेंसिव्ह',
      'studyParticipation.audioSatsangs': 'ऑडिओ सत्संग',
      'studyParticipation.liveStreams': 'लाइव स्ट्रीम',
      'studyParticipation.muktanandaDhyanSaptah': 'मुक्तानंद ध्यान सप्ताह',
      'studyParticipation.sweetSurpriseSatsang': 'स्वीट सरप्राइज सत्संग',
      'studyParticipation.retreatHistory': 'रिट्रीट इतिहास',
      'studyParticipation.retreatName': 'रिट्रीट का नाम',
      'studyParticipation.selectRetreatName': 'रिट्रीट का नाम चुनें',
      'studyParticipation.retreatDate': 'रिट्रीट की तारीख',
      'studyParticipation.selectDate': 'तारीख निवडा',
      'studyParticipation.deleteRetreat': 'रिट्रीट हटवा',
      'studyParticipation.addNewRetreat': 'नवीन रिट्रीट जोडा',
      'studyParticipation.sevaHistory': 'सेवा इतिहास',
      'studyParticipation.ashramName': 'आश्रम का नाम',
      'studyParticipation.selectAshramName': 'आश्रम का नाम चुनें',
      'studyParticipation.sevaRole': 'सेवा भूमिका',
      'studyParticipation.department': 'विभाग',
      'studyParticipation.year': 'वर्ष',
      'studyParticipation.duration': 'अवधि',
      'studyParticipation.supervisorName': 'पर्यवेक्षक का नाम',
      'studyParticipation.deleteSeva': 'सेवा हटवा',
      'studyParticipation.addNewSeva': 'नई सेवा जोडा',
      'validation.beginPracticeYearRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.practiceBeginDetailsRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.otherCentreRequired': '\'अन्य\' निवडल्यावर हे फील्ड आवश्यक आहे.',
      'validation.centerVisitFrequencyRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.currentSYPracticesRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.isEngagedWithTeachingsRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.isParticipateInSadhanaCircleRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.firstIntensiveYearRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.retreatNameRequired': 'रिट्रीट का नाम आवश्यक आहे.',
      'validation.retreatDateRequired': 'रिट्रीट की तारीख आवश्यक आहे.',
      'validation.fillAllFieldsBeforeAdding': 'नया रिट्रीट जोडण्यापूर्वी कृपया सभी आवश्यक फ़ील्ड भरें।',
      'validation.atLeastOneRetreatRequired': 'कम से कम 1 रिट्रीट इतिहास प्रविष्टि आवश्यक है।',
      'validation.fillAllFieldsBeforeAddingSeva': 'नई सेवा जोड़ने से पहले कृपया सभी आवश्यक फ़ील्ड भरें।',
      'validation.atLeastOneSevaRequired': 'कम से कम 1 सेवा इतिहास प्रविष्टि आवश्यक है।',

      // Other Personal Information Page
      'otherPersonal.title': 'इतर वैयक्तिक माहिती',
      'otherPersonal.involvedOtherSpiritual': 'क्या आप कभी किसी अन्य आध्यात्मिक संगठनों से जुड़े रहे हैं?',
      'otherPersonal.whichOnesWhen': 'कोणत्या आणि कधी?',
      'otherPersonal.enterOrganisationDetails': 'संगठन तपशील प्रविष्ट करा',
      'otherPersonal.askedToLeaveAshram': 'क्या आपसे कभी सिद्धयोग आश्रम किंवा केंद्र सोडण्यास सांगितले गेले आहे का?',
      'otherPersonal.fromWhereWhenReason': 'कहाँ से, कब आणि कोणत्या कारणासाठी?',
      'otherPersonal.enterReason': 'कारण प्रविष्ट करा',
      'otherPersonal.criminalOffence': 'क्या आप कभी मामूली वाहतूक उल्लंघन व्यतिरिक्त कोणत्याही गुन्ह्याबद्दल दोषी ठरवले गेले आहात का?',
      'otherPersonal.pleaseDescribe': 'कृपया वर्णन करा:',
      'otherPersonal.enterDetails': 'तपशील प्रविष्ट करा',
      'otherPersonal.drugHistory': 'क्या आपके पास पिछले पांच वर्षांत अवैध नशीली दवाओं के उपयोग आणि/किंवा उपचाराचा कोणताही इतिहास आहे का?',
      'validation.involvedOtherSpiritualRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.otherOrganisationDetailsRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.askedToLeaveAshramRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.reasonForAskToLeaveRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.criminalOffenceRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.offenceDetailsRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.drugHistoryRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.drugHistoryDetailsRequired': 'हे फील्ड आवश्यक आहे.',

      // References Page
      'references.title': 'संदर्भ',
      'references.instructionText': 'कृपया सेवा पर्यवेक्षक, सहकारी सिद्ध योगी, किंवा केंद्र नेतृत्वापासून संदर्भ प्रदान करा. कृपया आपल्या कुटुंबातील सदस्याची यादी करू नका.',
      'references.reference1Name': 'संदर्भ 1 - नाव:',
      'references.reference2Name': 'संदर्भ 2 - नाव:',
      'references.emailAddress': 'ई-मेल पत्ता:',
      'references.phoneNumber': 'फोन नंबर:',
      'references.relationship': 'नाते:',
      'references.selectRelationship': '-- नाते निवडा --',
      'references.sevaSupervisor': 'सेवा पर्यवेक्षक',
      'references.fellowSiddhaYogi': 'सहकारी सिद्ध योगी',
      'references.centreLeadership': 'केंद्र नेतृत्व',
      'references.enterName': 'नाव प्रविष्ट करा',
      'references.enterEmail': 'ई-मेल प्रविष्ट करा',
      'references.enterPhoneNumber': 'फोन नंबर प्रविष्ट करा',
      'references.consentText': 'मी वरील संदर्भांशी संपर्क साधण्यास सहमती देतो.',
      'validation.reference1NameRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.reference1EmailRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.reference1EmailInvalid': 'कृपया वैध ई-मेल पत्ता प्रविष्ट करा.',
      'validation.reference1PhoneRequired': 'मोबाइल आवश्यक आहे आणि 10 अंकांचा असावा.',
      'validation.reference1RelationshipRequired': 'यह फ़ील्ड आवश्यक आहे.',
      'validation.reference2NameRequired': 'यह फ़ील्ड आवश्यक आहे.',
      'validation.reference2EmailRequired': 'यह फ़ील्ड आवश्यक आहे.',
      'validation.reference2PhoneRequired': 'मोबाइल आवश्यक आहे आणि 10 अंकांचा असावा.',
      'validation.reference2RelationshipRequired': 'यह फ़ील्ड आवश्यक आहे.',
      'validation.consentRequired': 'पुढे जाण्यासाठी कृपया घोषणा स्वीकारा.',

      // Medical History Page
      'medicalHistory.title': 'वैद्यकीय इतिहास',
      'medicalHistory.height': 'उंची',
      'medicalHistory.weight': 'वजन (किलो)',
      'medicalHistory.enterHeight': 'उंची प्रविष्ट करा',
      'medicalHistory.select': 'निवडा',
      'medicalHistory.bloodPressure': 'रक्तदाब:',
      'medicalHistory.bloodSugarDiabetes': 'रक्त शर्करा किंवा मधुमेह:',
      'medicalHistory.brainStroke': 'मेंदूचा स्ट्रोक:',
      'medicalHistory.heartDisease': 'हृदयरोग:',
      'medicalHistory.tuberculosis': 'क्षयरोग (टीबी):',
      'medicalHistory.asthma': 'दमा:',
      'medicalHistory.cancer': 'कर्करोग:',
      'medicalHistory.hivAids': 'एचआयव्ही / एड्स:',
      'medicalHistory.respiratoryLungProblem': 'श्वसन / फुफ्फुस समस्या:',
      'medicalHistory.visionHearingSpeechProblem': 'दृष्टी, श्रवण किंवा भाषणाशी संबंधित समस्या:',
      'medicalHistory.depressionPsychological': 'तुम्हाला कधीही नैराश्य किंवा इतर मानसिक स्थितीचे निदान झाले आहे का?:',
      'medicalHistory.otherMedicalCondition': 'वरीलपैकी इतर कोणतीही वैद्यकीय स्थिती?',
      'medicalHistory.typhoidFever': 'टायफॉइड ताप?',
      'medicalHistory.psychiatricDisorder': 'मानसिक विकार?',
      'medicalHistory.parasites': 'परजीवी?',
      'medicalHistory.hepatitis': 'हेपॅटायटिस ए, बी, किंवा सी किंवा पिवळसर?',
      'medicalHistory.regularMedication': 'तुम्हाला नियमितपणे औषधे घेण्याची गरज आहे का?',
      'medicalHistory.provideCurrentReading': 'सध्याचे वाचन प्रदान करा',
      'medicalHistory.describeCurrentCondition': 'सध्याच्या स्थितीचे वर्णन करा',
      'medicalHistory.whenTreatmentComplete': 'उपचार कधी पूर्ण झाला?',
      'medicalHistory.specify': 'निर्दिष्ट करा:',
      'medicalHistory.pleaseDescribe': 'कृपया वर्णन करा',
      'medicalHistory.ifYesWhen': 'होय असल्यास, कधी?',
      'medicalHistory.ifYesWhenWhatKind': 'होय असल्यास, कधी आणि कोणत्या प्रकारचे?',
      'medicalHistory.ifYesWhenWhichOne': 'होय असल्यास, कधी आणि कोणते?',
      'medicalHistory.pleaseSpecifyMedicine': 'कृपया औषधांची नावे आणि डोस निर्दिष्ट करा:',
      'medicalHistory.enterBpRange': 'बीपी रेंज प्रविष्ट करा',
      'medicalHistory.enterBloodSugarRange': 'रक्त शर्करा रेंज प्रविष्ट करा',
      'medicalHistory.enterDetails': 'तपशील प्रविष्ट करा',
      'validation.heightRequired': 'उंची आवश्यक आहे.',
      'validation.weightRequired': 'वजन आवश्यक आहे.',
      'validation.bloodPressureRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.bpRangeRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.bloodSugarRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.bloodSugarRangeRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.brainStrokeRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.brainStrokeDetailsRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.heartProblemRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.heartProblemDetailsRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.tuberculosisRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.tuberculosisDetailsRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.asthmaRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.asthmaDetailsRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.cancerRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.cancerDetailsRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.hivRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.hivConditionDetailsRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.lungProblemRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.lungProblemDetailsRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.visionProblemRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.visionProblemDetailsRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.depressionRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.depressionDetailsRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.otherMedicalConditionRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.otherMedicalConditionDetailsRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.typhoidFeverRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.typhoidFeverDetailsRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.psychiatricDisorderRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.psychiatricDisorderDetailsRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.parasitesRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.parasitesDetailsRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.hepatitisRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.hepatitisDetailsRequired': 'हे फील्ड आवश्यक आहे.',
      'validation.regularMedicationRequired': 'यह फ़ील्ड आवश्यक आहे.',
      'validation.medicationsRequired': 'हे फ़ील्ड आवश्यक आहे.',

      // Educational and Work Experience Page
      'educationWork.title': 'शैक्षणिक आणि कामाचा अनुभव',
      'educationWork.educationalBackground': 'शैक्षणिक पार्श्वभूमी',
      'educationWork.workExperience': 'कामाचा अनुभव',
      'educationWork.srNo': 'क्र. सं.',
      'educationWork.degreeDiploma': 'पदवी / डिप्लोमा',
      'educationWork.fieldOfStudySpecialization': 'अभ्यासाचे क्षेत्र / विशेषज्ञता',
      'educationWork.university': 'विद्यापीठ',
      'educationWork.yearOfCompletion': 'पूर्णतेचे वर्ष',
      'educationWork.action': 'क्रिया',
      'educationWork.nameOfCompany': 'कंपनीचे नाव',
      'educationWork.designationJobTitle': 'पदनाम / नोकरीचे शीर्षक',
      'educationWork.fromToYear': 'से - तक (वर्ष)',
      'educationWork.fieldTrade': 'क्षेत्र / व्यापार',
      'educationWork.educationNumber': 'शिक्षण #',
      'educationWork.workExperienceNumber': 'कामाचा अनुभव #',
      'educationWork.enterDegreeDiploma': 'पदवी/डिप्लोमा प्रविष्ट करा',
      'educationWork.enterFieldOfStudy': 'अभ्यासाचे क्षेत्र प्रविष्ट करा',
      'educationWork.enterUniversity': 'विद्यापीठ प्रविष्ट करा',
      'educationWork.enterCompanyName': 'कंपनीचे नाव प्रविष्ट करा',
      'educationWork.enterJobDesignation': 'नोकरीचे पदनाम प्रविष्ट करा',
      'educationWork.from': 'से',
      'educationWork.to': 'तक',
      'educationWork.fieldTradeExample': 'जैसे बिक्री, आईटी, एचआर, वित्त',
      'educationWork.removeEducation': 'शिक्षण हटवा',
      'educationWork.removeWorkExperience': 'कामाचा अनुभव हटवा',
      'educationWork.addEducation': 'शिक्षण जोडा',
      'educationWork.addWorkExperience': 'कामाचा अनुभव जोडा',
      'educationWork.atLeastOneEducationRequired': 'किमान एक शैक्षणिक रेकॉर्ड सर्व आवश्यक फील्डसह पूर्ण केले जाणे आवश्यक आहे.',
      'educationWork.atLeastOneWorkRequired': 'किमान एक कामाचा अनुभव रेकॉर्ड सर्व आवश्यक फील्डसह पूर्ण केले जाणे आवश्यक आहे.',
      'validation.degreeDiplomaRequired': 'पदवी/डिप्लोमा आवश्यक आहे',
      'validation.degreeDiplomaMaxLength': 'पदवी/डिप्लोमा 50 अक्षरांपेक्षा जास्त असू शकत नाही',
      'validation.fieldOfStudyRequired': 'अभ्यासाचे क्षेत्र / विशेषज्ञता आवश्यक आहे',
      'validation.fieldOfStudyMaxLength': 'अभ्यासाचे क्षेत्र / विशेषज्ञता 50 अक्षरांपेक्षा जास्त असू शकत नाही',
      'validation.universityRequired': 'विद्यापीठ आवश्यक आहे',
      'validation.universityMaxLength': 'विद्यापीठ 50 अक्षरांपेक्षा जास्त असू शकत नाही',
      'validation.yearRequired': 'वर्ष आवश्यक आहे',
      'validation.yearInvalidLength': 'वर्ष नक्की 4 अंकांचे असावे',
      'validation.yearInvalidFormat': 'वर्षात फक्त संख्या असाव्यात',
      'validation.yearFuture': 'वर्ष सध्याच्या वर्षापेक्षा जास्त असू शकत नाही',
      'validation.companyNameRequired': 'कंपनीचे नाव आवश्यक आहे',
      'validation.companyNameMaxLength': 'कंपनीचे नाव 50 अक्षरांपेक्षा जास्त असू शकत नाही',
      'validation.designationRequired': 'पदनाम / नोकरीचे शीर्षक आवश्यक आहे',
      'validation.designationMaxLength': 'पदनाम / नोकरीचे शीर्षक 50 अक्षरांपेक्षा जास्त असू शकत नाही',
      'validation.fromYearRequired': 'से वर्ष आवश्यक आहे',
      'validation.fromYearInvalidLength': 'से वर्ष नक्की 4 अंकांचे असावे',
      'validation.fromYearInvalidFormat': 'से वर्षात फक्त संख्या असाव्यात',
      'validation.fromYearFuture': 'से वर्ष सध्याच्या वर्षापेक्षा जास्त असू शकत नाही',
      'validation.toYearRequired': 'तक वर्ष आवश्यक आहे',
      'validation.toYearInvalidLength': 'तक वर्ष नक्की 4 अंकांचे असावे',
      'validation.toYearInvalidFormat': 'तक वर्षात फक्त संख्या असाव्यात',
      'validation.toYearFuture': 'तक वर्ष सध्याच्या वर्षापेक्षा जास्त असू शकत नाही',
      'validation.yearsFuture': 'वर्षे सध्याच्या वर्षापेक्षा जास्त असू शकत नाहीत',
      'validation.invalidYearRange': 'पासून वर्ष पर्यंत वर्षापेक्षा कमी असावे',
      'validation.fieldTradeRequired': 'क्षेत्र / व्यापार आवश्यक आहे',
      'validation.fieldTradeMaxLength': 'क्षेत्र / व्यापार 50 अक्षरांपेक्षा जास्त असू शकत नाही'
      ,

      // Physical Ability Page
      'physicalAbility.title': 'शारीरिक क्षमता',
      'physicalAbility.neckOrShouldersQuestion': 'तुमच्या मानेला किंवा खांद्यांना वेदना किंवा मर्यादा आहेत का?',
      'physicalAbility.backQuestion': 'तुमच्या पाठीमध्ये वेदना किंवा मर्यादा आहेत का?',
      'physicalAbility.kneesQuestion': 'तुमच्या गुडघ्यांना वेदना किंवा मर्यादा आहेत का?',
      'physicalAbility.hipsQuestion': 'तुमच्या नितंबांना वेदना किंवा मर्यादा आहेत का?',
      'physicalAbility.feetQuestion': 'तुमच्या पायांना वेदना किंवा मर्यादा आहेत का?',
      'physicalAbility.handsWristsArmsQuestion': 'तुमच्या हातांना, मनगटांना किंवा बाहूंना वेदना किंवा मर्यादा आहेत का?',
      'physicalAbility.preventedActivitiesQuestion': 'तुम्ही कोणत्या क्रिया करण्यास असमर्थ आहात?',
      'physicalAbility.repetitiveStrainInjuryQuestion': 'तुम्हाला रिपिटेटिव स्ट्रेन इन्ज्युरी झाली आहे का?',
      'physicalAbility.whenAndHowLong': 'कधी आणि किती काळासाठी?',
      'physicalAbility.affectedAreas': 'प्रभावित भागांचे तपशील?',

      // Generic placeholder (used across pages)
      'placeholder.enterDetails': 'तपशील प्रविष्ट करा',

      // Physical Fitness Page
      'physicalFitness.title': 'शारीरिक तंदुरुस्ती',
      'physicalFitness.canClimbStairs': 'तुम्ही 3 मजल्यांच्या पायऱ्या सहज चढू शकता का?',
      'physicalFitness.why': 'का?',
      'physicalFitness.canWalkDistance': 'तुम्ही 1.5 कि.मी. किंवा 1 मैल कोणत्याही समस्येशिवाय चालू शकता का?',
      'physicalFitness.canLiftWeight': 'तुम्ही 4 किग्रॅ (10 पाउंड) कोणत्याही समस्येशिवाय उचलू शकता का?',
      'physicalFitness.canStandDuration': 'तुम्ही 30 मिनटे कोणत्याही समस्येशिवाय उभे राहू शकता का?',
      'physicalFitness.canSitDuration': 'तुम्ही 30 मिनटे कोणत्याही समस्येशिवाय बसू शकता का?',
      'physicalFitness.canBendOver': 'तुम्ही कोणत्याही समस्येशिवाय वाकू शकता का?',
      'physicalFitness.ifNoWhy': 'नसल्यास, का?',
      'physicalFitness.doExerciseRegularly': 'तुम्ही नियमित व्यायाम करता का?',
      'physicalFitness.exerciseType': 'तुम्ही कोणत्या प्रकारचा व्यायाम करता?',
      'physicalFitness.exerciseFrequency': 'तुम्ही किती वेळा व्यायाम करता?',
      'physicalFitness.overallHealth': 'तुमचे एकूण आरोग्य कसे आहे?',
      'physicalFitness.healthPoor': 'खराब',
      'physicalFitness.healthGood': 'चांगले',
      'physicalFitness.healthExcellent': 'उत्कृष्ट',
      'physicalFitness.healthComments': 'तुमच्या आरोग्याबद्दल आणि शारीरिक स्थितीबद्दल आम्हाला काही सांगायचे असल्यास कृपया सांगा:',
      'physicalFitness.forFemalePregnancy': 'महिला विद्यार्थ्यांसाठी: तुम्ही गर्भवती आहात का?',
      'physicalFitness.pregnancyDueDate': 'होय असल्यास, तुमची अपेक्षित तारीख सांगा:',
      'placeholder.enterReason': 'कारण प्रविष्ट करा',
      'placeholder.enterExerciseType': 'व्यायामाचा प्रकार प्रविष्ट करा',
      'placeholder.enterExerciseFrequency': 'व्यायामाची वारंवारता प्रविष्ट करा (उदा., दैनिक, साप्ताहिक)',
      'placeholder.enterComments': 'टिप्पण्या प्रविष्ट करा',
      'placeholder.enterDueDate': 'नियत तारीख प्रविष्ट करा'
      ,

      // Documents Page
      'documents.title': 'दस्तऐवज अपलोड करा',
      'documents.guidelines.photo.header': 'मानक मार्गदर्शक सूचना: पासपोर्ट आकार फोटो',
      'documents.guidelines.photo.li1': 'फोटोची रचना पासपोर्ट आकाराच्या फोटोसारखी असावी.',
      'documents.guidelines.photo.li2': 'अपलोड केलेला फोटो फक्त JPEG किंवा PNG स्वरूपात असावा.',
      'documents.guidelines.photo.li3': 'फाइलचा आकार 5 एमबीपेक्षा कमी ठेवा.',
      'documents.guidelines.photo.li4': 'उत्तम परिणामांसाठी उच्च गुणवत्तेचा फोटो वापरा.',
      'documents.guidelines.photo.li5': 'अपलोड करण्यापूर्वी फोटोची सामग्री तपासा.',
      'documents.guidelines.id.header': 'मानक मार्गदर्शक सूचना: आयडी आणि इतर दस्तऐवज',
      'documents.guidelines.id.li1': 'कृपया आपला आधार कार्ड किंवा पासपोर्ट आयडी दस्तऐवज अपलोड करा.',
      'documents.guidelines.id.li2': 'दस्तऐवज PDF, JPEG किंवा PNG स्वरूपात असल्याची खात्री करा.',
      'documents.guidelines.id.li3': 'फाइलचा आकार 5 एमबीपेक्षा कमी ठेवा.',
      'documents.guidelines.id.li4': 'अपलोड करण्यापूर्वी योग्य दस्तऐवज आहे की नाही याची पुनर्तपासणी करा.',
      'documents.fileInfo.title': 'फाइल अपलोड मार्गदर्शक:',
      'documents.fileInfo.li1': 'पासपोर्ट आकार फोटो: फक्त JPEG/PNG, कमाल 5 एमबी',
      'documents.fileInfo.li2': 'इतर सर्व दस्तऐवज: PDF, JPEG, या PNG, अधिकतम 5 एमबी',
      'documents.fileInfo.li3': 'फाइल आकार मर्यादा सर्व दस्तऐवजांवर लागू होते',
      'documents.table.srNo': 'क्र. नं.',
      'documents.table.documentName': 'दस्तऐवजाचे नाव',
      'documents.table.fileName': 'फाइल नाव',
      'documents.table.action': 'क्रिया',
      'documents.table.infoTitle': 'फाइल निर्बंध:',
      'documents.table.infoText': 'पासपोर्ट फोटो: फक्त JPEG/PNG | सर्व दस्तऐवज: अधिकतम 5एमबी | स्वरूप: PDF, JPEG, PNG',
      'documents.section.mandatory': 'अनिवार्य दस्तऐवज',
      'documents.section.nonMandatory': 'गैर-आवश्यक दस्तऐवज',
      'documents.doc.passportPhoto': 'पासपोर्ट आकार फोटो',
      'documents.doc.aadharCard': 'आधार कार्ड',
      'documents.doc.photoIdProof': 'फोटो आयडी पुरावा',
      'documents.doc.panCard': 'पॅन कार्ड',
      'documents.doc.resume': 'रेझ्युमे',
      'documents.doc.sevaProfileDoc': 'सेवा प्रोफाइल दस्तऐवज',
      'documents.doc.vsDocument': 'व्हीएस दस्तऐवज',
      'documents.doc.other': 'इतर',
      'documents.noFile': 'कोणतीही फाइल अपलोड केलेली नाही',
      'documents.addFileTooltip.photo': 'फाइल जोडा (फक्त JPEG/PNG, कमाल 5एमबी)',
      'documents.addFileTooltip.default': 'फाइल जोडा (PDF/JPEG/PNG, कमाल 5एमबी)',
      'documents.deleteFileTooltip': 'फाइल हटवा',
      'documents.requiredMark': '* आवश्यक',
      'documents.validation.passportRequired': 'पासपोर्ट आकार फोटो आवश्यक आहे. कृपया पुढे जाण्यापूर्वी अपलोड करा.',
      'documents.validation.aadharRequired': 'आधार कार्ड आवश्यक आहे. कृपया पुढे जाण्यापूर्वी अपलोड करा.',
      'documents.uploading': 'फाइल अपलोड होत आहे...',
      'documents.uploadingTitle': 'अपलोड होत आहे',
      'documents.deleting': 'फाइल हटवली जात आहे...',
      'documents.deletingTitle': 'हटवली जात आहे',
      'documents.deleteConfirm': 'तुम्हाला खात्री आहे की तुम्ही हा दस्तऐवज हटवू इच्छिता?',

      // Agreement Page (Marathi)
      'agreement.title': 'अर्जदार करार, प्रतिबद्धता आणि स्वीकृती',
      'agreement.acknowledgement.title': 'गुरुदेव सिद्ध पीठ (आश्रम) येथे भेट देण्याच्या संधीचा विचार करून, मी स्वीकार करतो',
      'agreement.declaration': 'घोषणा: मी येथे प्रमाणित करतो की वरील दिलेली माहिती बरोबर आणि सत्य आहे.',
      'agreement.button.proceed': 'पुढे जा',
      'agreement.clause1': 'या अर्जावरील सर्व माहिती सत्य आणि पूर्ण आहे. मी समजतो की जर मी कोणतीही खोटी माहिती दिली किंवा विचारलेल्या कोणत्याही माहितीला वगळले किंवा चुकीच्या प्रकारे सादर केले, तर माझा अर्ज नाकारला जाऊ शकतो. जर माझा अर्ज स्वीकारल्यानंतर किंवा मी ज्या पदासाठी स्वीकारलो आहे त्यावर काम सुरू केल्यानंतर कोणतीही खोटी माहिती, वगळणे किंवा चुकीचे सादरीकरण आढळले, तर मी समजतो की माझी स्वीकृती रद्द केली जाऊ शकते किंवा माझी गुरुकुल विद्यार्थी स्थिती संपुष्टात आणली जाऊ शकते.',
      'agreement.clause2': 'या अर्जावरील कोणतीही माहिती बदलल्यास, मी त्वरित गुरुदेव सिद्ध पीठ गुरुकुल अर्ज विभागाला सूचित करेन.',
      'agreement.clause3': 'मी समजतो की गुरुदेव सिद्ध पीठ कोणत्याही कारणास्तव गुरुदेव सिद्ध पीठ येथे माझ्या राहण्याच्या कालावधीत बदल करण्याचा अधिकार सुरक्षित ठेवतो.',
      'agreement.clause4': 'मी समजतो की हा अर्ज कोणताही करार किंवा करार नाही. हा गुरुदेव सिद्ध पीठ येथे गुरुकुल विद्यार्थी आणि/किंवा रिट्रीट सहभागी म्हणून स्वीकृतीसाठी विचार करण्यासाठी गुरुदेव सिद्ध पीठ येथे अर्ज आहे. मी समजतो की माझ्याकडून या अर्जाची प्राप्ती आणि/किंवा सादरीकरण हे आश्वासन नाही की गुरुदेव सिद्ध पीठ मला गुरुकुल विद्यार्थी आणि/किंवा रिट्रीट सहभागी म्हणून स्वीकारेल आणि मी स्वीकार करतो की या संदर्भात माझ्याशी कोणतेही वचन दिले गेले नाहीत. मी हे देखील स्वीकार करतो की जर मला गुरुकुल विद्यार्थी आणि/किंवा रिट्रीट सहभागी म्हणून स्वीकारले गेले, तर आश्रमात माझे राहणे कोणत्याही वेळी माझ्याकडून किंवा गुरुदेव सिद्ध पीठ येथे संपुष्टात आणले जाऊ शकते, आणि कोणतेही स्पष्ट किंवा निहित वचने किंवा प्रतिनिधित्व केले गेले नाहीत, या अर्जात विशेषतः नमूद केल्याशिवाय. हा अर्ज गुरुदेव सिद्ध पीठची मालमत्ता राहील.',
      'agreement.clause5': 'मी गुरुदेव सिद्ध पीठच्या प्रतिनिधींना माझ्या पार्श्वभूमी, मागील कामाचा अनुभव आणि शिक्षणाची चौकशी करण्यासाठी आणि माझ्या संदर्भांशी आणि मागील नियोक्त्यांशी संपर्क साधण्यासाठी माझ्याकडून दिलेल्या माहितीची पुष्टी करण्यासाठी आणि माझ्या अर्जाशी संबंधित माझ्याबद्दल इतर माहिती मिळविण्यासाठी अधिकृत करतो. मी कोणत्याही व्यक्ती किंवा संस्थेला अशा चौकशीशी संबंधित गुरुदेव सिद्ध पीठ येथे माझ्याबद्दल माहिती देण्यासाठी अधिकृत करतो आणि ही माहिती देण्यासाठी कोणत्याही व्यक्ती किंवा संस्थेला सर्व दायित्वापासून मुक्त करतो.',
      'agreement.clause6': 'मी समजतो की माझ्या अर्जाशी संबंधित सर्व निर्णय गुरुदेव सिद्ध पीठ येथे घेतले जातात.',
      'agreement.clause7': 'मी स्वीकार करतो की मीने हा अर्ज वाचला आहे आणि पूर्णपणे समजून घेतला आहे ज्यामध्ये पार्श्वभूमी चौकशीसाठी वरील विधाने आणि प्राधिकरण समाविष्ट आहे. मी सहमत आहे की हा अर्ज इलेक्ट्रॉनिक पद्धतीने सादर करून हे प्रमाणपत्र मूळ प्रमाणे बंधनकारक आणि प्रभावी असेल. मी हे देखील सहमत आहे की या प्रमाणपत्राची स्कॅन केलेली किंवा फॅक्स स्वाक्षरी प्रत मूळ प्रमाणे बंधनकारक आणि प्रभावी असेल.',
      'agreement.clause8': 'मी स्वीकार करतो की गुरुदेव सिद्ध पीठ सिद्ध योग ध्यानाचा सराव करणाऱ्या व्यक्तींना निवडण्याचा अधिकार सुरक्षित ठेवतो, त्यांच्या सिद्ध योग पार्श्वभूमी तसेच इतर कौशल्ये आणि पात्रता यावर आधारित.',
      'agreement.clause9': 'मीने वरील विधाने वाचली आहेत, समजून घेतली आहेत आणि त्यांच्याशी सहमत आहे.',
      'agreement.acknowledgement.clause1': 'मी समजतो आणि स्वीकार करतो की गुरुदेव सिद्ध पीठ मला आश्रमात राहण्याची परवानगी देत आहे, या रिलीज, वेव्हर आणि इंडेम्निटीच्या घोषणा आणि करारावर (करार) आंशिक अवलंबून जो मीने आश्रमात राहण्याच्या पूर्व शर्त म्हणून स्वतंत्रपणे निष्पादित केला आहे.',
      'agreement.acknowledgement.clause2': 'माझ्याकडे आरोग्य विमा आणि वित्तीय साधने आहेत किंवा असतील जी माझ्या भेटीदरम्यान उद्भवू शकणाऱ्या कोणत्याही आरोग्य गरजा किंवा आपत्कालीन परिस्थितीचे निराकरण करण्यासाठी आवश्यक आहेत.',
      'agreement.acknowledgement.clause3': 'मी सर्व खर्च वहन करण्याचा उत्तरदायित्व स्वीकारतो, यामध्ये वैद्यकीय खर्च समाविष्ट आहेत जे माझ्या राहण्यादरम्यान किंवा आश्रमातून माझ्या निघण्याशी किंवा परतण्याशी संबंधित कोणत्याही अपघात, मालमत्तेची हानी किंवा आजारामुळे होणारे किंवा होण्याची शक्यता असलेले आहेत.',
      'agreement.acknowledgement.clause4': 'मी स्वीकार करतो की GSP माझ्या भेटीदरम्यान माझ्या कोणत्याही वैद्यकीय गरजांकडे लक्ष देण्यासाठी बंधनकारक नाही. माझ्या भेटीदरम्यान वैद्यकीय आपत्कालीन परिस्थितीत, मी योग्य वैद्यकीय उपचार सुरक्षित करण्यास सक्षम नाही; मीने GSPच्या प्रतिनिधीला आवश्यक असलेला कोणताही उपचार सुरक्षित करण्यासाठी अधिकृत केले आहे. GSP परिस्थितीनुसार योग्य समजले जाणारे कोणतेही कृती करू शकतो, परंतु बंधनकारक नाही, माझ्या आरोग्य आणि सुरक्षिततेबाबत. अशा कृती GSP आणि माझ्यात विशेष संबंध निर्माण करत नाहीत.',
      'agreement.acknowledgement.clause5': 'मी समजतो की गुरुदेव सिद्ध पीठच्या सर्वोत्तम प्रयत्नांनंतरही, मी माझ्या भेटीदरम्यान आजारी किंवा जखमी होऊ शकतो, आणि मी गुरुदेव सिद्ध पीठ, त्याच्या ट्रस्टींना आणि/किंवा एजंटांना माझ्या भेटीशी संबंधित जखमा, आजार किंवा इतर हानीसाठी माझ्याकडे असू शकणाऱ्या सर्व दाव्यांपासून मुक्त करतो, आणि लापरवाहीच्या दाव्यांसह अशा दाव्यांना आणण्याचे सर्व अधिकार सोडतो.',
      'agreement.acknowledgement.clause6': 'मी आश्रमात माझ्या राहण्याशी संबंधित किंवा त्यातून उद्भवणाऱ्या सर्व जोखमी, खर्च आणि जबाबदाऱ्या पूर्णपणे स्वीकारतो.',
      'agreement.acknowledgement.clause7': 'मी गुरुदेव सिद्ध पीठला माझ्या बाजूने केलेल्या कोणत्याही कृती किंवा चुकीच्या कृती किंवा येथे नमूद केलेल्या प्रतिबद्धता किंवा उत्तरदायित्वांच्या माझ्या बाजूने केलेल्या कोणत्याही उल्लंघनाच्या परिणामी गुरुदेव सिद्ध पीठ येथे झालेल्या सर्व दाव्यांविरुद्ध बिनशर्त नुकसानभरपाई करेन आणि हानिरहित ठेवेन.',
      'agreement.acknowledgement.clause8': 'मी येथे गुरुदेव सिद्ध पीठला सर्व तक्रारी, दावे, विवाद, दायित्वे, मागण्या आणि कार्याची कारणे ज्ञात किंवा अज्ञात, निश्चित किंवा आकस्मिक, जी मी गुरुकुल विद्यार्थी म्हणून आश्रमात माझ्या राहण्याच्या परिणामी गुरुदेव सिद्ध पीठविरुद्ध ठेवू शकतो किंवा ठेवण्याचा दावा करू शकतो, यापासून मुक्त आणि कायमस्वरूपी मुक्त करतो, आणि येथे सहमत आहे की अशा दाव्यांना स्थापित करण्यासाठी कोणताही दावा दाखल करणार नाही किंवा कोणताही अन्य अर्ज करणार नाही.',
      'agreement.acknowledgement.clause9': 'वरील रिलीज आणि हानिरहित करार माझ्या उत्तराधिकारी, वारसदार आणि नियुक्त केलेल्या व्यक्तींवर बंधनकारक असतील.',
      'agreement.acknowledgement.clause10': 'मी स्वीकार करतो की मीने वरील विधाने वाचली आहेत, समजून घेतली आहेत आणि त्यांच्याशी सहमत आहे.',

      // Summary & Requirements Modal - Gurukula Student (Marathi)
      'modal.gurukula.title': 'गुरुकुल विद्यार्थी: सेवा निवासासाठी अर्ज',
      'modal.gurukula.subtitle': 'सहा महिने किंवा त्यापेक्षा जास्त कालावधीसाठी सेवा निवासासाठी अर्ज',
      'modal.gurukula.req.age18+': 'अर्जदाराचे वय 18 वर्षे किंवा त्यापेक्षा अधिक असणे आवश्यक आहे.',
      'modal.gurukula.req.sadhana2y': 'किमान दोन वर्षांच्या सिद्ध योग साधनेचा सराव आवश्यक आहे.',
      'modal.gurukula.req.intensive3y': 'गेल्या तीन वर्षांत किमान एक सिद्ध योग शक्तिपात इंटेन्सिवमध्ये सहभाग आवश्यक आहे.',
      'modal.gurukula.req.sweetSurprise': 'गेल्या दोन वर्षांत एक स्वीट सरप्राइज सत्संगमध्ये सहभाग आवश्यक आहे.',
      'modal.gurukula.req.understandingSeva': 'सेवा हे आध्यात्मिक सराव म्हणून समजून घेणे आवश्यक आहे.',
      'modal.gurukula.req.visitingSeva': 'गेल्या पाच वर्षांत गुरुदेव सिद्ध पीठ येथे विजिटिंग सेवित म्हणून सेवा दिली असणे आवश्यक आहे.',
      'modal.gurukula.req.sixMonths': 'सहा महिने किंवा त्यापेक्षा जास्त कालावधीसाठी सेवा देण्यासाठी समर्पित होण्यास सक्षम असणे आवश्यक आहे.',
      'modal.gurukula.req.language': 'सेवा सूचना (लेखी आणि मौखिक) समजण्यासाठी आणि साधना अनुभव सामायिक करण्यासाठी इंग्रजी आणि/किंवा हिंदीमध्ये प्राविण्य आवश्यक आहे.',
      'modal.gurukula.req.health': 'सेवा देण्यासाठी आणि आश्रमाच्या दैनंदिन वेळापत्रकात पूर्ण सहभागासाठी चांगले आरोग्य आणि शारीरिक तंदुरुस्ती आवश्यक आहे.',
      'modal.gurukula.req.centreParticipation2y': 'गेल्या दोन वर्षांसाठी सिद्ध योग ध्यान केंद्र किंवा सिद्ध योग जप आणि ध्यान गटात तसेच जागतिक सिद्ध योग वेबकास्टमध्ये नियमित सहभाग वांछनीय आहे.',
      'modal.gurukula.req.sevaParticipation1y': 'गेल्या एका वर्षासाठी सिद्ध योग ध्यान केंद्र, आश्रम किंवा जप आणि ध्यान गटात सेवेत नियमित सहभाग वांछनीय आहे.',

      // Summary & Requirements Modal - Pilgrimage to the Heart Retreat (Marathi)
      'modal.pth.title': 'हृदयाच्या तीर्थयात्रेचे रिट्रीट',
      'modal.pth.subtitle': 'सिद्ध योग विद्यार्थ्यांसाठी त्यांची साधना तीव्र करण्याची संधी. रिट्रीटच्या प्रत्येक दिवशी जप, ध्यान, चिंतन, अभ्यास, हठ योग, मौनाचा सराव यांचा समावेश असतो, आणि या सर्वांच्या मध्यभागी, आंतरिक आत्म्याशी असलेला गहरत जाणारा संबंध.',
      'modal.pth.req.age18+': 'अर्जदाराचे वय 18 वर्षे किंवा त्यापेक्षा अधिक असणे आवश्यक आहे.',
      'modal.pth.req.sadhana2y': 'किमान दोन वर्षांच्या सिद्ध योग साधनेचा सराव आवश्यक आहे.',
      'modal.pth.req.sadhana1yNote': '18 ते 29 वर्ष वयोगटातील विद्यार्थ्यांसाठी, किमान एक वर्षाच्या सिद्ध योग साधनेचा सराव आवश्यक आहे.',
      'modal.pth.req.intensive3y': 'गेल्या तीन वर्षांत किमान एक सिद्ध योग शक्तिपात इंटेन्सिवमध्ये सहभाग आवश्यक आहे.',
      'modal.pth.req.dailyMeditation': 'गेल्या एका वर्षासाठी दैनंदिन ध्यानाचा सराव आवश्यक आहे.',
      'modal.pth.req.language': 'ज्या भाषेत रिट्रीट आयोजित केला जातो त्या भाषेत प्राविण्य आवश्यक आहे.',
      'modal.pth.req.health': 'सकाळपासून संध्याकाळपर्यंत रिट्रीट वेळापत्रकात पूर्ण सहभागासाठी चांगले आरोग्य आणि शारीरिक तंदुरुस्ती आवश्यक आहे.',
      'modal.pth.req.dakshina': 'दक्षिणेचा नियमित सराव वांछनीय आहे.',
      'modal.pth.req.centreParticipation2y': 'गेल्या दोन वर्षांसाठी सिद्ध योग ध्यान केंद्र किंवा सिद्ध योग जप आणि ध्यान गटात तसेच जागतिक सिद्ध योग वेबकास्टमध्ये नियमित सहभाग वांछनीय आहे.',
      'modal.pth.req.sevaParticipation1y': 'गेल्या एका वर्षासाठी सिद्ध योग ध्यान केंद्र, आश्रम किंवा जप आणि ध्यान गटात सेवेत नियमित सहभाग वांछनीय आहे.',

      // Summary & Requirements Modal - Siddha Yoga Family Retreat (Marathi)
      'modal.familyRetreat.title': 'सिद्ध योग फॅमिली रिट्रीट',
      'modal.familyRetreat.subtitle': 'सिद्ध योग फॅमिली रिट्रीटमध्ये, 10 ते 17 वर्ष वयोगटातील मुलांसह कुटुंबे आत्म्याचा सन्मान करण्याचे मार्ग शिकतात आणि सराव करतात जे त्यांच्यामधून, इतरांमधून आणि त्यांच्या सभोवतालच्या जगात प्रकट होतात.',
      'modal.familyRetreat.parents.title': 'अर्ज करण्यासाठीची पूर्वअट: पालकांसाठी',
      'modal.familyRetreat.parents.sadhana2y': 'किमान दोन वर्षांच्या सिद्ध योग साधनेचा सराव आवश्यक आहे.',
      'modal.familyRetreat.parents.intensive3y': 'गेल्या तीन वर्षांत किमान एक सिद्ध योग शक्तिपात इंटेन्सिवमध्ये सहभाग आवश्यक आहे.',
      'modal.familyRetreat.parents.hindi': 'हिंदीमध्ये प्राविण्य आवश्यक आहे.',
      'modal.familyRetreat.parents.health': 'रिट्रीट सत्रांमध्ये आणि आश्रमाच्या दैनंदिन वेळापत्रकात पूर्ण सहभागासाठी चांगले आरोग्य आणि शारीरिक तंदुरुस्ती आवश्यक आहे.',
      'modal.familyRetreat.parents.centreParticipation1y': 'गेल्या एका वर्षासाठी सिद्ध योग ध्यान केंद्र किंवा सिद्ध योग जप आणि ध्यान गटात तसेच जागतिक सिद्ध योग वेबकास्टमध्ये नियमित सहभाग वांछनीय आहे.',
      'modal.familyRetreat.parents.sevaParticipation1y': 'गेल्या एका वर्षासाठी सिद्ध योग ध्यान केंद्र, आश्रम किंवा जप आणि ध्यान गटात सेवेत नियमित सहभाग वांछनीय आहे.',
      'modal.familyRetreat.parents.dakshina': 'दक्षिणेचा नियमित सराव वांछनीय आहे.',
      'modal.familyRetreat.children.title': 'अर्ज करण्यासाठीची पूर्वअट: मुलांसाठी',
      'modal.familyRetreat.children.age10to17': '10 ते 17 वर्ष वयोगट आवश्यक आहे.',
      'modal.familyRetreat.children.example': 'केवळ स्पष्टीकरणाच्या उद्देशाने उदाहरण: जर मुलाची जन्मतिथी 1 जानेवारी 2000 असेल आणि आश्रमात आगमनाची तारीख 1 जानेवारी 2010 आणि 31 डिसेंबर 2017 यांच्या दरम्यान असेल, तर तो/ती सिद्ध योग फॅमिली रिट्रीटसाठी पात्र आहे/आहे.',
      'modal.familyRetreat.children.hindi': 'हिंदीमध्ये मूलभूत प्राविण्य आवश्यक आहे.',
      'modal.familyRetreat.children.motivation': 'मुलाला रिट्रीट सत्रांमध्ये आणि आश्रमाच्या दैनंदिन वेळापत्रकात सहभागी होण्यासाठी स्व-प्रेरित असणे आवश्यक आहे.',
      'modal.familyRetreat.children.intensive': 'सिद्ध योग शक्तिपात इंटेन्सिवमध्ये सहभागी झाले असणे वांछनीय आहे.',
      'modal.familyRetreat.children.satsang': 'गेल्या दोन वर्षांत मुलांच्या किंवा युवा सत्संगांमध्ये नियमित सहभाग वांछनीय आहे.',

      // Summary & Requirements Modal - Siddha Yoga Retreat for Young Adults (Marathi)
      'modal.kypr.title': 'तरुण प्रौढांसाठी सिद्ध योग रिट्रीट',
      'modal.kypr.subtitle': 'गुरुदेव सिद्ध पीठच्या स्वागतपूर्ण वातावरणात, तरुण सिद्ध योग विद्यार्थी जप आणि ध्यानाच्या सिद्ध योग सरावांमध्ये सहभागी होतात, तसेच सक्रिय शिक्षण सत्रांमध्ये आणि निसर्गात शांत वेळेतही. जसजसे सहभागी रिट्रीटमध्ये पूर्णपणे गुंतले जातात, तसतसे ते आंतरिक आत्म्याशी असलेला संबंध मजबूत करतात. आध्यात्मिक सराव आणि या परिवर्तनकारी वातावरणातील अभ्यासाशी त्यांच्या गतिशील सहभागाद्वारे, ते त्यांच्या साधनेत स्थायी लाभ आणतात.',
      'modal.kypr.req.age18to30': '18 ते 30 वर्ष वयोगट आवश्यक आहे.',
      'modal.kypr.req.ageExample': 'केवळ स्पष्टीकरणाच्या उद्देशाने उदाहरण: जर अर्जदाराची जन्मतिथी 1 जानेवारी 2000 असेल आणि आश्रमात आगमनाची तारीख 1 जानेवारी 2018 आणि 31 डिसेंबर 2030 यांच्या दरम्यान असेल, तर तो/ती तरुण प्रौढांसाठी सिद्ध योग रिट्रीटसाठी पात्र आहे/आहे.',
      'modal.kypr.req.sadhana1y': 'किमान एक वर्षाच्या सिद्ध योग साधनेचा सराव आवश्यक आहे.',
      'modal.kypr.req.intensive3y': 'गेल्या तीन वर्षांत किमान एक सिद्ध योग शक्तिपात इंटेन्सिवमध्ये सहभाग आवश्यक आहे.',
      'modal.kypr.req.dailyMeditation': 'गेल्या एका वर्षासाठी दैनंदिन ध्यानाचा सराव आवश्यक आहे.',
      'modal.kypr.req.hindi': 'हिंदीमध्ये प्राविण्य आवश्यक आहे.',
      'modal.kypr.req.health': 'रिट्रीट सत्रांमध्ये आणि आश्रमाच्या दैनंदिन वेळापत्रकात पूर्ण सहभागासाठी चांगले आरोग्य आणि शारीरिक तंदुरुस्ती आवश्यक आहे.',
      'modal.kypr.req.centreParticipation1y': 'गेल्या एका वर्षासाठी सिद्ध योग ध्यान केंद्र किंवा सिद्ध योग जप आणि ध्यान गटात तसेच जागतिक सिद्ध योग वेबकास्टमध्ये नियमित सहभाग वांछनीय आहे.',
      'modal.kypr.req.sevaParticipation1y': 'गेल्या एका वर्षासाठी सिद्ध योग ध्यान केंद्र, आश्रम किंवा जप आणि ध्यान गटात सेवेत नियमित सहभाग वांछनीय आहे.',

      // Summary & Requirements Modal - Visiting Seva (Marathi)
      'modal.visitingSeva.title': 'विजिटिंग सेवा: सेवा निवासासाठी अर्ज',
      'modal.visitingSeva.subtitle': 'एक ते पाच महिन्यांच्या सेवा निवासासाठी अर्ज',
      'modal.common.prerequisitesTitle': 'अर्ज करण्यासाठीची पूर्वअट:',
      'modal.common.ageNote': '(वयाच्या मर्यादा आगमनाच्या दिवशी लागू आणि अंमलात आणल्या जातात.)',
      'modal.common.guardianshipNote': '(पालकत्व प्रक्रियेचे तपशील जाणून घेण्यासाठी, कृपया गुरुदेव सिद्ध पीठच्या अर्ज विभागाशी संपर्क साधा.)',
      'modal.common.exampleTitle': 'स्पष्टीकरणाच्या उद्देशाने उदाहरण:',
      'modal.visitingSeva.req.age18+': 'अर्जदाराचे वय 18 वर्षे किंवा त्यापेक्षा अधिक असणे आवश्यक आहे.',
      'modal.visitingSeva.req.sadhana1y': 'किमान एक वर्षाचा सिद्ध योग साधनेचा सराव आवश्यक आहे.',
      'modal.visitingSeva.req.intensive3y': 'गेल्या तीन वर्षांत किमान एक सिद्ध योग शक्तिपात इंटेन्सिव्हमध्ये सहभाग आवश्यक आहे.',
      'modal.visitingSeva.req.language': 'सेवा सूचना (लेखी आणि मौखिक) समजण्यासाठी आणि साधना अनुभव सामायिक करण्यासाठी इंग्रजी आणि/किंवा हिंदीमध्ये मूलभूत प्राविण्य आवश्यक आहे.',
      'modal.visitingSeva.req.health': 'सेवा देण्यासाठी आणि आश्रमाच्या दैनंदिन वेळापत्रकात पूर्ण सहभागासाठी चांगले आरोग्य आणि शारीरिक तंदुरुस्ती आवश्यक आहे.',
      'modal.visitingSeva.req.centreParticipation': 'गेल्या एका वर्षासाठी सिद्ध योग ध्यान केंद्र किंवा जप आणि ध्यान गटात तसेच जागतिक सिद्ध योग वेबकास्टमध्ये नियमित सहभाग असणे वांछनीय आहे.',
      'modal.visitingSeva.req.sevaParticipation': 'गेल्या एका वर्षासाठी सिद्ध योग ध्यान केंद्र, आश्रम किंवा जप आणि ध्यान गटात सेवेत नियमित सहभाग असणे वांछनीय आहे.',
      'modal.visitingSeva.children5to12.title': 'विजिटिंग सेवा मुलांसाठी: वय 5 ते 12 वर्षे',
      'modal.visitingSeva.children5to12.req1': 'पाच ते नऊ वयातील मुलांना दोन्ही पालकांसह येणे आवश्यक आहे.',
      'modal.visitingSeva.children5to12.req2': '10 ते 12 वयातील मुलांना दोन्ही पालक किंवा एक पालक आणि एक अभिभावकासह येणे आवश्यक आहे.',
      'modal.visitingSeva.children5to12.req3': 'किमान एक पालकाने आपल्या मुलासोबत सतत राहण्यास सहमती दिली पाहिजे.',
      'modal.visitingSeva.children5to12.req4': 'मुलासोबत येणारे पालक आणि अभिभावकांनी विजिटिंग सेवाच्या आवश्यकता पूर्ण केल्या पाहिजेत.',
      'modal.visitingSeva.children5to12.req5': 'पालक आपल्या मुलासोबत सात दिवसांसाठी अर्ज करू शकतात.',
      'modal.visitingSeva.children5to12.req6': 'वेबसाइटवर पोस्ट केलेल्या रिट्रीट तारखांव्यतिरिक्त कधीही गुरुदेव सिद्ध पीठ येथे अर्ज करू शकतात.',
      'modal.visitingSeva.children13to17.title': 'विजिटिंग सेवा मुलांसाठी: वय 13 ते 17 वर्षे',
      'modal.visitingSeva.children13to17.req1': 'किमान एक पालक किंवा त्याच लिंगाचा अभिभावक सोबत असणे आवश्यक आहे.',
      'modal.visitingSeva.children13to17.req2': 'मुलासोबत येणाऱ्या पालक/अभिभावकांनी विजिटिंग सेवाइट आवश्यकता पूर्ण केल्या पाहिजेत.',
      'modal.visitingSeva.children13to17.req3': 'रिट्रीट दरम्यान वगळता, गुरुदेव सिद्ध पीठ येथे सेवा देण्यासाठी अर्ज करू शकतात.',
      'modal.visitingSeva.example.p1': "जर मुलाचा जन्मदिनांक 1 जानेवारी 2000 असेल आणि आश्रमात आगमनाची तारीख 1 जानेवारी 2005 ते 31 डिसेंबर 2009 दरम्यान असेल, तर मुलाने दोन्ही पालकांसह यायला हवे. जर आश्रमात मुलाच्या आगमनाची तारीख 1 जानेवारी 2010 ते 31 डिसेंबर 2012 दरम्यान असेल, तर मुलाने दोन्ही पालक किंवा एक पालक आणि एक अभिभावकासह यायला हवे.",
      'modal.visitingSeva.example.p2': "जर मुलाचा जन्मदिनांक 1 जानेवारी 2000 असेल आणि आश्रमात आगमनाची तारीख 1 जानेवारी 2013 ते 31 डिसेंबर 2017 दरम्यान असेल, तर मुलासोबत किमान एक पालक किंवा त्याच लिंगाचा अभिभावक असणे आवश्यक आहे.",
      // Contemplation (Visiting Seva)
      'contemplation.title': 'चिंतन प्रश्न',
      'contemplation.chooseSubmissionTitle': 'तुमची सबमिशन पद्धत निवडा',
      'contemplation.chooseSubmissionSubtitle': 'चिंतन उत्तर कसे सादर कराल ते निवडा',
      'contemplation.card.form.title': 'ऑनलाइन फॉर्म भरा',
      'contemplation.card.form.description': 'खालील फॉर्ममध्ये थेट उत्तरे द्या',
      'contemplation.card.file.title': 'PDF अपलोड करा',
      'contemplation.card.file.description': 'तुमचा पूर्ण चिंतन दस्तऐवज अपलोड करा',
      'contemplation.tooltip': 'हे एक टूलटिप आहे',
      'contemplation.alert.title': 'कृपया चिंतन प्रश्नांना उत्तर देण्यापूर्वी वाचा',
      'contemplation.alert.intro': 'खालील प्रत्येक प्रश्नासाठी किमान 100 शब्दांमध्ये उत्तर लिहा. कृपया तुमच्या उत्तरांमध्ये विशिष्ट रहा आणि शक्य तिथे उदाहरणे द्या.',
      'contemplation.alert.demonstrate': 'तुमचे उत्तर हे दर्शवेल की:',
      'contemplation.alert.point1': 'तुम्ही सिद्धयोग ध्यानाशी सजग आणि सातत्यपूर्ण संलग्नतेसाठी स्वतःला समर्पित केले आहे.',
      'contemplation.alert.point2': 'तुम्ही तुमच्या शिक्षण, अनुभव आणि आध्यात्मिक मार्गाची जबाबदारी स्वीकारली आहे.',
      'contemplation.alert.point3': 'तुम्ही तुमची समज आणि अनुभव स्पष्टपणे व्यक्त करण्याचा प्रयत्न केला आहे.',
      'contemplation.answerPlaceholder': 'कृपया येथे उत्तर लिहा',
      'contemplation.words': 'शब्द',
      'contemplation.q1a.label': '1. तुमच्या जीवनात खालीलचा अभ्यास आणि सराव तुम्ही कसा करत आहात? A. सिद्धयोग शिकवणी आणि सराव [विशिष्ट शिकवणी, सराव (वारंवारता, कालावधी) इ. नमूद करा]:',
      'contemplation.q1b.label': 'B. सिद्धयोग पथ वेबसाइट [विशिष्ट घटक (टॉक्स, टीका, फोटो गॅलरीज इ.), तुम्ही त्यांच्याशी कसे जोडता, किती वेळा, इ.]:',
      'contemplation.q2.intro': '2. सिद्ध योगाच्या शिकवणी आणि सरावाशी तुमच्या संलग्नतेतून तुम्हाला मिळालेला एक विशिष्ट लाभ उदाहरणांसह वर्णन करें:',
      'contemplation.q2a.label': 'A. सिद्धयोग शिकवणी आणि सराव:',
      'contemplation.q2b.label': 'B. सिद्धयोग पथ वेबसाइट:',
      'contemplation.q3.label': '3. सेवा हे गुण शिकण्याची आणि विकसित करण्याची संधी आहे. सेवा करताना तुम्ही स्वतःवर कसा काम कराल?',
      'contemplation.q4.label': '4. तुम्ही आश्रमाचा दैनिक अनुशासन आणि वेळापत्रक कसे पाळाल?',
      'contemplation.q5.label': '5. सेवा तुम्हाला श्री गुरुमईंच्या शिकवणी समजून घेण्यास आणि अंमलात आणण्यास कशी मदत करते?',

      // Additional validation used in contemplation
      'validation.minLength100': 'किमान लांबी 100 अक्षरे आहे.',
      'validation.maxLength1000': 'कमाल लांबी 1000 अक्षरे आहे.',

      // Accommodation Details (Visiting Seva)
      'accommodation.title': 'निवास व्यवस्था',
      'accommodation.housingPreference.label': 'तुमची निवास पसंती काय आहे?:',
      'accommodation.housingPreference.options.dorm': 'डॉर्म',
      'accommodation.housingPreference.options.double': 'डबल',
      'accommodation.housingPreference.options.doubleAc': 'डबल (एसीसह)',
      'accommodation.housingPreference.options.single': 'सिंगल',
      'accommodation.housingPreference.options.singleAc': 'सिंगल (एसीसह)',
      'accommodation.validation.housingPreferenceRequired': 'निवास पसंती आवश्यक आहे.',
      'accommodation.snoreQuestion': 'तुम्हाला कधी सांगितले आहे का की तुम्ही घोरता?',
      'accommodation.validation.snoreRequired': 'घोरण्याची माहिती आवश्यक आहे.',
      'accommodation.roommateConcernQuestion': 'गुरुदेव सिद्ध पीठ येथे रूममेट्ससोबत राहण्याबद्दल तुम्हाला काही चिंता आहेत का?',
      'accommodation.validation.roommateConcernRequired': 'रूममेट चिंतेची माहिती आवश्यक आहे.',
      'accommodation.describeConcerns': 'कृपया वर्णन करा:',
      'accommodation.otherRequests': 'निवास व्यवस्थेशी संबंधित काही विनंत्या आहेत का?',
      'accommodation.placeholder.enterRequests': 'विनंत्या प्रविष्ट करा',

      // Skills (Visiting Seva)
      'skills.title': 'कौशल्य',
      'skills.instructions': 'इतर कौशल्ये (खाली दिलेल्या कोणत्याही क्षेत्रात तुमचे कौशल्य स्तर चिन्हांकित करा: 1 = मूलभूत, 2 = चांगले, 3 = अतिशय चांगले)',
      'skills.level.basic': 'मूलभूत',
      'skills.level.good': 'चांगले',
      'skills.level.veryGood': 'अतिशय चांगले',
      'skills.category.management': 'व्यवस्थापन',
      'skills.category.design': 'डिझाईन',
      'skills.category.arts': 'कला',
      'skills.category.construction': 'बांधकाम',
      'skills.category.finance': 'वित्त',
      'skills.category.foodServices': 'अन्नसेवा',
      'skills.category.healthMedical': 'आरोग्य आणि वैद्यकीय',
      'skills.category.technical': 'तांत्रिक',
      'skills.software.word': 'वर्ड',
      'skills.software.excel': 'एक्सेल',
      'skills.software.powerPoint': 'पावरपॉइंट',
      'skills.software.outlook': 'आउटलुक',
      'skills.otherSkills': 'इतर कौशल्ये',
      'skills.placeholder.otherSkills': 'इतर कौशल्ये नमूद करा',

      // Tentative Dates (Visiting Seva)
      'tentativeDates.title': 'सेवा करण्यासाठी उपलब्ध तारखा',
      'tentativeDates.tooltip': 'हे एक टूलटिप आहे',
      'tentativeDates.info.readBefore': 'तुमच्या उपलब्ध तारखा निवडण्यापूर्वी खालील माहिती वाचा',
      'tentativeDates.info.paragraph1a': 'खालील जागेत, तुम्ही येण्यासाठी उपलब्ध असलेल्या तारखा नमूद करा',
      'tentativeDates.info.gsp': 'गुरुदेव सिद्ध पीठ (GSP)',
      'tentativeDates.info.paragraph1b': 'जेव्हा सेवा संधी तुमच्या कौशल्यांशी आणि उपलब्धतेशी जुळते, तेव्हा तुम्हाला GSP येथे सेवा देण्यासाठी आमंत्रित केले जाऊ शकते. हे आमंत्रण पुरेसा आधी दिले जाईल जेणेकरून तुम्ही प्रवास आणि वैयक्तिक व्यवस्था सहज करू शकाल.',
      'tentativeDates.info.paragraph1c': 'GSP येथे येण्यासाठी तुम्हाला औपचारिकरित्या आमंत्रित केल्यावरच तुमचा मुक्काम निश्चित होतो.',
      'tentativeDates.factors.title': 'उपलब्ध तारखा निवडताना लक्षात ठेवण्यासारखे घटक',
      'tentativeDates.factors.healthTitle': 'चांगले आरोग्य:',
      'tentativeDates.factors.healthText': 'आश्रमाच्या दैनंदिन वेळापत्रकात पूर्णपणे सहभागी होण्यासाठी तुमचे आरोग्य चांगले असणे आवश्यक आहे.',
      'tentativeDates.factors.weatherTitle': 'हवामान:',
      'tentativeDates.factors.weatherText': 'मान्सूनमध्ये गणेशपुरी येथे दीर्घकाळ मुसळधार पाऊस आणि जास्त आर्द्रता असते. उन्हाळ्यात गणेशपुरी खूप उष्ण आणि दमट असते. तुम्हाला बुरशी किंवा आर्द्रतेची संवेदनशीलता असल्यास, मान्सून किंवा उन्हाळ्यात येऊ नका.',
      'tentativeDates.factors.recentEventsTitle': 'अलीकडे खालीलपैकी कोणताही अनुभव आल्यास, किमान 6 महिन्यांनंतर किंवा पूर्ण बरे झाल्यावर, जे नंतर असेल तेव्हा या:',
      'tentativeDates.factors.recent.divorce': 'घटस्फोट किंवा दीर्घकालीन नातेसंबंध समाप्त होणे',
      'tentativeDates.factors.recent.death': 'प्रिय व्यक्तीचा मृत्यू',
      'tentativeDates.factors.recent.addictionRecovery': 'दारू किंवा अमली पदार्थांच्या व्यसनातून सावरले',
      'tentativeDates.factors.recent.surgeryIllness': 'मोठी शस्त्रक्रिया किंवा गंभीर आजार',
      'tentativeDates.factors.recent.mentalHealthTreatment': 'नैराश्य किंवा इतर मानसिक आरोग्य उपचार',
      'tentativeDates.arrivalDate': 'अनुरोधित आगमन तारीख:',
      'tentativeDates.arrivalDatePlaceholder': 'अनुरोधित आगमन तारीख निवडा',
      'tentativeDates.departureDate': 'अनुरोधित प्रस्थान तारीख:',
      'tentativeDates.departureDatePlaceholder': 'अनुरोधित प्रस्थान तारीख निवडा',
      'tentativeDates.arrivalBeforeDeparture': 'अनुरोधित आगमन तारीख, अनुरोधित प्रस्थान तिथि से पहले या उसी दिन होनी चाहिए।',
      'tentativeDates.departureAfterArrival': 'अनुरोधित प्रस्थान तारीख, अनुरोधित आगमन तारखे नंतर किंवा त्याच दिवशी असावी.',
      'validation.arrivalDateRequired': 'Arrival date is required.',
      'validation.departureDateRequired': 'Departure date is required.',

      // PTH Contemplation
      'pthContemplation.title': 'रिट्रीट आणि अभ्यास',
      'pthContemplation.chooseSubmissionTitle': 'तुमची सबमिशन पद्धत निवडा',
      'pthContemplation.chooseSubmissionSubtitle': 'चिंतन प्रतिसाद कसे सादर कराल ते निवडा',
      'pthContemplation.card.form.title': 'ऑनलाइन फॉर्म भरा',
      'pthContemplation.card.form.description': 'खालील फॉर्ममध्ये थेट उत्तरे द्या',
      'pthContemplation.card.file.title': 'PDF अपलोड करा',
      'pthContemplation.card.file.description': 'तुमचा पूर्ण चिंतन दस्तऐवज अपलोड करा',
      'pthContemplation.q1.label': '1. तुम्ही सिद्ध योगाच्या शिकवणींचा अभ्यास आणि सराव कसा करता? या अध्ययन आणि सरावामुळे तुम्हाला कोणते लाभ मिळत आहेत?',
      'pthContemplation.q2.label': '2. तुम्ही तुमचा ध्यान सराव अधिक सखोल करण्यासाठी कोणत्या प्रकारे प्रयत्न केले? तुम्ही वापरलेली विशिष्ट साधने नमूद करा.',
      'pthContemplation.q3.label': '3. तुमच्या नियमित ध्यान सरावाचा तुमच्या दैनंदिन जीवनातील विविध पैलूंवर — इतरांसोबतचे संबंध, तुमचे काम आणि तुमची अंतर्गत अवस्था — कसा प्रभाव पडतो?',
      'pthContemplation.q4.label': '4. या वेळेस या सिद्धयोग रिट्रीट/कार्यक्रमात सहभागी होण्याचा तुमचा उद्देश काय आहे? हा उद्देश साध्य करण्यासाठी तुम्ही स्वतःला कसे तयार कराल?',
      'pthContemplation.placeholder.answerHere': 'कृपया येथे उत्तर लिहा',
      'validation.minLength10': 'किमान लांबी 10 अक्षरे आहे.'
    }
  };

  constructor(private http: HttpClient) {
    this.loadTranslations();
    // Load saved language preference
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage) {
      this.currentLanguage = savedLanguage;
    }
  }

  /**
   * Load translations from JSON files
   */
  private loadTranslations(): void {
    if (this.translationsLoaded) {
      return;
    }

    // Load English translations
    this.http.get<any>('assets/i18n/en.json').pipe(
      catchError(() => of(this.fallbackTranslations.en || {}))
    ).subscribe(enData => {
      this.translations['en'] = enData;
    });

    // Load Hindi translations
    this.http.get<any>('assets/i18n/hi.json').pipe(
      catchError(() => of(this.fallbackTranslations.hi || {}))
    ).subscribe(hiData => {
      this.translations['hi'] = hiData;
      this.translationsLoaded = true;
    });
  }

  /**
   * Get translation for a given key
   * @param key Translation key
   * @returns Translated text or key if not found
   */
  getTranslation(key: string): string {
    // Try current language first
    if (this.translations[this.currentLanguage]?.[key]) {
      return this.translations[this.currentLanguage][key];
    }
    // Fallback to English
    if (this.translations['en']?.[key]) {
      return this.translations['en'][key];
    }
    // Fallback to hardcoded translations
    if (this.fallbackTranslations[this.currentLanguage]?.[key]) {
      return this.fallbackTranslations[this.currentLanguage][key];
    }
    if (this.fallbackTranslations['en']?.[key]) {
      return this.fallbackTranslations['en'][key];
    }
    // Return key if not found
    return key;
  }

  /**
   * Set current language
   * @param language Language code (en, hi)
   */
  setLanguage(language: string): void {
    if (this.translations[language] || this.fallbackTranslations[language]) {
      this.currentLanguage = language;
      localStorage.setItem('selectedLanguage', language);
    }
  }

  /**
   * Get current language
   * @returns Current language code
   */
  getCurrentLanguage(): string {
    return this.currentLanguage;
  }

  /**
   * Get available languages
   * @returns Array of available language codes
   */
  getAvailableLanguages(): string[] {
    return Object.keys(this.translations).length > 0 
      ? Object.keys(this.translations)
      : Object.keys(this.fallbackTranslations);
  }

  /**
   * Check if a translation key exists
   * @param key Translation key
   * @returns True if key exists
   */
  hasTranslation(key: string): boolean {
    return !!(
      this.translations[this.currentLanguage]?.[key] ||
      this.translations['en']?.[key] ||
      this.fallbackTranslations[this.currentLanguage]?.[key] ||
      this.fallbackTranslations['en']?.[key]
    );
  }

  /**
   * Get all translations for a language (for admin UI)
   * @param language Language code
   * @returns Object with all translations
   */
  getAllTranslations(language: string): any {
    return this.translations[language] || this.fallbackTranslations[language] || {};
  }

  /**
   * Update translations (for admin UI)
   * @param language Language code
   * @param translations Translation object
   */
  updateTranslations(language: string, translations: any): void {
    this.translations[language] = translations;
  }
}
